import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavActions } from '../../hooks/useNavActions';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import {
  DEFAULT_DATE_RANGE,
} from '../../components/listDefaults';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { useMiniGrid, popupWidthFor, type MiniCol } from './useMiniGrid';
import { BusyIcon } from '../../components/loading';
import { AppToolbar, FormMetaChips, type ExportData } from '../../components/AppToolbar';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { FormTitle } from '../../components/FormTitle';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { fmtLocalDate, fmtLocalDateTime, csvCell } from '../../utils/datetime';
import { exportRows } from '../../utils/exportData';
import {
  RollbackModal,
  type SdmsBatch,
} from './SdmsValidationActions';
import { SdmsPackageDetail, type SdmsPackageRow } from './SdmsPackageDetail';
import { fetchPlmBatches } from './plm/plm.api';
// Result Validation (manual queue) drill-in detail — Sample Results + QC Results
// tabs + whole-batch Validate / Rollback / Project Notes (ALPACA ResultEntry_Validation).
import ResultValidationDetail, { type ResultValidationBatch } from './ResultValidationDetail';
// Pending Job / QC Batch selection stage — the SAME component + helpers Result Entry
// Express uses, applied to Result Validation / Result Approval (RTCFR 2026-09-28).
import { ResultEntryPendingSelector } from './ResultEntryPendingSelector';
import { RESULT_GRID_NO_ZEBRA } from './resultGrids';
import {
  buildPendingBatchesFromEdges, filterPendingJobs, pendingBatchCols, pendingJobCols,
  PENDING_APPROVAL_STATUS, PENDING_VALIDATION_STATUS,
  type PendingJob, type ReePendingBatch, type ReeSelTab,
} from './resultEntryPending';
import { guardedExit } from '../../hooks/useUnsavedGuard';
// Result Tracking "Retrieve" drill-in — READ-ONLY Sample Results + QC Results for
// one batch (legacy ResultEntryQueryPanel Retrieve → SampleParameter ResultView).
import ResultTrackingDetail, { type ResultTrackingBatch, type RtEmbedState } from './ResultTrackingDetail';
// Preview opens the SDMS spreadsheet directly (ALPACA parity) — same popup as
// Open Package → SDMS, but skipping the package-detail step.
import { SdmsSpreadsheet } from './SdmsSpreadsheet';
// Reuses the .dv-flow / .dv-fstep / .dv-fnote styles for the SDMS Result
// Validation / Approval list-view workflow stepper (same dark-pill flow bar
// shown in the Open Package overlay).
import './SdmsPackageDetail.css';
// (Merged 2026-07-07) QC Batches list-view UX — the per-row Data Package
// hyperlink, the New/Edit/Delete/Rollback/SDMS toolbar actions, and the inline
// QC Batch detail form (opened from New / row Edit).
import QcBatchDetailPage, { type QcBatchSeed } from './QcBatchDetailPage';
import { QcBatchModal } from './QcBatchModal';
// Sample Prep Batch detail form (Prep Queue → New; Prep Batches → row Edit/view).
// Merged from the prep-feature branch 2026-07-17.
import PrepBatchDetailPage, { type PrepBatchSeed } from './PrepBatchDetailPage';
import { prepBatchApi } from './prepBatch.api';
import { qcBatchApi } from './qcBatch.api';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { useDeleteFlow, useConfirm, showDialog } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { consumePendingNavigation, returnToOrigin } from '../../layouts/navigateTo';
import { runLoading } from '../../components/loading/loadingStore';
import { consumePendingRecord } from '../../layouts/navigateTo';
// Result Tracking's filter section reuses the SAME Query Panel component/style
// as Result Entry Express (user request 2026-09-02) — the underlying Result
// Tracking data load/filter behavior is unchanged; only the filter UI is shared.
import {
  ResultEntryQueryPanel, EMPTY_REE_FILTERS, hasActiveReeFilters,
  type ReeFilters, type ReeOptions, type ReeBatchOption,
} from './ResultEntryQueryPanel';

/** Result Tracking cascading-filter base — two relations from
 *  /api/v1/analysis/result-tracking/facet-rows (AI_Analysis_ResultTracking_FacetRows_SP).
 *  Job-side combos + QC Batch↔Job edges; the panel joins them logically to cascade. */
type RtFacetJob  = { jobID: string | null; client: string | null; projectID: string | null; projectName: string | null; test: string | null; matrix: string | null; sampleLocation: string | null; sampleOid: string | null; receivedDate: string | null; collectDate: string | null; analyzedDate: string | null };
type RtFacetEdge = { qcBatchID: string | null; jobID: string | null; batchTest: string | null; batchEmp: string | null; batchMethod: string | null };

/** Min/max of a yyyy-MM-dd column across the given (filter-matched) job rows — used to
 *  constrain the six date pickers to the dates present for the current Status + filter
 *  combination (user request 2026-09-09). yyyy-MM-dd compares chronologically; NULLs
 *  ignored (no bound if none present). Shared by the RT and RV/RA builders. */
function jobDateBounds(rows: RtFacetJob[], pick: (r: RtFacetJob) => string | null): { min?: string; max?: string } {
  let min: string | undefined; let max: string | undefined;
  for (const r of rows) {
    const v = pick(r);
    if (!v) continue;
    if (min === undefined || v < min) min = v;
    if (max === undefined || v > max) max = v;
  }
  return { min, max };
}

/** Per-value DISTINCT pending-sample count (user request 2026-09-09), shared by the RT
 *  and RV/RA facet builders. Counts distinct sampleOid per value of `dim` among `rows`
 *  (the caller passes the exclude-self-filtered job rows), so it cascades with every
 *  other filter/Status and counts a multi-test sample once per value. */
function sampleCountsFrom(rows: RtFacetJob[], dim: (r: RtFacetJob) => string | null): Record<string, number> {
  const byVal = new Map<string, Set<string>>();
  for (const r of rows) {
    const v = dim(r);
    if (!v || !r.sampleOid) continue;
    let s = byVal.get(v);
    if (!s) { s = new Set<string>(); byVal.set(v, s); }
    s.add(r.sampleOid);
  }
  const counts: Record<string, number> = {};
  for (const [v, s] of byVal) counts[v] = s.size;
  return counts;
}

// The subset of ReeFilters/RapprFilters the cascade reads (the eight categorical
// controls). RT inlines this; RV & RA share the helper below (task RTCFR 2026-09-08).
type FacetSelKeys = 'batchId' | 'id' | 'test' | 'matrix' | 'clientName' | 'projectId' | 'projectName' | 'sampleLocation';
type FacetDraft = Record<FacetSelKeys, string[]>;

/** Bidirectional exclude-self faceting shared by RV & RA (mirrors the RT rtOptions /
 *  prune logic). Given the two facet relations + the current draft, returns the
 *  cascaded dropdown OPTIONS (each control's values = distinct values among rows
 *  matching every OTHER selected filter + the batch filter) AND the per-control
 *  set of still-VALID values (so the caller can prune stale selections). */
/** Result Validation / Approval: the Query Panel's bottom "Pending Result … |
 *  JobID n | QCBatchID n" strip. Hidden for now (RTCFR 2026-09-28, REE parity) —
 *  the Sample / QC Batch tab badges carry the counts; flip to true to restore it. */
const SHOW_DR_PENDING_SUMMARY = false;

/** RV / RA pending-selection stage (RTCFR 2026-09-28): the facet job rows matching
 *  EVERY Query Panel filter — the categorical selections (a QC Batch filter via its
 *  job edges, like facetOptionsFrom) plus each date range ONLY when the user has
 *  narrowed it inside the auto-filled span (REE's "active date" rule: a full-span
 *  auto-fill never filters; once active, a row with no date is excluded). Drives the
 *  Sample tab's job set and the QC Batch tab's batches, so both follow the panel live. */
function facetMatchedJobs(
  facetJobs: RtFacetJob[], facetEdges: RtFacetEdge[], draft: RapprFilters,
  bounds: RapprOptions['dateBounds'],
): RtFacetJob[] {
  const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
  const batchJobSet = draft.batchId.length
    ? new Set(facetEdges.filter(e => e.qcBatchID && draft.batchId.includes(e.qcBatchID)).map(e => e.jobID))
    : null;
  const active = (val: string, bound: string | undefined, isFrom: boolean) =>
    val && (!bound || (isFrom ? val > bound : val < bound)) ? val : '';
  const range = (key: 'analyzed' | 'received' | 'collected', from: string, to: string) => ({
    from: active(from, bounds?.[key]?.min, true),
    to:   active(to,   bounds?.[key]?.max, false),
  });
  const an = range('analyzed', draft.analyzedFrom, draft.analyzedTo);
  const rc = range('received', draft.receivedFrom, draft.receivedTo);
  const co = range('collected', draft.collectedFrom, draft.collectedTo);
  const inYmd = (ymd: string | null, r: { from: string; to: string }) => {
    if (!r.from && !r.to) return true;
    if (!ymd) return false;
    const d = ymd.slice(0, 10);
    return (!r.from || d >= r.from) && (!r.to || d <= r.to);
  };
  return facetJobs.filter(r =>
    inSel(draft.id, r.jobID) && inSel(draft.clientName, r.client) && inSel(draft.projectId, r.projectID)
    && inSel(draft.projectName, r.projectName) && inSel(draft.test, r.test) && inSel(draft.matrix, r.matrix)
    && inSel(draft.sampleLocation, r.sampleLocation)
    && (!batchJobSet || (r.jobID != null && batchJobSet.has(r.jobID)))
    && inYmd(r.analyzedDate, an) && inYmd(r.receivedDate, rc) && inYmd(r.collectDate, co));
}

function facetOptionsFrom(facetJobs: RtFacetJob[], facetEdges: RtFacetEdge[], draft: FacetDraft):
  { options: RapprOptions; validFor: Record<string, Set<string>> } {
  const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
  const batchJobSet = draft.batchId.length
    ? new Set(facetEdges.filter(e => e.qcBatchID && draft.batchId.includes(e.qcBatchID)).map(e => e.jobID))
    : null;
  // The selected QC Batches' OWN tests (task RTCFR, 2026-09-10). A QC batch belongs to
  // exactly one test, so selecting a batch must narrow Test to that batch's test — and a
  // Test selection must narrow QC Batch to batches OF that test (not merely batches that
  // ride on a test-matching job). Mirrors the REE facet's job+test alignment.
  const batchTestSet = draft.batchId.length
    ? new Set(facetEdges.filter(e => e.qcBatchID && draft.batchId.includes(e.qcBatchID))
        .map(e => e.batchTest).filter((v): v is string => !!v))
    : null;
  const jobsFor = (except: FacetSelKeys) => facetJobs.filter(r =>
    (except === 'id'             || inSel(draft.id, r.jobID))
    && (except === 'clientName'     || inSel(draft.clientName, r.client))
    && (except === 'projectId'      || inSel(draft.projectId, r.projectID))
    && (except === 'projectName'    || inSel(draft.projectName, r.projectName))
    && (except === 'test'           || inSel(draft.test, r.test))
    && (except === 'matrix'         || inSel(draft.matrix, r.matrix))
    && (except === 'sampleLocation' || inSel(draft.sampleLocation, r.sampleLocation))
    && (except === 'batchId' || !batchJobSet || (r.jobID != null && batchJobSet.has(r.jobID))));
  const uniq = (vals: (string | null)[]) => Array.from(new Set(vals.filter((v): v is string => !!v)));
  const asc  = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const desc = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
  const batchJobs = new Set(jobsFor('batchId').map(r => r.jobID).filter(Boolean));
  // A QC batch is a valid option when it rides on a job that passes every OTHER filter
  // (batchJobs) AND its OWN test satisfies the Test filter (task RTCFR, 2026-09-10) — so
  // Test = pH shows only pH batches, not every batch on a pH job.
  const seen = new Map<string, { id: string; test: string; method: string; emp: string }>();
  for (const e of facetEdges)
    if (e.qcBatchID && e.jobID && batchJobs.has(e.jobID) && inSel(draft.test, e.batchTest) && !seen.has(e.qcBatchID))
      seen.set(e.qcBatchID, { id: e.qcBatchID, test: e.batchTest ?? '', method: e.batchMethod ?? '', emp: e.batchEmp ?? '' });
  const validBatch = new Set(facetEdges.filter(e => e.qcBatchID && e.jobID && batchJobs.has(e.jobID) && inSel(draft.test, e.batchTest)).map(e => e.qcBatchID as string));
  // Rows matching EVERY categorical filter (no exclusion) — the intersection used to
  // derive the date-picker bounds for the current filter combination (user request
  // 2026-09-09). The facet is already stage-scoped (RV waiting-for-validation, RA
  // pending-approval) server-side, so these dates are stage-correct.
  const allCat = facetJobs.filter(r =>
    inSel(draft.id, r.jobID) && inSel(draft.clientName, r.client) && inSel(draft.projectId, r.projectID)
    && inSel(draft.projectName, r.projectName) && inSel(draft.test, r.test) && inSel(draft.matrix, r.matrix)
    && inSel(draft.sampleLocation, r.sampleLocation)
    && (!batchJobSet || (r.jobID != null && batchJobSet.has(r.jobID))));
  return {
    options: {
      clients:         uniq(jobsFor('clientName').map(r => r.client)).sort(asc),
      projects:        uniq(jobsFor('projectId').map(r => r.projectID)).sort(asc),
      projectNames:    uniq(jobsFor('projectName').map(r => r.projectName)).sort(asc),
      tests:           uniq(jobsFor('test').map(r => r.test)).filter(t => !batchTestSet || batchTestSet.has(t)).sort(asc),
      matrices:        uniq(jobsFor('matrix').map(r => r.matrix)).sort(asc),
      jobIds:          uniq(jobsFor('id').map(r => r.jobID)).sort(desc),
      sampleLocations: uniq(jobsFor('sampleLocation').map(r => r.sampleLocation)).sort(asc),
      batchIds:        Array.from(seen.values()).sort((a, b) => desc(a.id, b.id)),
      counts: {
        test:           sampleCountsFrom(jobsFor('test'),           r => r.test),
        matrix:         sampleCountsFrom(jobsFor('matrix'),         r => r.matrix),
        projectId:      sampleCountsFrom(jobsFor('projectId'),      r => r.projectID),
        projectName:    sampleCountsFrom(jobsFor('projectName'),    r => r.projectName),
        clientName:     sampleCountsFrom(jobsFor('clientName'),     r => r.client),
        sampleLocation: sampleCountsFrom(jobsFor('sampleLocation'), r => r.sampleLocation),
      },
      dateBounds: {
        analyzed:  jobDateBounds(allCat, r => r.analyzedDate),
        received:  jobDateBounds(allCat, r => r.receivedDate),
        collected: jobDateBounds(allCat, r => r.collectDate),
      },
    },
    validFor: {
      batchId:        validBatch,
      id:             new Set(uniq(jobsFor('id').map(r => r.jobID))),
      matrix:         new Set(uniq(jobsFor('matrix').map(r => r.matrix))),
      test:           new Set(uniq(jobsFor('test').map(r => r.test)).filter(t => !batchTestSet || batchTestSet.has(t))),
      projectId:      new Set(uniq(jobsFor('projectId').map(r => r.projectID))),
      projectName:    new Set(uniq(jobsFor('projectName').map(r => r.projectName))),
      clientName:     new Set(uniq(jobsFor('clientName').map(r => r.client))),
      sampleLocation: new Set(uniq(jobsFor('sampleLocation').map(r => r.sampleLocation))),
    },
  };
}
// Result Approval's OWN copy of the Query Panel (task RTCFR, 2026-09-04) — a
// SEPARATE component/types/CSS so changes here never touch Result Entry Express.
import {
  ResultApprovalQueryPanel, EMPTY_RAPPR_FILTERS, hasActiveRapprFilters,
  type RapprFilters, type RapprOptions,
} from './ResultApprovalQueryPanel';
// .dp-cell-link (the QC Batch grid's Data Package command-cell link) lives here.
import './qcBatch.css';

// ─────────────────────────────────────────────────────────────────────────────
// Shared list component for the eight BTLIMS XAF Analysis-module screens.
// Each screen passes a `kind` prop; the component picks the API endpoint and
// the column layout to render.  Data shapes:
//   - queue          → AnalysisQueueRow  (Sample Prep Queue, Analysis Queue)
//   - prep-batches   → PrepBatchRow
//   - batches        → AnalyticalBatchRow (QC Batches / Result Validation /
//                       SDMS Result Approval)
//   - result-tracking → ResultTrackingRow
// ─────────────────────────────────────────────────────────────────────────────

interface AnalysisQueueRow {
  testMethodId:        string;
  test:                string | null;
  method:              string | null;
  matrix:              string | null;
  pendingSampleCount:  number;
  oldestCollectedDate: string | null;
  newestCollectedDate: string | null;
  // Prep Queue extras
  prepMethod:          string | null;
  tierType:            string | null;
  prepSort?:           number | null;   // real PrepMethod Sort (prep-queue → seed)
  statusText:          string | null;
  statusId:            string | null;
  // Analysis Queue extras
  templateID:          number | null;
  // Prep Queue: one row per test + COMPONENT (2026-09-30). componentId null = the
  // parameters that have no component. Always null on the Analysis Queue.
  component?:          string | null;
  componentId?:        string | null;
}

/** "No component" in the component lists sent to the Prep Batch API (a real Oid is never all zeros). */
const NO_COMPONENT = '00000000-0000-0000-0000-000000000000';

/** Row identity for the queue grids: the Prep Queue is one row per test + component, so the
 *  test id alone repeats there; the Analysis Queue stays one row per test. */
const queueRowKey = (r: AnalysisQueueRow): string =>
  r.component !== undefined ? `${r.testMethodId}::${r.componentId ?? ''}` : r.testMethodId;

interface PrepBatchRow {
  id:              string;
  batchId:         string | null;
  jobIds:          string | null;
  test:            string | null;
  method:          string | null;
  matrix:          string | null;
  instrument:      string | null;
  tier:            number | null;
  sort:            number | null;
  temperature:     string | null;
  humidity:        string | null;
  remarks:         string | null;
  createdDate:     string | null;
  statusCode:      number;
  statusText:      string;
  statusId:        string | null;
  createdBy:       string | null;          // Employee.DisplayName ("E168044")
  lastUpdatedDate: string | null;
}

interface AnalyticalBatchRow {
  id:              string;
  batchId:         string | null;
  createdDate:     string | null;
  statusCode:      number;
  statusText:      string;
  statusId:        string | null;          // DV-1-X / QB-1-X
  templateID:      number | null;          // 1014 / 8074
  templateName:    string | null;          // SpreadSheetBuilder template name ("Phosphate")
  method:          string | null;
  test:            string | null;
  matrix:          string | null;
  jobIds:          string | null;
  instrument:      string | null;
  noruns:          number | null;
  reviewedBy:      string | null;
  reviewedDate:    string | null;
  verifiedBy:      string | null;
  verifiedByName:  string | null;          // approver E-code, resolved (SDMS Result Approval)
  verifiedDate:    string | null;
  rolledBackBy:    string | null;
  rolledBackDate:  string | null;
  rollbackReason:  string | null;
  // SDMS Result Validation extras (GoLive parity)
  validatedByList:  string | null;     // "E155503, E172600"
  validatedDateMax: string | null;
  // "Submitted By" (Batch Information header) — distinct from ValidatedByList.
  submittedByList:  string | null;
  // "Date Submitted" — ALPACA's MAX(sample SubmittedDate), NOT the batch CreatedDate.
  submittedDateMax: string | null;
  // (Merged 2026-07-07) Per-row Data Package availability (legacy
  // DataPackageCheckList gate) — drives the QC Batches "Data Package" link.
  dataPackageEnabled: boolean;
}

// ResultTrackingRow now mirrors GoLive's QC-batch-level shape exactly.
interface ResultTrackingRow {
  id:            string;
  qcBatchID:     string | null;
  client:        string | null;
  projectID:     string | null;
  jobID:         string | null;
  createdDate:   string | null;
  // MAX(SampleParameter.AnalyzedDate) across the batch's signed-off rows
  // (2026-09-02) — backs the Query Panel's "Date Analyzed" filter.
  analyzedDate:  string | null;
  statusCode:    number;
  statusText:    string;
}

// Result Validation (manual) list row — a SUPERSET of both "Search By" grains
// (ALPACA DataReviewQueryPanelController): QCBatchID mode = one row per QC batch
// (QCBatchID · Client · ProjectID · CreatedDate · Status); JobID mode = one row
// per job (JobID · ProjectID · ProjectName · Client · ReceivedDate).  The API
// returns the superset; the unused fields are null in each mode.
interface ResultValidationListRow {
  id:            string;
  /** Samplecheckin Oid for this row's job — QCBatchID mode is one row per
   *  (batch, job); the detail scopes the Sample tab to this job. */
  jobOid:        string | null;
  qcBatchID:     string | null;
  client:        string | null;
  projectID:     string | null;
  projectName:   string | null;
  jobID:         string | null;
  createdDate:   string | null;
  receivedDate:  string | null;
  statusCode:    number;
  statusText:    string;
}

export type AnalysisKind =
  | 'prep-queue' | 'prep-batches'
  | 'analysis-queue' | 'qc-batches'
  | 'result-validation' | 'result-approval' | 'sdms-approval'  // (Merged 2026-07-07)
  | 'result-validation-manual'   // "Result Validation" — manual result-entry Level-2 queue
  | 'result-tracking';

// kind → the form's nav id, so the shared <FormTitle> sources the icon/colour
// from the SAME navData entry as the sidebar (CLAUDE.md §18.6, task 3).
const KIND_NAV_ID: Record<AnalysisKind, string> = {
  'prep-queue':        'preplqueue',
  'prep-batches':      'prepbatches',
  'analysis-queue':    'analysisq',
  'qc-batches':        'qcbatches',
  'result-validation': 'sdmsval',
  'result-approval':   'rappr',
  'sdms-approval':     'sdmsappr',   // (Merged 2026-07-07)
  'result-validation-manual': 'rval',
  'result-tracking':   'restrack',
};

interface AnalysisListPageProps {
  kind:      AnalysisKind;
  title:     string;
  subtitle:  string;
  /** Breadcrumb group label (e.g. 'Sample Preparation', 'Analysis', 'Data Review'). */
  group:     string;
  /** (Merged 2026-07-07) Navigate to another nav page (AppShell.open) — used by
   *  the QC Batches list's New button to jump to the Analysis Queue (where QC
   *  batches are started). */
  onOpenNav?: (page: string) => void;
  /** View-visibility check (AppShell `canSee`) threaded to SdmsPackageDetail so its
   *  cross-nav buttons (SDMS / QC Batches / Prep Batches) hide for a user without
   *  permission to that form (user report 2026-10-27). */
  canSee?: (id: string, label?: string) => boolean;
}

const ENDPOINT: Record<AnalysisKind, string> = {
  'prep-queue':         '/api/v1/analysis/prep-queue',
  'prep-batches':       '/api/v1/analysis/prep-batches',
  'analysis-queue':     '/api/v1/analysis/analysis-queue',
  'qc-batches':         '/api/v1/analysis/qc-batches',
  'result-validation':  '/api/v1/analysis/result-validation',
  'result-approval':    '/api/v1/analysis/result-approval',
  'sdms-approval':      '/api/v1/analysis/sdms-result-approval',   // (Merged 2026-07-07)
  'result-validation-manual': '/api/v1/analysis/manual-result-validation',
  'result-tracking':    '/api/v1/analysis/result-tracking',
};

const DATE_OPTS = ['1M', '3M', '6M', '1Y', 'All'] as const;
type DateRangeKey = typeof DATE_OPTS[number];

/** Result Tracking's status-chip order (user request 2026-09-02) — the
 *  workflow's own lifecycle, Pending Entry through Sent, not count-descending. */
const RESULT_TRACKING_STATUS_ORDER = [
  // Full workflow lifecycle order (user request 2026-09-05), earliest → last. Values
  // are the RAW StatusDefinition captions (the chip DISPLAY is prettified by
  // rtChipLabel). 'Pending Validation' = RP-3-1 REPORT validation, so it sits AFTER
  // 'Pending Reporting' (shown as "Pending Report Validation"); 'Pending Data Entry'
  // (field data) is the earliest and was previously appended out of order.
  'Pending Data Entry', 'Pending Entry', 'Pending Submission', 'Pending SDMS Validation',
  'Pending Result Validation', 'Pending Result Approval', 'Pending Reporting',
  'Pending Validation', 'Pending Delivery', 'Sent',
];

// RTT Status-panel buckets (task RTCFR, 2026-09-11). The long per-stage chip row is
// replaced by three grouped tabs: Approved Results, Pending Results, All Results.
//   • PENDING  = the workflow stages BEFORE (and including) result approval — the result
//                itself is not yet approved.
//   • APPROVED = the result is approved and has moved downstream (reporting / delivery /
//                sent). Approved = every RT status that is NOT pending.
// Values are the RAW StatusDefinition captions (same strings used in row.statusText and
// the facet SP's status scope), covering the full enum universe so the two sets partition
// every RTT record. rtStatusBucketOf() maps a status to its bucket; RT_BUCKET_STATUSES
// gives the member list a bucket sends to the facet SP (@Status, '|'-joined).
const RT_PENDING_STATUSES: readonly string[] = [
  'Pending Data Entry', 'Pending Entry', 'Pending Submission', 'Pending SDMS Validation',
  'Pending Result Validation', 'Pending Result Approval', 'Pending Approval', 'Pending Verify',
  'Subout Pending Validation', 'Subout Pending Approval',
];
const RT_APPROVED_STATUSES: readonly string[] = [
  'Pending Reporting', 'Pending Validation', 'Pending Report Validation', 'Pending Report Approval',
  'Approved', 'Report Approved', 'Reported', 'Pending Delivery', 'Sent',
];
const RT_PENDING_SET = new Set(RT_PENDING_STATUSES);
const RT_BUCKET_STATUSES: Record<'pending' | 'approved', readonly string[]> = {
  pending: RT_PENDING_STATUSES, approved: RT_APPROVED_STATUSES,
};
/** Which grouped bucket a raw RT status caption belongs to. Anything not explicitly
 *  pending is treated as approved (downstream), so the two buckets always partition. */
const rtStatusBucketOf = (statusText: string | null | undefined): 'pending' | 'approved' =>
  statusText && RT_PENDING_SET.has(statusText) ? 'pending' : 'approved';
/** The three RTT bucket tabs, in display order (Approved · Pending · All). */
const RT_BUCKET_TABS: { key: 'approved' | 'pending' | 'all'; label: string }[] = [
  { key: 'all',      label: 'All Results' },
  { key: 'approved', label: 'Approved Results' },
  { key: 'pending',  label: 'Pending Results' },
];

/** Compact CSV export — used by the SampleDetails picker modal's Export button.
 *  (The main list grid exports via gridExportMatrix through AppToolbar; this is
 *  a local helper only for the modal, which isn't backed by <AppDataGrid>.) */
function exportCsv(rows: unknown[], filename: string) {
  if (rows.length === 0) return;
  const first = rows[0] as Record<string, unknown>;
  const keys = Object.keys(first);
  // Route each cell through csvCell so ISO date/datetime cells export as
  // MM/DD/YYYY (matching the grid) instead of raw ISO; non-dates pass through.
  void exportRows(rows, keys.map(k => ({
    header: k,
    value: (r: unknown) => csvCell((r as Record<string, unknown>)[k]),
  })), { filename });
}

// Render via the shared §14b.6 helpers (local tz, 24-hour, no AM/PM); keep the
// '—' placeholder for empty/unparseable values used in grid cells + CSV export.
function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}
function fmtDateTime(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '—') : '—';
}

// Result Tracking's Query Panel "Date Received" / "Date Analyzed" ranges (user
// request 2026-09-02: the date controls must actually filter, not just sit
// there). "Date Received" maps to CreatedDate; "Date Analyzed" maps to the
// batch's AnalyzedDate (AI_Analysis_ResultTracking_SP now returns both —
// MAX(SampleParameter.AnalyzedDate), added 2026-09-02). Compares via the SAME
// local-date rendering the grid columns already use (fmtLocalDate), reformatted
// to YYYY-MM-DD so it lines up with the <input type="date"> boundary values —
// never a hand-rolled UTC/local parse.
function toYmd(mmddyyyy: string): string | null {
  const [mm, dd, yyyy] = mmddyyyy.split('/');
  return mm && dd && yyyy ? `${yyyy}-${mm}-${dd}` : null;
}
function inLocalDateRange(iso: string | null, from: string, to: string): boolean {
  if (!from && !to) return true;
  const ymd = iso ? toYmd(fmtLocalDate(iso)) : null;
  if (!ymd) return false;
  if (from && ymd < from) return false;
  if (to && ymd > to) return false;
  return true;
}

function sinceFromKey(k: DateRangeKey): string | undefined {
  // "All" = no date limit.  The batch SPs floor a NULL @SinceDate to a 1-month
  // DEFAULT (a perf guard: `ISNULL(@SinceDate, DATEADD(MONTH,-1,GETDATE()))`), so
  // sending nothing would make "All" return FEWER rows than "3M".  Send an
  // explicit far-past floor instead so "All" truly returns every record.
  if (k === 'All') return '1900-01-01';
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(),  now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Whether the dataset for this kind supports a date filter.
/** The forms a link can open on one job (My Jobs ▸ Resume): list kind → nav id. */
const JOB_LINK_PAGE: Partial<Record<AnalysisKind, string>> = {
  'qc-batches': 'qcbatches',
  'result-validation': 'sdmsval',
  'sdms-approval': 'sdmsappr',
  'result-validation-manual': 'rval',
  'result-approval': 'rappr',
};

function supportsDate(kind: AnalysisKind): boolean {
  return kind === 'prep-batches' || kind === 'qc-batches' || kind === 'result-tracking'
      || kind === 'result-validation' || kind === 'sdms-approval'   // (Merged 2026-07-07)
      || kind === 'result-validation-manual'
      || kind === 'result-approval';   // manual Result Approval — same Date Range picker
}

// Column lists below match GoLive ALPACA LIMS exactly (audited 2026-05-24).
// We keep our existing DTOs and just relabel + reorder visible columns.
// Where GoLive shows a field we don't have, we emit "—".
//
// Each kind builds AppColumn[] for the shared grid.  Status columns use
// type:'status' (shared statusPill mapping); date columns render through the
// page's existing fmt* helpers so the on-screen text is byte-for-byte the same
// as before the migration.  ID/text columns render "—" for null, matching the
// previous table look (cells stay black — the shared grid colours status only).

const PREP_QUEUE_COLS: AppColumn<AnalysisQueueRow>[] = [
  { field: 'Test',         header: 'Test',         text: r => r.test ?? '',   render: r => r.test || '—' },
  // Component (user 2026-09-30) — the queue is one row per test + component.
  { field: 'Component',    header: 'Component',    text: r => r.component ?? '', render: r => r.component || '—' },
  { field: 'SampleMatrix', header: 'SampleMatrix', text: r => r.matrix ?? '', render: r => r.matrix || '—' },
  { field: 'PrepMethod',   header: 'PrepMethod',   text: r => r.prepMethod ?? '', render: r => r.prepMethod || '—' },
  { field: 'Method',       header: 'Method',       text: r => r.method ?? '', render: r => r.method || '—' },
  { field: 'TierType',     header: 'TierType',     text: r => r.tierType ?? '', render: r => r.tierType || '—' },
  { field: 'NumSamples',   header: '#Samples',     type: 'number', text: r => String(r.pendingSampleCount ?? ''), value: r => r.pendingSampleCount ?? 0 },
  { field: 'Status',       header: 'Status',       type: 'status', text: r => r.statusText ?? 'Pending Tier2 Prep' },
  { field: 'StatusID',     header: 'StatusID',     text: r => r.statusId ?? 'PB-2-1', render: r => r.statusId ?? 'PB-2-1' },
];

const ANALYSIS_QUEUE_COLS: AppColumn<AnalysisQueueRow>[] = [
  { field: 'Test',          header: 'Test',          text: r => r.test ?? '',   render: r => r.test || '—' },
  { field: 'Method',        header: 'Method',        text: r => r.method ?? '', render: r => r.method || '—' },
  { field: 'Matrix',        header: 'Matrix',        text: r => r.matrix ?? '', render: r => r.matrix || '—' },
  // No active template linked to the test → the SP's TOP-1 TemplateID subquery is
  // NULL (or 0); show "N/A" rather than a blank/dash (user 2026-09-18).
  { field: 'Template',      header: 'Template',
    text:   r => (r.templateID == null || r.templateID <= 0) ? 'N/A' : String(r.templateID),
    render: r => (r.templateID == null || r.templateID <= 0) ? 'N/A' : String(r.templateID) },
  // GoLive shows two distinct columns: a numeric count and a clickable
  // "Sample Details" link.  We render the count plain (no "pending" word)
  // and a chevron column that opens the same drill-down panel Prep Queue
  // uses — the whole row is clickable via onRowClick further down.
  { field: 'NumSamples',    header: '#Samples',      type: 'number', text: r => String(r.pendingSampleCount ?? 0), value: r => r.pendingSampleCount ?? 0 },
  { field: 'SampleDetails', header: 'Sample Details', sortable: false, filterable: false, text: () => 'View details →', render: () => 'View details →' },
];

const PREP_BATCH_COLS: AppColumn<PrepBatchRow>[] = [
  { field: 'PrepBatchID', header: 'PrepBatchID', text: r => r.batchId ?? '', render: r => r.batchId || '—' },
  { field: 'Matrix',      header: 'Matrix',      text: r => r.matrix ?? '',  render: r => r.matrix || '—' },
  { field: 'Test',        header: 'Test',        text: r => r.test ?? '',    render: r => r.test || '—' },
  { field: 'Method',      header: 'Method',      text: r => r.method ?? '',  render: r => r.method || '—' },
  { field: 'Instrument',  header: 'Instrument',  text: r => r.instrument ?? '', render: r => r.instrument || '—' },
  { field: 'DateCreated', header: 'DateCreated', text: r => fmtDateTime(r.createdDate), render: r => fmtDateTime(r.createdDate) },
  { field: 'CreatedBy',   header: 'CreatedBy',   text: r => r.createdBy ?? '', render: r => r.createdBy || '—' },
  { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  { field: 'StatusID',    header: 'StatusID',    text: r => r.statusId ?? '', render: r => r.statusId || '—' },
];

// (Merged 2026-07-07) QC Batch list columns.  The leading "Data Package" column
// is a per-row command cell (legacy list-view hyperlink): a clickable link when
// the batch's Test has a DataPackageCheckList (r.dataPackageEnabled), otherwise a
// greyed, non-clickable label.  onDataPackage runs the action (deferred — info
// toast until the module lands).
function qcBatchCols(onDataPackage: (r: AnalyticalBatchRow) => void): AppColumn<AnalyticalBatchRow>[] {
  return [
    // Data Package command-cell link — FIRST column (user request), sized to its
    // caption width (no sort/filter chrome — it's an action cell, not data).
    {
      field: 'DataPackage', header: 'DataPackage', sortable: false, filterable: false,
      width: 120, minWidth: 120,
      text: r => r.dataPackageEnabled ? 'Available' : '',
      // The disabled half used to be a bare grey label: clicking it did nothing
      // and said nothing, so it read as a broken link (user report 2026-09-24 —
      // "when i click data package the data package form not opening"). It now
      // says WHY, and announces itself as disabled to assistive tech. The cause
      // is configuration, not this row: dbo.DataPackageCheckList has no live
      // entry naming this batch's Test (QCBatchController.cs :638-657). PLM
      // batches are normally in that state — no PLM test is on a check list even
      // in GoLive — which is why PLM submits from its OWN Submit button instead.
      render: r => r.dataPackageEnabled
        ? <a className="dp-cell-link"
             onClick={e => { e.stopPropagation(); onDataPackage(r); }}>Data Package</a>
        : <span className="dp-cell-link dp-cell-link--off" aria-disabled="true"
                title={"No Data Package check list is configured for this batch's test"
                     + " (Settings → Data Package Check List), so there is nothing to open here."
                     + " A PLM batch is submitted from the PLM screen's own Submit button."}>
            Data Package
          </span>,
    },
    { field: 'QCBatchID',  header: 'QCBatchID',  text: r => r.batchId ?? '', render: r => r.batchId || '—' },
    { field: 'Matrix',     header: 'Matrix',     text: r => r.matrix ?? '',  render: r => r.matrix || '—' },
    { field: 'Test',       header: 'Test',       text: r => r.test ?? '',    render: r => r.test || '—' },
    { field: 'Method',     header: 'Method',     text: r => r.method ?? '',  render: r => r.method || '—' },
    { field: 'Status',     header: 'Status',     type: 'status', text: r => r.statusText ?? '' },
    { field: 'StatusID',   header: 'StatusID',   text: r => r.statusId ?? '', render: r => r.statusId || '—' },
    { field: 'TemplateID', header: 'TemplateID', text: r => r.templateID == null ? '' : String(r.templateID), render: r => r.templateID == null ? '—' : String(r.templateID) },
  ];
}

// (Merged 2026-07-07) Pull the API's friendly error message out of an axios error
// (guard messages from the QC Delete/Rollback endpoints), falling back to a default.
function qcApiErr(e: unknown, fallback: string): string {
  const er = e as { response?: { data?: { error?: { message?: string } } } };
  return er.response?.data?.error?.message ?? fallback;
}

/** Result Tracking columns — JobID leads, QCBatchID follows (user request
 *  2026-09-02: both id columns shown together now that the list's Job ID / QC
 *  Batch ID filters live side-by-side in the Query Panel, not behind a grain
 *  toggle). Every row already carries both ids regardless of query. */
function resultTrackingCols(): AppColumn<ResultTrackingRow>[] {
  return [
    { field: 'JobID',       header: 'JobID',       text: r => r.jobID ?? '',     render: r => r.jobID || '—' },
    { field: 'QCBatchID',   header: 'QCBatchID',   text: r => r.qcBatchID ?? '', render: r => r.qcBatchID || '—' },
    { field: 'Client',      header: 'Client',      text: r => r.client ?? '',    render: r => r.client || '—' },
    { field: 'ProjectID',   header: 'ProjectID',   text: r => r.projectID ?? '', render: r => r.projectID || '—' },
    { field: 'CreatedDate', header: 'CreatedDate', text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  ];
}

/** Result Validation (manual) — QCBatchID mode columns (ALPACA
 *  `QCBatch_ListView_ResultValidation_Filter`, base = SampleParameter):
 *  QCBatchID · Client · ProjectID · CreatedDate · Status.  One row per QC batch. */
function resultValidationQcCols(): AppColumn<ResultValidationListRow>[] {
  return [
    { field: 'QCBatchID',   header: 'QCBatchID',   text: r => r.qcBatchID ?? '', render: r => r.qcBatchID || '—' },
    { field: 'Client',      header: 'Client',      text: r => r.client ?? '',    render: r => r.client || '—' },
    // ProjectID renders BLANK when empty (user request 2026-07-10), not '—'.
    { field: 'ProjectID',   header: 'ProjectID',   text: r => r.projectID ?? '', render: r => r.projectID ?? '' },
    { field: 'CreatedDate', header: 'CreatedDate', text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Status',      header: 'Status',      type: 'status', text: r => r.statusText ?? '' },
  ];
}

/** Result Validation (manual) — JobID mode columns (ALPACA
 *  `Samplecheckin_ListView_ResultValidation`, base = Samplecheckin):
 *  JobID · ProjectID · ProjectName · Client · ReceivedDate.  One row per job.
 *  ReceivedDate uses the legacy "{0:g}" general format = MM/DD/YYYY HH:mm. */
function resultValidationJobCols(): AppColumn<ResultValidationListRow>[] {
  return [
    { field: 'JobID',        header: 'JobID',        text: r => r.jobID ?? '',       render: r => r.jobID || '—' },
    // ProjectID / ProjectName render BLANK when empty (user request 2026-07-10;
    // ALPACA leaves them blank), not the '—' placeholder.
    { field: 'ProjectID',    header: 'ProjectID',    text: r => r.projectID ?? '',   render: r => r.projectID ?? '' },
    { field: 'ProjectName',  header: 'ProjectName',  text: r => r.projectName ?? '', render: r => r.projectName ?? '' },
    { field: 'Client',       header: 'Client',       text: r => r.client ?? '',      render: r => r.client || '—' },
    { field: 'ReceivedDate', header: 'ReceivedDate', text: r => fmtDateTime(r.receivedDate), render: r => fmtDateTime(r.receivedDate) },
  ];
}

// SDMS Result Validation columns — DataPackage "Open Package" link first
// (XAF parity, see DataReviewController.BatchReviewDataPackageHyperLink),
// followed by a per-row Edit icon (legacy SpreadSheetEntry_AnalyticalBatch
// _ListView_RawDataLevel2BatchReview has AllowEdit="True" InlineEditMode="Batch").
// Callbacks are passed in so the column factory stays pure.
// (Merged 2026-07-07) — the `approval` flag drives SDMS Result Approval (Level-3):
// it hides the per-row Edit icon (ALPACA read-only parity) and appends
// DateVerified / VerifiedBy columns.
function resultValidationCols(
  onOpenPackage: (row: AnalyticalBatchRow) => void,
  onEditRow:     (row: AnalyticalBatchRow) => void,
  onPreviewRow:  (row: AnalyticalBatchRow) => void,
  onOpenQcBatch: (row: AnalyticalBatchRow) => void,   // QCBatchID link → QC Batch form
  approval = false,   // SDMS Result Approval (Level-3) appends DateVerified/VerifiedBy
  canWrite = true,    // Role-Based MAC — no Write → no per-row Edit icon. Defaults
                      // true so any caller that does not pass it keeps today's
                      // behaviour (fall-open, as everywhere else in the MAC model).
): AppColumn<AnalyticalBatchRow>[] {
  const iconBtn: CSSProperties = {
    border: 'none', background: 'transparent',
    color: 'var(--ai-icon-edit)', cursor: 'pointer',
    padding: 2, borderRadius: 4, lineHeight: 0,
  };
  const cols: AppColumn<AnalyticalBatchRow>[] = [
    {
      // Actions sit BETWEEN the multi-select checkbox and DataPackage — a per-row
      // Edit (opens the detail) + Preview (opens the SDMS result image), matching
      // the prototype's Actions column (pencil + eye).
      field: 'Actions', header: 'Actions', sortable: false, filterable: false,
      width: 74, align: 'center',
      text: () => '',
      render: r => (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          {/* Edit (opens the detail) — SDMS Result VALIDATION only.  ALPACA's
              SDMS Result Approval (Level-3 · DataVerify) grid is READ-ONLY, so
              it shows no edit icon there (Merged 2026-07-07). */}
          {/* Also hidden without Write: this icon opens the EDITABLE detail, so a
              Read-only MAC level must not offer it (owner report 2026-10-26 —
              Data Review Level1 grants SDMS Result Validation Read only, yet the
              pencil was still shown on every row). */}
          {!approval && canWrite && (
            <button onClick={e => { e.preventDefault(); e.stopPropagation(); onEditRow(r); }}
              title="Edit this batch" aria-label="Edit" style={iconBtn}>
              <i className="ti ti-edit" style={{ fontSize: 16 }} />
            </button>
          )}
          <button onClick={e => { e.preventDefault(); e.stopPropagation(); onPreviewRow(r); }}
            title="Preview SDMS result image" aria-label="Preview" style={iconBtn}>
            <i className="ti ti-eye" style={{ fontSize: 16 }} />
          </button>
        </span>
      ),
    },
    {
      field: 'DataPackage', header: 'DataPackage', sortable: false, filterable: false,
      text: () => 'Open Package',
      render: r => (
        <a href="#" onClick={e => { e.preventDefault(); onOpenPackage(r); }}
           className="sa-openpkg"
           style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))', fontWeight: 600, textDecoration: 'none' }}>
          {/* Prototype parity: steel-blue package icon + orange-dk text
              (.pkg a{color:var(--orange-dk)} — SDMS-Result-Validation-Prototype (7)). */}
          <i className="ti ti-package" style={{ marginRight: 5, color: 'var(--ai-blue)' }} />
          Open Package
        </a>
      ),
    },
    { field: 'QCBatchID',     header: 'QCBatchID',     text: r => r.batchId ?? '',
      // Clicking the QCBatchID opens the batch's QC Batch form (QcBatchDetailPage),
      // the same detail the QC Batches list opens (user request 2026-10-27).
      render: r => r.batchId
        ? (<a href="#" className="sa-openpkg"
               onClick={e => { e.preventDefault(); e.stopPropagation(); onOpenQcBatch(r); }}
               title="Open this batch in the QC Batch form"
               style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))', fontWeight: 600, textDecoration: 'none' }}>
             {r.batchId}
           </a>)
        : '—' },
    // DateCreated: legacy xafml (Model.xafml line 59368) has NO DisplayFormat
    // on this column → date-only display.  DateValidated (line 59378) is the
    // only date column on this view that specifies MM/dd/yyyy HH:mm.
    { field: 'DateCreated',   header: 'DateCreated',   text: r => fmtDate(r.createdDate), render: r => fmtDate(r.createdDate) },
    { field: 'Test',          header: 'Test',          text: r => r.test ?? '',    render: r => r.test || '—' },
    { field: 'Method',        header: 'Method',        text: r => r.method ?? '',  render: r => r.method || '—' },
    { field: 'Status',        header: 'Status',        type: 'status', text: r => r.statusText ?? '' },
    // StatusID: blank when the row has no TemplateID (i.e. TemplateID = 0 →
    // uninitialised batch — the legacy stores a mojibake em-dash in that case
    // which renders as "â€"", so we hide it) or when the value itself is empty.
    { field: 'StatusID',      header: 'StatusID',
      text:   r => (r.templateID === 0 || !r.statusId) ? '' : r.statusId,
      render: r => (r.templateID === 0 || !r.statusId) ? '' : r.statusId },
    // TemplateID: blank for 0 (uninitialised) and null; show the number otherwise.
    { field: 'TemplateID',    header: 'TemplateID',
      text:   r => (r.templateID == null || r.templateID === 0) ? '' : String(r.templateID),
      render: r => (r.templateID == null || r.templateID === 0) ? '' : String(r.templateID) },
    // GoLive SDMS Result Validation pulls DateValidated/ValidatedBy from
    // SampleParameter aggregation (CSV of distinct E-codes + MAX validated
    // date).  Empty cells render blank — no em-dash placeholder — for pending
    // batches (matches legacy display for these two columns specifically).
    { field: 'DateValidated', header: 'DateValidated',
      text:   r => r.validatedDateMax ? (fmtLocalDateTime(r.validatedDateMax) || '') : '',
      render: r => r.validatedDateMax ? (fmtLocalDateTime(r.validatedDateMax) || '') : '' },
    { field: 'ValidatedBy',   header: 'ValidatedBy',
      text:   r => r.validatedByList ?? '',
      render: r => r.validatedByList ?? '' },
  ];
  // SDMS Result Approval (Level-3, RawDataLevel3BatchReview) shows two extra
  // columns after ValidatedBy — the verification/approval stamp, blank until the
  // batch is approved (matches ALPACA's screenshot: DateVerified / VerifiedBy).
  // (Merged 2026-07-07.)
  if (approval) cols.push(
    { field: 'DateVerified', header: 'DateVerified',
      text:   r => r.verifiedDate ? (fmtLocalDateTime(r.verifiedDate) || '') : '',
      render: r => r.verifiedDate ? (fmtLocalDateTime(r.verifiedDate) || '') : '' },
    { field: 'VerifiedBy',   header: 'VerifiedBy',
      text:   r => r.verifiedByName ?? '',
      render: r => r.verifiedByName ?? '' },
  );
  return cols;
}

// MAC form id for a kind. The four explicit cases keep their historical ids;
// EVERY other kind resolves through KIND_NAV_ID (the same nav id the sidebar
// uses). Returning '' for the rest made useNavActions look up NAV_VIEW_MAPPING['']
// → [] → no match → fall OPEN, so Sample Prep Queue / Prep Batches (and the
// Analysis Queue / QC Batches lists) showed New/Edit/Delete to a read-only role
// however their MAC was set. (owner report 2026-10-24.)
function kindToFormId(k: string): string {
  switch (k) {
    case 'result-validation':        return 'sdmsval';
    case 'sdms-approval':            return 'sdmsappr';
    case 'result-validation-manual': return 'rval';
    case 'result-approval':          return 'rappr';
    default:                         return KIND_NAV_ID[k as AnalysisKind] ?? '';
  }
}

export default function AnalysisListPage({ kind, title, subtitle, group, onOpenNav, canSee }: AnalysisListPageProps) {
  const perm = useNavActions(kindToFormId(kind));
  const [rows, setRows]       = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  // Monotonic list-load id — only the latest load() writes rows/error (stale-response
  // guard for rapid kind/status/date/search changes; see load()).
  const loadSeq = useRef(0);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  // GoLive Date Filter Category default (Analysis Entry Model + Analysis
  // Review Level both ship as 3M).
  const [dateRange, setDateRange] = useState<DateRangeKey>(
    (kind === 'result-tracking'   // 3M by default (user request 2026-09-08; was 1M per 2026-09-02)
      ? '3M'
      : kind === 'result-validation' || kind === 'result-approval' || kind === 'sdms-approval'  // (Merged 2026-07-07)
      || kind === 'result-validation-manual'
      ? DEFAULT_DATE_RANGE.AnalysisReview
      : DEFAULT_DATE_RANGE.AnalysisEntry) as DateRangeKey,
  );
  const [search,   setSearch]   = useState('');
  const [statusChip, setStatusChip] = useState<string>('all');
  const [gridMaxed, setGridMaxed] = useState(false);   // SDMS list: full-screen the grid
  // Result Tracking / Result Validation / Result Approval: GoLive's Search By
  // dropdown.  Result Validation + Approval both default to QCBatchID (the ALPACA
  // Data-Review default) with a JOBID toggle.
  const [searchBy, setSearchBy] = useState<'QCBatchID' | 'JOBID'>('QCBatchID');
  // Prep Queue / Analysis Queue drill-down — the row opens a slide-over panel.
  const [selectedQueue, setSelectedQueue] = useState<AnalysisQueueRow | null>(null);
  // A link asked for one job (My Jobs ▸ Resume): acted on once the list has loaded.
  const jobLink = useRef<{ jobId: string; batchOid?: string; resultEntry: boolean } | null>(null);
  const jobLinkSawLoad = useRef(false);
  useEffect(() => {
    const page = JOB_LINK_PAGE[kind];
    const nav = page ? consumePendingNavigation(page) : null;
    if (!nav) return;
    // No batch yet: open the new QC Batch form on this test with the job's samples, as
    // the Analysis Queue's OK does. It does not go through the queue, because the queue
    // lists only the tests of the user's own department.
    if (nav.context.newBatch) {
      const batch = JSON.parse(nav.context.newBatch) as {
        testMethodOid: string; test: string | null; method: string | null; matrix: string | null;
        templateId: number | null; sampleOids: string[];
      };
      setQcBatchSeed({
        testMethodId: batch.testMethodOid, test: batch.test, method: batch.method, matrix: batch.matrix,
        templateID: batch.templateId,
        preSelectedJobOids: [nav.context.jobOid!],
        preSelectedSampleLogInOids: batch.sampleOids,
      });
      return;
    }
    jobLink.current = { jobId: nav.recordId, batchOid: nav.context.batchOid, resultEntry: nav.context.resultEntry === '1' };
    // The job may be older than the default date window.
    if (supportsDate(kind)) setDateRange('All');
    if (kind === 'qc-batches' || kind === 'result-validation' || kind === 'sdms-approval') setSearch(nav.recordId);
  }, [kind]);

  // ── QC Batches list — inline detail form + single-select toolbar actions ────
  // (Merged 2026-07-07) Clicking New / a row Edit swaps the list for the inline
  // QcBatchDetailPage (seeded from the selected batch); Edit/Delete/Rollback
  // operate on the single ticked batch.
  const [qcBatchSeed, setQcBatchSeed] = useState<QcBatchSeed | null>(null);
  // Row whose QC Batch detail is open in a MODAL POPUP (SDMS Result Validation /
  // Approval QCBatchID link) — distinct from qcBatchSeed's full-page detail.
  const [qcBatchModalRow, setQcBatchModalRow] = useState<AnalyticalBatchRow | null>(null);

  // Which of these QC batches are asbestos-PLM?  Read once from the PLM list
  // endpoint (rule 8 — no second "is this PLM?" implementation) and used to
  // steer the SDMS / PLM buttons to the right screen AND — on SDMS Result
  // Validation / Approval — to open a PLM batch's data package as legacy's
  // "SDMS Result Validation - PLM" view (PLM Results grid, no SDMS category tabs
  // or blank-result gate). A failure leaves the set empty, which degrades to
  // today's behaviour rather than blocking the list.
  const [plmBatchOids, setPlmBatchOids] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (kind !== 'qc-batches' && kind !== 'result-validation' && kind !== 'sdms-approval') return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchPlmBatches();
        if (!cancelled) setPlmBatchOids(new Set(rows.map(r => r.batchOid)));
      } catch { /* leave empty — the buttons simply stop steering */ }
    })();
    return () => { cancelled = true; };
  }, [kind]);
  // Sample Prep Queue "New" swaps the list for the inline PrepBatchDetailPage,
  // seeded from the selected prep-queue row (TestMethod + Tier).
  const [prepBatchSeed, setPrepBatchSeed] = useState<PrepBatchSeed | null>(null);
  // QC Batches list — single-row checkbox selection driving the Edit/Delete/
  // Rollback toolbar actions (legacy SpreadSheetEntry_AnalyticalBatch_ListView
  // actions). Radio-like one-at-a-time model.
  const [qcSel, setQcSel] = useState<Set<string | number>>(new Set());
  // Analysis Queue single-select → drives the "Create QC Batch" action (New).
  // Completes the QC create flow: pick a test-method row here, then New opens
  // the QC Batch form (create mode) for it. (Merged 2026-07-07.)
  const [analysisSel, setAnalysisSel] = useState<Set<string | number>>(new Set());
  const runDelete = useDeleteFlow();
  const { prompt } = useConfirm();
  const notify = useNotify();

  // ── SDMS Result Validation / SDMS Result Approval: selection + modal state ─
  // (Merged 2026-07-07) SDMS Result Approval (Level-3, kind 'sdms-approval') reuses
  // the ENTIRE Result Validation machinery (selection · Open Package · toolbar ·
  // island stepper · detail overlay); the ONLY differences are the labels + the
  // terminal action ("Approve" not "Validate"), keyed off `isApproval`.  NOTE:
  // this is a SEPARATE form from the legacy job-level "Result Approval" (kind
  // 'result-approval'), which is left exactly as it was.
  const isApproval = kind === 'sdms-approval';
  const isSdms = kind === 'result-validation' || isApproval;
  // "Result Validation" (manual result-entry queue) — a SEPARATE, self-contained
  // selectable list with Validate / Rollback toolbar actions (NOT the SDMS
  // Open-Package / stepper machinery).  Reuses selectedIds for row selection.
  const isManualVal = kind === 'result-validation-manual';
  // "Result Approval" (manual result-entry Level-3 queue) — the DIRECT parallel of
  // the manual Result Validation list, one stage later.  Shares ALL the list
  // machinery (Search By · columns · row-click select · Load · detail); the only
  // differences (Status=2, Approve, ApprovedBy/Date) live in the shared detail via
  // its `mode='approval'` prop.  `isManualDR` = "either manual Data-Review stage".
  const isManualAppr = kind === 'result-approval';
  const isManualDR   = isManualVal || isManualAppr;
  // Result Tracking — read-only list with a single-select checkbox + "Retrieve"
  // drill-in (reuses `selectedIds`; the kinds are mutually exclusive).
  const isTracking   = kind === 'result-tracking';
  // Result Tracking's filter section — the REE Query Panel component, reused
  // as-is. Filters are applied CLIENT-SIDE over the already-loaded rows (the
  // server load still uses sinceDate/searchBy exactly as before); Date
  // Analyzed/Received/Collected render "soon" — that granularity isn't in the
  // Result Tracking list row (batch/job grain only).
  const [rtDraft, setRtDraft]     = useState<ReeFilters>(EMPTY_REE_FILTERS);
  const [rtApplied, setRtApplied] = useState<ReeFilters>(EMPTY_REE_FILTERS);
  // Expanded by default (user request 2026-09-03) — the criteria (and Clear/Load)
  // are visible on open. Collapse it with the Query toggle to reclaim space.
  const [rtQpCollapsed, setRtQpCollapsed] = useState(false);
  // Grid-top fuzzy search (user request 2026-09-02) — deliberately a SEPARATE
  // state from the shared `search` (which other kinds still send to the
  // server as @Search). Result Tracking's box is purely client-side over the
  // already-loaded rows, so typing here never fires a network request.
  const [rtSearch, setRtSearch] = useState('');
  // Whether Load has been clicked for the current selection (user request 2026-09-05).
  // The RT grid is empty until Load; Load is now allowed with the Query panel all-blank
  // (no "select a filter first" warning), so this flag — NOT hasActiveReeFilters — gates
  // the embed. Cleared on a status-chip change (re-Load required) and when leaving RT.
  const [rtLoaded, setRtLoaded] = useState(false);
  useEffect(() => {
    if (!isTracking) { setRtDraft(EMPTY_REE_FILTERS); setRtApplied(EMPTY_REE_FILTERS); setRtSearch(''); setRtLoaded(false); }
  }, [isTracking]);
  // ── Result Approval's Query Panel (task RTCFR, 2026-09-04) — its OWN draft/
  // applied/collapsed/search state, mirroring the RTT wiring but with the Result
  // Approval copy. Server-side filters (Matrix/Test/ProjectName/Site) ride in
  // load()'s params from raApplied; the row-present fields (QC Batch ID · Job ID
  // · Client · Project ID · Project Name + Date Received) filter client-side in
  // raFiltered. raSearch is a client-side grid fuzzy-search (SEPARATE from the
  // shared `search`), so typing never fires a network request.
  const [raDraft, setRaDraft]     = useState<RapprFilters>(EMPTY_RAPPR_FILTERS);
  const [raApplied, setRaApplied] = useState<RapprFilters>(EMPTY_RAPPR_FILTERS);
  const [raQpCollapsed, setRaQpCollapsed] = useState(false);
  const [raSearch, setRaSearch] = useState('');
  // Cascaded options (raOptions/rvOptions) are computed from the facet feeds below.
  useEffect(() => {
    if (!isManualAppr) { setRaDraft(EMPTY_RAPPR_FILTERS); setRaApplied(EMPTY_RAPPR_FILTERS); setRaSearch(''); }
  }, [isManualAppr]);
  // ── Result Validation's Query Panel (task RTCFR, 2026-09-05) — a DIRECT parallel
  // of the Result Approval wiring above, one Data-Review stage earlier. Its OWN
  // draft/applied/collapsed/search/options state so RV and RA never share filter
  // state. Server-side filters (Matrix/Test/ProjectName/Site + Date Analyzed/
  // Collected) ride in load()'s params from rvApplied; the row-present fields
  // (QC Batch ID · Job ID · Client · Project ID · Project Name + Date Received)
  // filter client-side in rvFiltered. rvSearch is a client-side grid fuzzy-search.
  const [rvDraft, setRvDraft]     = useState<RapprFilters>(EMPTY_RAPPR_FILTERS);
  const [rvApplied, setRvApplied] = useState<RapprFilters>(EMPTY_RAPPR_FILTERS);
  const [rvQpCollapsed, setRvQpCollapsed] = useState(false);
  const [rvSearch, setRvSearch] = useState('');
  useEffect(() => {
    if (!isManualVal) { setRvDraft(EMPTY_RAPPR_FILTERS); setRvApplied(EMPTY_RAPPR_FILTERS); setRvSearch(''); }
  }, [isManualVal]);
  // The Search By (QCBatchID | JOBID) control is NOT used on Result Approval or
  // Result Tracking (user decision 2026-09-04) — both are PINNED to JOBID grain so
  // the list is job-centric and a job that never got a QC batch (unbatched samples)
  // is still tracked/visible (e.g. job 260825007, whose 13 samples are all
  // unbatched, was invisible in the QCBatchID grain). The shared `searchBy` still
  // drives the list SP / columns / detail scope; forcing it here keeps both forms
  // job-grained without a visible toggle. (Result Validation keeps its own Search By.)
  // Result Validation is now JOBID-pinned too (task RTCFR, 2026-09-05) — it mirrors
  // the Result Approval UI, whose control bar has NO Search By and whose embedded
  // grid is job-grained (JobID column, job-scope detail). Same reasoning: a job with
  // unbatched pending-validation samples stays visible.
  useEffect(() => { if (isManualAppr || isManualVal || isTracking) setSearchBy('JOBID'); }, [isManualAppr, isManualVal, isTracking]);
  // Row-click drills into the Result Validation detail (Sample + QC tabs).
  const [rvDetail, setRvDetail] = useState<ResultValidationBatch | null>(null);
  // Result Tracking "Retrieve" opens the read-only batch-results drill-in.
  const [rtDetail, setRtDetail] = useState<ResultTrackingBatch | null>(null);
  // Dock into the shared topbar stepper slot AppShell actually renders
  // (#ai-topbar-stepper). The SDMS/QC bases used a differently-named slot that
  // doesn't exist here, so the SDMS workflow stepper never appeared. (Fix 2026-07-07.)
  const [bannerSlot, setBannerSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setBannerSlot(document.getElementById('ai-topbar-stepper')); }, []);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [rollbackOpen,  setRollbackOpen]  = useState(false);
  // §13 Notifications (standing rule 7): rollback outcomes go through the shared
  // global service (useNotify) — success → transient top banner (notify.success),
  // refusal → centered blocking modal (notify.error / .warning auto-route via
  // NotificationProvider). No per-form banner state/JSX. Wording keeps ALPACA
  // parity ("Rollbacked successfully." = DataReviewController ShowMessage).
  const [previewTarget, setPreviewTarget] = useState<AnalyticalBatchRow | null>(null);
  const [pkgTarget,     setPkgTarget]     = useState<AnalyticalBatchRow | null>(null);
  // QC Batches list "Data Package" link → OG modal (TargetWindow.NewModalWindow):
  // create-or-find the batch's DataPackage, then open the SAME SdmsPackageDetail view as a
  // POPUP (windowed).  Kept separate from pkgTarget (SDMS Validation/Approval, full-content).
  const [qcPkgTarget,   setQcPkgTarget]   = useState<AnalyticalBatchRow | null>(null);
  /** The data package's "PLM" button (legacy): open the batch's PLM screen,
   *  read-only — the same path as the QC Batches list's PLM button. Back returns
   *  to this list. */
  const openPlmFor = (row: AnalyticalBatchRow) => {
    setPkgTarget(null);
    setQcPkgTarget(null);
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
      autoOpenPlm: true,
    });
  };
  const [qcPkgStatus,   setQcPkgStatus]   = useState<number | null>(null);   // DataPackage.Status → Submit vs Validate
  const [qcPkgCreated,  setQcPkgCreated]  = useState<{ by: string | null; date: string | null }>({ by: null, date: null });  // OG header (persisted): batch creator + package create date
  // Reset selection whenever the kind or dataset changes — including a Search By
  // flip on the manual queue (JOBID rows are jobs, QCBatchID rows are batches, so
  // a stale selected id would never match the swapped grid).
  useEffect(() => { setSelectedIds(new Set()); setQcSel(new Set()); setAnalysisSel(new Set()); }, [kind, searchBy]);

  // Browser Back returns from the Open Package detail overlay to the list.
  // The detail is an on-page overlay (same URL, no route change), so without
  // this the Back button would skip past it to the previous page.  We push a
  // history entry when it opens and close the overlay on popstate (Back); the
  // detail's X closes via closePackage → history.back() so the entry pops
  // cleanly and Back/X behave identically.
  useEffect(() => {
    if (!pkgTarget) return;
    window.history.pushState({ sdmsPkg: true }, '');
    const onPop = () => setPkgTarget(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [pkgTarget]);
  const closePackage = useCallback(() => {
    if ((window.history.state as { sdmsPkg?: boolean } | null)?.sdmsPkg) window.history.back();
    else setPkgTarget(null);
  }, []);

  // QC Batches list Data Package link: run the OG :8910 create-or-find SP (same gates as
  // the SDMS entry ribbon), then open the package review view as a modal popup.  A gate
  // refusal (e.g. "Data Package is not created for this QC batch.") surfaces as a warning.
  const openQcDataPackage = useCallback(async (r: AnalyticalBatchRow) => {
    try {
      const pkg = await runLoading(qcBatchApi.createSdmsDataPackage(r.id, true /* list mode — OG :13905 gates */),
        { blocking: true, label: 'Preparing data package…' });
      setQcPkgStatus(pkg?.status ?? null);   // 1 PendingSubmission → Submit; else view-only (match OG: validation is on the Result Validation form)
      setQcPkgCreated({ by: pkg?.batchCreatedBy ?? null, date: pkg?.packageCreatedDate ?? null });   // OG header (persisted)
      setQcPkgTarget(r);
    } catch (e) {
      notify.warning((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Could not prepare the data package.');
    }
  }, [notify]);

  // Open a QC batch's DETAIL form (QcBatchDetailPage, read-only) — the same view
  // clicking the QCBatchID opens. Shared by onQcBatchRowClick, the SDMS Result
  // Validation list's QCBatchID link, and the QC Batches deep-link (user requests
  // 2026-10-27). Defined here so the column factory (above onQcBatchRowClick) can use it.
  const openQcBatchDetail = useCallback((row: AnalyticalBatchRow) => {
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
    });
  }, []);
  // The SDMS Result Validation / Approval list's QCBatchID link opens the batch's
  // QC Batch detail as a MODAL POPUP over this list (user decision 2026-10-30),
  // keeping the list mounted behind it — NOT the full-page switch openQcBatchDetail
  // does for the dedicated QC Batches list. Shared <QcBatchModal> (standing rule 8).
  const openQcBatchModal = useCallback((row: AnalyticalBatchRow) => setQcBatchModalRow(row), []);
  // Open a PREP batch's DETAIL form (PrepBatchDetailPage, read-only) — the same
  // view clicking the prep batch row opens. Shared by onPrepBatchRowClick and the
  // Prep Batches deep-link (user request 2026-10-27).
  const openPrepBatchDetail = useCallback((row: PrepBatchRow) => {
    setPrepBatchSeed({
      testMethodId: '', tier: row.tier ?? 2, sort: row.sort ?? 1,
      matrix: row.matrix ?? undefined, test: row.test ?? undefined, method: row.method ?? undefined,
      prepOid: row.id, viewOnly: true,
    });
  }, []);

  // Deep-link from another form (e.g. the SDMS Result Validation detail's "QC
  // Batches" button): navigateToPage('qcbatches', qcBatchId) parks the QCBatchID
  // so this page opens THAT batch's DETAIL form directly — the same view clicking
  // the QCBatchID in the list opens (user request 2026-10-27), not the bare list.
  // Consumed once on mount (navigateTo.ts); the detail opens once the rows load
  // (see the effect next to onQcBatchRowClick). A later plain sidebar visit opens
  // the list, as standing rule 10 requires.
  const [pendingQcBatchId] = useState<string | null>(
    () => (kind === 'qc-batches' ? consumePendingRecord('qcbatches') : null));
  const qcDeepLinkOpenedRef = useRef(false);
  // Same deep-link for the SDMS Result Validation detail's "Prep Batches" button:
  // navigateToPage('prepbatches', prepBatchId) parks the PrepBatchID so this page
  // opens THAT prep batch's DETAIL form directly (user request 2026-10-27) — like
  // QC Batches. Consumed once on mount; the detail opens once the rows load (the
  // effect below). A later plain sidebar visit opens the list, as rule 10 requires.
  const [pendingPrepBatchId] = useState<string | null>(
    () => (kind === 'prep-batches' ? consumePendingRecord('prepbatches') : null));
  const prepDeepLinkOpenedRef = useRef(false);
  useEffect(() => {
    if (prepDeepLinkOpenedRef.current || !pendingPrepBatchId || kind !== 'prep-batches') return;
    const row = (rows as PrepBatchRow[]).find(r => r.batchId === pendingPrepBatchId);
    if (row) { prepDeepLinkOpenedRef.current = true; openPrepBatchDetail(row); }
  }, [pendingPrepBatchId, rows, kind, openPrepBatchDetail]);

  const load = useCallback(async () => {
    // Stale-response guard (user report 2026-09-09): rapid kind/status/date/search changes
    // fire overlapping list loads; only the LATEST may write rows/error so a slow earlier
    // response never clobbers the grid (or the RT status chips) with stale/empty data.
    const myReq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (supportsDate(kind)) {
        const since = sinceFromKey(dateRange);
        if (since) params.sinceDate = since;
      }
      if (kind === 'result-tracking' || kind === 'result-approval'
          || kind === 'result-validation-manual') {
        params.searchBy = searchBy;
        if (search.trim()) params.search = search.trim();
      }
      // Result Tracking server-side query-panel filters (2026-09-03) — the four
      // that have no column at the list grain (Matrix · Test · Project Name ·
      // Site Name) are resolved by the SP; '|'-joined multi-value (ANY). The
      // other four (QC Batch ID · Job ID · Client · Project ID) + date ranges
      // stay client-side in rtFiltered.
      if (isTracking) {
        // Per-stage JOB grain (user request 2026-09-04): one row per (job × status
        // it has), so the status chips count per stage and a job spanning stages
        // shows under each. Overrides the searchBy grain server-side.
        params.perStage = 'true';
        // NOTE (user request 2026-09-07): the Query-Panel filters (Test/Matrix/
        // ProjectName/Site) are DELIBERATELY NOT sent to this per-stage list load.
        // This list feeds ONLY the Status chips (its own grid is display:none — the
        // visible Sample/QC grid is the ResultTrackingDetail embed, filtered
        // independently via windowFilters). The chips must reflect the FULL date
        // window (every workflow stage with data), not the query-filtered subset —
        // else applying e.g. Test=VOC + Load hid the stages with no VOC job (8 chips
        // → 4). The Query Panel is a refinement WITHIN a selected status, so it
        // narrows the grid, never the chip set.
      }
      // Result Approval query-panel server-side filters (task RTCFR, 2026-09-04) —
      // the same four with no column at the list grain (Matrix · Test · Project
      // Name · Site Name), resolved by AI_Analysis_ResultApproval_SP; '|'-joined
      // multi-value (ANY). The row-present fields + Date Received stay client-side
      // in raFiltered.
      if (isManualAppr) {
        const joinv = (a: string[]) => a.filter(Boolean).join('|');
        if (raApplied.matrix.length)         params.matrix         = joinv(raApplied.matrix);
        if (raApplied.test.length)           params.test           = joinv(raApplied.test);
        if (raApplied.projectName.length)    params.projectName    = joinv(raApplied.projectName);
        if (raApplied.sampleLocation.length) params.sampleLocation = joinv(raApplied.sampleLocation);
        // Date Analyzed / Date Collected have no list-grain column → server-side
        // (2026-09-05). Date Received stays client-side (raFiltered on receivedDate).
        if (raApplied.analyzedFrom)  params.analyzedFrom  = raApplied.analyzedFrom;
        if (raApplied.analyzedTo)    params.analyzedTo    = raApplied.analyzedTo;
        if (raApplied.collectedFrom) params.collectedFrom = raApplied.collectedFrom;
        if (raApplied.collectedTo)   params.collectedTo   = raApplied.collectedTo;
      }
      // Result Validation query-panel server-side filters (task RTCFR, 2026-09-05) —
      // the SAME four no-list-column filters + date ranges as Result Approval,
      // resolved by AI_Analysis_ResultValidation_SP; '|'-joined multi-value (ANY).
      if (isManualVal) {
        const joinv = (a: string[]) => a.filter(Boolean).join('|');
        if (rvApplied.matrix.length)         params.matrix         = joinv(rvApplied.matrix);
        if (rvApplied.test.length)           params.test           = joinv(rvApplied.test);
        if (rvApplied.projectName.length)    params.projectName    = joinv(rvApplied.projectName);
        if (rvApplied.sampleLocation.length) params.sampleLocation = joinv(rvApplied.sampleLocation);
        if (rvApplied.analyzedFrom)  params.analyzedFrom  = rvApplied.analyzedFrom;
        if (rvApplied.analyzedTo)    params.analyzedTo    = rvApplied.analyzedTo;
        if (rvApplied.collectedFrom) params.collectedFrom = rvApplied.collectedFrom;
        if (rvApplied.collectedTo)   params.collectedTo   = rvApplied.collectedTo;
      }
      const res = await api.get(ENDPOINT[kind], { params });
      if (myReq !== loadSeq.current) return;   // superseded — drop stale response
      setRows((res.data as { data: unknown[] }).data ?? []);
    } catch (e) {
      if (myReq !== loadSeq.current) return;   // superseded — don't clobber with an error
      console.error(e);
      setRows([]);
      setError('Failed to load — check the API logs.');
    } finally {
      if (myReq === loadSeq.current) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, [kind, dateRange, searchBy, search, isTracking,
      // rtApplied.* intentionally NOT a dep (2026-09-07): the RT per-stage list feeds
      // the Status chips only and must stay full-window; the Query Panel filters the
      // embed grid, not this load. (Re-adding them would shrink the chip set again.)
      isManualAppr,
      raApplied.matrix, raApplied.test, raApplied.projectName, raApplied.sampleLocation,
      raApplied.analyzedFrom, raApplied.analyzedTo, raApplied.collectedFrom, raApplied.collectedTo,
      isManualVal,
      rvApplied.matrix, rvApplied.test, rvApplied.projectName, rvApplied.sampleLocation,
      rvApplied.analyzedFrom, rvApplied.analyzedTo, rvApplied.collectedFrom, rvApplied.collectedTo]);

  useEffect(() => { void load(); }, [load]);
  // Reset status chip when the kind changes (different columns / data).
  useEffect(() => { setStatusChip('all'); }, [kind]);
  // Restore the maximized grid on Escape, and on navigation to another screen.
  useEffect(() => { setGridMaxed(false); }, [kind]);
  useEffect(() => {
    if (!gridMaxed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMaxed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMaxed]);

  // Does this kind have a status column suitable for chip filtering?
  // Includes prep-queue + result-tracking (GoLive parity — both grids
  // ship with the same status chip row even though they currently only
  // have one common status; future SP additions will surface naturally
  // because the chips are derived from whatever statusText values the
  // API returns).
  const hasStatusChips =
    kind === 'prep-queue'    || kind === 'prep-batches'
    || kind === 'qc-batches' || kind === 'result-tracking'
    || kind === 'result-validation' || kind === 'result-approval'
    || kind === 'result-validation-manual';

  // ── Result Tracking's Query Panel filters (QC Batch ID / Job ID / Client
  // Name / Project ID) — applied client-side, ahead of the free-text search.
  // Data loading itself (sinceDate/searchBy → the SP call) is unchanged.
  const rtFiltered = useMemo(() => {
    if (!isTracking || !hasActiveReeFilters(rtApplied)) return rows;
    const inSet = (set: string[], v: string | null) => set.length === 0 || (v != null && set.includes(v));
    return (rows as ResultTrackingRow[]).filter(r =>
      inSet(rtApplied.batchId, r.qcBatchID)
      && inSet(rtApplied.id, r.jobID)
      && inSet(rtApplied.clientName, r.client)
      && inSet(rtApplied.projectId, r.projectID)
      && inLocalDateRange(r.createdDate, rtApplied.receivedFrom, rtApplied.receivedTo)
      && inLocalDateRange(r.analyzedDate, rtApplied.analyzedFrom, rtApplied.analyzedTo));
  }, [rows, isTracking, rtApplied]);

  // ── Result Approval's Query Panel filters (task RTCFR, 2026-09-04) — the
  // row-present fields applied client-side, ahead of the fuzzy search. The four
  // no-column-at-grain fields (Matrix · Test · Project Name · Site Name) are
  // resolved server-side (see load()); Date Received maps to the row's
  // receivedDate. Date Analyzed / Date Collected have no value at the job/batch
  // grain, so the panel renders them disabled ("soon") and they never filter.
  const raFiltered = useMemo(() => {
    if (!isManualAppr || !hasActiveRapprFilters(raApplied)) return rows;
    const inSet = (set: string[], v: string | null) => set.length === 0 || (v != null && set.includes(v));
    return (rows as ResultValidationListRow[]).filter(r =>
      inSet(raApplied.batchId, r.qcBatchID)
      && inSet(raApplied.id, r.jobID)
      && inSet(raApplied.clientName, r.client)
      && inSet(raApplied.projectId, r.projectID)
      && inSet(raApplied.projectName, r.projectName)
      && inLocalDateRange(r.receivedDate, raApplied.receivedFrom, raApplied.receivedTo));
  }, [rows, isManualAppr, raApplied]);

  // ── Result Validation's Query Panel filters (task RTCFR, 2026-09-05) — the exact
  // parallel of raFiltered: row-present fields client-side, the four no-column
  // fields + Date Analyzed/Collected server-side (see load()); Date Received maps
  // to the row's receivedDate.
  const rvFiltered = useMemo(() => {
    if (!isManualVal || !hasActiveRapprFilters(rvApplied)) return rows;
    const inSet = (set: string[], v: string | null) => set.length === 0 || (v != null && set.includes(v));
    return (rows as ResultValidationListRow[]).filter(r =>
      inSet(rvApplied.batchId, r.qcBatchID)
      && inSet(rvApplied.id, r.jobID)
      && inSet(rvApplied.clientName, r.client)
      && inSet(rvApplied.projectId, r.projectID)
      && inSet(rvApplied.projectName, r.projectName)
      && inLocalDateRange(r.receivedDate, rvApplied.receivedFrom, rvApplied.receivedTo));
  }, [rows, isManualVal, rvApplied]);

  // Dropdown options for the Result Tracking panel (2026-09-03). Two sources,
  // merged in rtOptions below:
  //  • The four fields with NO column at the list grain (Sample Matrix · Test ·
  //    Project Name · Site Name) come from AI_Analysis_ResultTracking_FilterOptions_SP
  //    (rtSpOptions), status-scoped server-side.
  //  • The four fields that ARE grid columns (QC Batch ID · Job ID · Client ·
  //    Project ID) are derived from the currently status-filtered ROWS, so the
  //    pickers offer EXACTLY the values visible in the grid for the selected
  //    Status chip (user report 2026-09-03: "Pending Entry still shows all Job ID"
  //    — the SP's job universe spans every job touched by a status's batches,
  //    which is MORE than the batch rows shown; deriving from the rows matches
  //    the grid).
  // Cascading query filters for Result Tracking (task RTCFR, 2026-09-07) — mirrors
  // REE. Two relations from AI_Analysis_ResultTracking_FacetRows_SP (job-side combos
  // + QC Batch↔Job edges), status-scoped SERVER-side (re-fetched when the Status chip
  // or date window changes). Categorical cascading is computed client-side by joining
  // the two relations logically (see rtOptions below). Replaces the old flat
  // rtSpOptions (FilterOptions) + row-derived hybrid.
  const [rtFacetJobs, setRtFacetJobs]   = useState<RtFacetJob[]>([]);
  const [rtFacetEdges, setRtFacetEdges] = useState<RtFacetEdge[]>([]);
  const rtFacetSeq = useRef(0);   // stale-response guard for the RT facet fetch
  const loadRtFacet = useCallback(async () => {
    if (!isTracking) { rtFacetSeq.current++; setRtFacetJobs([]); setRtFacetEdges([]); return; }
    const myReq = ++rtFacetSeq.current;   // stale-response guard (rapid Status/date changes)
    try {
      const params: Record<string, string> = { searchBy: 'JOBID' };
      const start = sinceFromKey(dateRange);
      if (start) params.startDate = start;
      // Status scoped server-side. RTT's chip is now a BUCKET (approved/pending) — send
      // its member statuses '|'-joined; the facet SP splits @Status and matches IN the set
      // (task RTCFR, 2026-09-11). 'all' = no scope.
      if (statusChip === 'approved' || statusChip === 'pending')
        params.status = RT_BUCKET_STATUSES[statusChip].join('|');
      // The facet is scoped by the quick Date Range window + Status ONLY — the panel
      // From/To date controls do NOT re-scope it (user request 2026-09-09). They are
      // auto-filled to the current filter combination's date range and filter the GRID;
      // feeding them into the facet would strand it at the previous Test's range on the
      // next switch. The date BOUNDS the controls snap to derive client-side from this
      // status-scoped facet, so switching Test/Status always recomputes correctly.
      const res = await api.get('/api/v1/analysis/result-tracking/facet-rows',
        { params, silentLoading: true } as never);
      if (myReq !== rtFacetSeq.current) return;   // superseded — keep the latest facet
      const d = (res.data as { data: { jobs: RtFacetJob[]; batchEdges: RtFacetEdge[] } }).data;
      setRtFacetJobs(d?.jobs ?? []);
      setRtFacetEdges(d?.batchEdges ?? []);
    } catch {
      if (myReq !== rtFacetSeq.current) return;   // superseded — don't wipe fresh options
      setRtFacetJobs([]); setRtFacetEdges([]);   // pickers just show no options; not fatal
    }
  }, [isTracking, dateRange, statusChip]);
  useEffect(() => { void loadRtFacet(); }, [loadRtFacet]);

  // Cascading (faceted) Query Panel for Result Approval & Result Validation (task
  // RTCFR, 2026-09-08) — the exact parallel of the RT facet. Each form fetches its two
  // facet relations (job combos + QC Batch↔Job edges) from its own facet-rows SP,
  // scoped to its pending stage; the categorical cascading + prune are computed
  // client-side by the shared facetOptionsFrom() helper. Replaces the old flat
  // loadRaOptions/loadRvOptions (AI_Analysis_Result*_FilterOptions_SP) — which offered
  // every distinct value regardless of the other picks (no cascading).
  const [raFacetJobs, setRaFacetJobs]   = useState<RtFacetJob[]>([]);
  const [raFacetEdges, setRaFacetEdges] = useState<RtFacetEdge[]>([]);
  const [rvFacetJobs, setRvFacetJobs]   = useState<RtFacetJob[]>([]);
  const [rvFacetEdges, setRvFacetEdges] = useState<RtFacetEdge[]>([]);
  // The facet is scoped by the quick Date Range window + stage ONLY — the panel From/To
  // date controls do NOT re-scope it (user request 2026-09-09; same rationale as RT/REE:
  // auto-filled dates would strand the facet at the previous Test's range). They are
  // auto-filled to the filter combination's range and filter the GRID (load() sends them).
  const loadManualFacet = useCallback(async (
    active: boolean, endpoint: string,
    setJobs: (j: RtFacetJob[]) => void, setEdges: (e: RtFacetEdge[]) => void) => {
    if (!active) { setJobs([]); setEdges([]); return; }
    try {
      const params: Record<string, string> = {};
      const start = sinceFromKey(dateRange);
      if (start) params.startDate = start;
      const res = await api.get(endpoint, { params, silentLoading: true } as never);
      const d = (res.data as { data: { jobs: RtFacetJob[]; batchEdges: RtFacetEdge[] } }).data;
      setJobs(d?.jobs ?? []); setEdges(d?.batchEdges ?? []);
    } catch {
      setJobs([]); setEdges([]);   // pickers just show no options; not fatal
    }
  }, [dateRange]);
  useEffect(() => { void loadManualFacet(isManualAppr, '/api/v1/analysis/result-approval/facet-rows', setRaFacetJobs, setRaFacetEdges); },
    [isManualAppr, loadManualFacet]);
  useEffect(() => { void loadManualFacet(isManualVal, '/api/v1/analysis/result-validation/facet-rows', setRvFacetJobs, setRvFacetEdges); },
    [isManualVal, loadManualFacet]);

  const raFacet = useMemo(() => facetOptionsFrom(raFacetJobs, raFacetEdges, raDraft), [raFacetJobs, raFacetEdges, raDraft]);
  const rvFacet = useMemo(() => facetOptionsFrom(rvFacetJobs, rvFacetEdges, rvDraft), [rvFacetJobs, rvFacetEdges, rvDraft]);
  const raOptions = raFacet.options;
  const rvOptions = rvFacet.options;

  // Auto-fill the six date controls with the current filter combination's date range
  // (user request 2026-09-09; same behaviour as REE). Gated on the CATEGORICAL signature
  // so a user editing a date is never overwritten — only a combo change / Clear
  // recalculates. A dimension with no dates (e.g. unanalyzed) clears to empty; no
  // categorical filter → all empty. Facet bounds are already stage-scoped (RA pending
  // approval, RV waiting for validation) so the dates are stage-correct.
  const raCatSig = useRef('');
  useEffect(() => {
    const sig = JSON.stringify([raDraft.batchId, raDraft.id, raDraft.test, raDraft.matrix,
      raDraft.clientName, raDraft.projectId, raDraft.projectName, raDraft.sampleLocation]);
    if (sig === raCatSig.current) return;
    raCatSig.current = sig;
    const has = !!(raDraft.batchId.length || raDraft.id.length || raDraft.test.length || raDraft.matrix.length
      || raDraft.clientName.length || raDraft.projectId.length || raDraft.projectName.length || raDraft.sampleLocation.length);
    const b = has ? raOptions.dateBounds : undefined;
    setRaDraft(prev => ({ ...prev,
      analyzedFrom: b?.analyzed?.min ?? '', analyzedTo: b?.analyzed?.max ?? '',
      receivedFrom: b?.received?.min ?? '', receivedTo: b?.received?.max ?? '',
      collectedFrom: b?.collected?.min ?? '', collectedTo: b?.collected?.max ?? '' }));
  }, [raDraft.batchId, raDraft.id, raDraft.test, raDraft.matrix, raDraft.clientName,
      raDraft.projectId, raDraft.projectName, raDraft.sampleLocation, raOptions.dateBounds]);

  const rvCatSig = useRef('');
  useEffect(() => {
    const sig = JSON.stringify([rvDraft.batchId, rvDraft.id, rvDraft.test, rvDraft.matrix,
      rvDraft.clientName, rvDraft.projectId, rvDraft.projectName, rvDraft.sampleLocation]);
    if (sig === rvCatSig.current) return;
    rvCatSig.current = sig;
    const has = !!(rvDraft.batchId.length || rvDraft.id.length || rvDraft.test.length || rvDraft.matrix.length
      || rvDraft.clientName.length || rvDraft.projectId.length || rvDraft.projectName.length || rvDraft.sampleLocation.length);
    const b = has ? rvOptions.dateBounds : undefined;
    setRvDraft(prev => ({ ...prev,
      analyzedFrom: b?.analyzed?.min ?? '', analyzedTo: b?.analyzed?.max ?? '',
      receivedFrom: b?.received?.min ?? '', receivedTo: b?.received?.max ?? '',
      collectedFrom: b?.collected?.min ?? '', collectedTo: b?.collected?.max ?? '' }));
  }, [rvDraft.batchId, rvDraft.id, rvDraft.test, rvDraft.matrix, rvDraft.clientName,
      rvDraft.projectId, rvDraft.projectName, rvDraft.sampleLocation, rvOptions.dateBounds]);

  // Prune any selected RA/RV filter value that's no longer valid under the OTHER
  // filters (mirrors RT). Only removes values, so it converges; guarded vs loops.
  useEffect(() => {
    if (!isManualAppr || (raFacetJobs.length === 0 && raFacetEdges.length === 0)) return;
    const patch: Partial<RapprFilters> = {};
    for (const [k, valid] of Object.entries(raFacet.validFor)) {
      const cur = raDraft[k as keyof RapprFilters] as string[];
      const kept = cur.filter(v => valid.has(v));
      if (kept.length !== cur.length) (patch as Record<string, string[]>)[k] = kept;
    }
    if (Object.keys(patch).length) setRaDraft(d => ({ ...d, ...patch }));
  }, [isManualAppr, raFacet, raFacetJobs, raFacetEdges, raDraft]);
  useEffect(() => {
    if (!isManualVal || (rvFacetJobs.length === 0 && rvFacetEdges.length === 0)) return;
    const patch: Partial<RapprFilters> = {};
    for (const [k, valid] of Object.entries(rvFacet.validFor)) {
      const cur = rvDraft[k as keyof RapprFilters] as string[];
      const kept = cur.filter(v => valid.has(v));
      if (kept.length !== cur.length) (patch as Record<string, string[]>)[k] = kept;
    }
    if (Object.keys(patch).length) setRvDraft(d => ({ ...d, ...patch }));
  }, [isManualVal, rvFacet, rvFacetJobs, rvFacetEdges, rvDraft]);

  // ── Cascading (faceted) option derivation for Result Tracking ──────────────
  // Job-side controls (Client/Project/ProjectName/Test/Matrix/Site/Job) are job
  // attributes; QC Batch relates to jobs via edges. Status is already applied
  // server-side (the facet fetch is status-scoped). rtBatchJobSet = the jobs linked
  // to the currently-selected QC Batch(es), or null when no batch filter is set.
  const rtBatchJobSet = useMemo<Set<string | null> | null>(() =>
    rtDraft.batchId.length
      ? new Set(rtFacetEdges.filter(e => e.qcBatchID && rtDraft.batchId.includes(e.qcBatchID)).map(e => e.jobID))
      : null,
    [rtFacetEdges, rtDraft.batchId]);
  // The selected QC Batches' OWN tests (task RTCFR, 2026-09-10) — a batch belongs to one
  // test, so selecting a batch narrows Test to it, and a Test selection narrows QC Batch
  // to batches OF that test (see rtOptions / prune below).
  const rtBatchTestSet = useMemo<Set<string> | null>(() =>
    rtDraft.batchId.length
      ? new Set(rtFacetEdges.filter(e => e.qcBatchID && rtDraft.batchId.includes(e.qcBatchID))
          .map(e => e.batchTest).filter((v): v is string => !!v))
      : null,
    [rtFacetEdges, rtDraft.batchId]);
  // Job-combos satisfying the job-side filters (optionally excluding one control's
  // own filter) AND — unless computing the QC Batch control itself — the batch filter.
  // Categorical-only facet filter (exclude-self on `except`). Used ONLY for the date-picker
  // BOUNDS (auto-fill), which must see the combo's FULL date span — so it must NOT be
  // narrowed by the date pickers themselves (that would collapse each picker to its own
  // current value).
  const rtJobsForCat = useCallback((except: keyof ReeFilters): RtFacetJob[] => {
    const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
    return rtFacetJobs.filter(r =>
      (except === 'id'             || inSel(rtDraft.id, r.jobID))
      && (except === 'clientName'     || inSel(rtDraft.clientName, r.client))
      && (except === 'projectId'      || inSel(rtDraft.projectId, r.projectID))
      && (except === 'projectName'    || inSel(rtDraft.projectName, r.projectName))
      && (except === 'test'           || inSel(rtDraft.test, r.test))
      && (except === 'matrix'         || inSel(rtDraft.matrix, r.matrix))
      && (except === 'sampleLocation' || inSel(rtDraft.sampleLocation, r.sampleLocation))
      && (except === 'batchId' || !rtBatchJobSet || (r.jobID != null && rtBatchJobSet.has(r.jobID))));
  }, [rtFacetJobs, rtDraft, rtBatchJobSet]);

  // Date-picker BOUNDS = the available span of each date over the CATEGORICAL-only facet
  // (rtJobsForCat). Computed here (not from rtJobsFor) so the auto-cascade below can't feed
  // back on itself — bounds never depend on the current date selection. RTCFR 2026-09-17.
  const rtDateBounds = useMemo(() => ({
    analyzed:  jobDateBounds(rtJobsForCat('analyzedFrom'), r => r.analyzedDate),
    received:  jobDateBounds(rtJobsForCat('analyzedFrom'), r => r.receivedDate),
    collected: jobDateBounds(rtJobsForCat('analyzedFrom'), r => r.collectDate),
  }), [rtJobsForCat]);

  // AUTO-CASCADE date filter (RTCFR, 2026-09-17). The six pickers are AUTO-FILLED to the
  // combo's available span (see the effect below) so the user sees the range — but a date end
  // counts as an ACTIVE filter ONLY when the user has TIGHTENED it inside that span (From >
  // min, or To < max). A value equal to the auto-bound (the full span) or empty is INACTIVE.
  // This is what lets auto-fill coexist with faceting: selecting a Client fills its span but
  // does NOT filter, so the client keeps its full count and sibling clients stay listed; the
  // moment the user narrows a date, the combo counts, cascading options AND the grid all apply
  // the SAME narrowed predicate together (rtActiveDates is committed into rtApplied at Load).
  const rtActiveDates = useMemo(() => {
    const from = (val: string, min?: string) => (val && (!min || val > min)) ? val : '';
    const to   = (val: string, max?: string) => (val && (!max || val < max)) ? val : '';
    return {
      analyzedFrom:  from(rtDraft.analyzedFrom,  rtDateBounds.analyzed?.min),
      analyzedTo:    to  (rtDraft.analyzedTo,    rtDateBounds.analyzed?.max),
      receivedFrom:  from(rtDraft.receivedFrom,  rtDateBounds.received?.min),
      receivedTo:    to  (rtDraft.receivedTo,    rtDateBounds.received?.max),
      collectedFrom: from(rtDraft.collectedFrom, rtDateBounds.collected?.min),
      collectedTo:   to  (rtDraft.collectedTo,   rtDateBounds.collected?.max),
    };
  }, [rtDraft.analyzedFrom, rtDraft.analyzedTo, rtDraft.receivedFrom, rtDraft.receivedTo,
      rtDraft.collectedFrom, rtDraft.collectedTo, rtDateBounds]);

  // The Query-Panel Sample COUNTS, cascading OPTIONS and Job ID set apply the ACTIVE date
  // ranges (rtActiveDates) — the SAME predicate the grid Load uses — so combo == grid always.
  // A set bound excludes rows whose date is missing or out of range (grid parity: the SP uses
  // `AnalyzedDate IS NOT NULL AND within`); a sample is counted if ANY of its facet rows
  // survives. Date-only yyyy-MM-dd compare, `to` INCLUSIVE. An inactive (full-span/empty) end
  // imposes no constraint, so NULL-date samples are INCLUDED until the user actually narrows.
  const rtJobsFor = useCallback((except: keyof ReeFilters): RtFacetJob[] => {
    const inYmd = (ymd: string | null, from: string, to: string): boolean => {
      if (!from && !to) return true;   // inactive end(s) → no constraint (NULL dates kept)
      if (!ymd) return false;          // active bound but row has no date → excluded (grid parity)
      const d = ymd.slice(0, 10);
      if (from && d < from) return false;
      if (to   && d > to)   return false;
      return true;
    };
    return rtJobsForCat(except).filter(r =>
      inYmd(r.analyzedDate, rtActiveDates.analyzedFrom, rtActiveDates.analyzedTo)
      && inYmd(r.receivedDate, rtActiveDates.receivedFrom, rtActiveDates.receivedTo)
      && inYmd(r.collectDate,  rtActiveDates.collectedFrom, rtActiveDates.collectedTo));
  }, [rtJobsForCat, rtActiveDates]);

  // Job ID combo universe = the SELECTED status bucket's DISTINCT jobs, taken from the
  // chip's OWN source (rows / AI_Analysis_ResultTracking_JobStatus_SP) so the Job ID
  // dropdown count matches the Status chip EXACTLY for every user and every DB. The facet
  // #J can legitimately differ from the chip — per-row test-method authorization for a
  // non-admin, QC-type rows, or an un-migrated facet SP — which would make Job ID ≠ chip.
  // Sourcing Job ID from the chip set removes that drift; the OTHER job-side controls keep
  // cascading over #J unchanged (task RTCFR — RTT status group / Job Count logic
  // correction). rtChipJobIds mirrors rtBucketCounts' partition (a job is in a bucket if
  // any of its stages is there; 'all' = every job).
  const rtChipJobIds = useMemo(() => {
    if (!isTracking) return [] as string[];
    const s = new Set<string>();
    for (const r of rows as ResultTrackingRow[]) {
      const j = r.jobID; if (!j) continue;
      if (statusChip === 'all' || rtStatusBucketOf(r.statusText) === statusChip) s.add(j);
    }
    return Array.from(s);
  }, [rows, isTracking, statusChip]);

  // Job ID options: the bucket set (== chip) when no other job-side filter is picked;
  // when one IS picked the combo still cascades — intersect the bucket set with the facet
  // jobs matching those filters (rtJobsFor('id') already excludes the Job ID filter).
  const rtJobIdOpts = useMemo(() => {
    const desc = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
    const otherJobFilter = rtDraft.clientName.length || rtDraft.projectId.length
      || rtDraft.projectName.length || rtDraft.test.length || rtDraft.matrix.length
      || rtDraft.sampleLocation.length || rtDraft.batchId.length;
    if (!otherJobFilter) return [...rtChipJobIds].sort(desc);
    const facetIdJobs = new Set(rtJobsFor('id').map(r => r.jobID).filter((v): v is string => !!v));
    return rtChipJobIds.filter(j => facetIdJobs.has(j)).sort(desc);
  }, [rtChipJobIds, rtJobsFor, rtDraft.clientName, rtDraft.projectId, rtDraft.projectName,
      rtDraft.test, rtDraft.matrix, rtDraft.sampleLocation, rtDraft.batchId]);

  const rtOptions = useMemo<ReeOptions>(() => {
    const uniq = (vals: (string | null)[]) => Array.from(new Set(vals.filter((v): v is string => !!v)));
    const asc  = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    const desc = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
    // QC Batch options = batches whose job matches ALL job-side filters (the batch filter
    // itself excluded) AND whose OWN test satisfies the Test filter (task RTCFR,
    // 2026-09-10) — so Test = pH lists only pH batches, not every batch on a pH job.
    const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
    const batchJobs = new Set(rtJobsFor('batchId').map(r => r.jobID).filter(Boolean));
    const seen = new Map<string, ReeBatchOption>();
    for (const e of rtFacetEdges)
      if (e.qcBatchID && e.jobID && batchJobs.has(e.jobID) && inSel(rtDraft.test, e.batchTest) && !seen.has(e.qcBatchID))
        seen.set(e.qcBatchID, { id: e.qcBatchID, test: e.batchTest ?? '', method: e.batchMethod ?? '', emp: e.batchEmp ?? '' });
    return {
      clients:         uniq(rtJobsFor('clientName').map(r => r.client)).sort(asc),
      projects:        uniq(rtJobsFor('projectId').map(r => r.projectID)).sort(asc),
      projectNames:    uniq(rtJobsFor('projectName').map(r => r.projectName)).sort(asc),
      tests:           uniq(rtJobsFor('test').map(r => r.test)).filter(t => !rtBatchTestSet || rtBatchTestSet.has(t)).sort(asc),
      matrices:        uniq(rtJobsFor('matrix').map(r => r.matrix)).sort(asc),
      jobIds:          rtJobIdOpts,   // bucket set (== Status chip), cascaded — see rtJobIdOpts
      sampleLocations: uniq(rtJobsFor('sampleLocation').map(r => r.sampleLocation)).sort(asc),
      batchIds:        Array.from(seen.values()).sort((a, b) => desc(a.id, b.id)),
      counts: {
        test:           sampleCountsFrom(rtJobsFor('test'),           r => r.test),
        matrix:         sampleCountsFrom(rtJobsFor('matrix'),         r => r.matrix),
        projectId:      sampleCountsFrom(rtJobsFor('projectId'),      r => r.projectID),
        projectName:    sampleCountsFrom(rtJobsFor('projectName'),    r => r.projectName),
        clientName:     sampleCountsFrom(rtJobsFor('clientName'),     r => r.client),
        sampleLocation: sampleCountsFrom(rtJobsFor('sampleLocation'), r => r.sampleLocation),
      },
      // Date-picker bounds = the available span over the CATEGORICAL-only facet (see the
      // rtDateBounds memo above) — the auto-fill fills the pickers to this, and it also
      // powers the range hint + the "active only when narrower" rule. RTCFR 2026-09-17.
      dateBounds: rtDateBounds,
    };
  }, [rtJobsFor, rtDateBounds, rtFacetEdges, rtDraft.test, rtBatchTestSet, rtJobIdOpts]);

  // AUTO-CASCADE (RTCFR, 2026-09-17): auto-fill the six RT date pickers to the current
  // combination's available span (rtDateBounds) so the user SEES the range. Gated on the
  // CATEGORICAL signature so a user's own date edit is never overwritten — only a combo change
  // (or Clear/Status switch, which reset rtDraft) recomputes it. Filling to the span is a
  // NO-OP for filtering: rtActiveDates treats a value equal to its auto-bound as INACTIVE, so
  // selecting a value keeps its count and siblings; only when the user tightens a date inside
  // the span does it become an active filter (applied to counts, options AND the grid alike).
  const rtCatSig = useRef('');
  useEffect(() => {
    if (!isTracking) return;
    const sig = JSON.stringify([rtDraft.batchId, rtDraft.id, rtDraft.test, rtDraft.matrix,
      rtDraft.clientName, rtDraft.projectId, rtDraft.projectName, rtDraft.sampleLocation]);
    if (sig === rtCatSig.current) return;
    rtCatSig.current = sig;
    const has = !!(rtDraft.batchId.length || rtDraft.id.length || rtDraft.test.length || rtDraft.matrix.length
      || rtDraft.clientName.length || rtDraft.projectId.length || rtDraft.projectName.length || rtDraft.sampleLocation.length);
    const b = has ? rtDateBounds : undefined;
    setRtDraft(prev => ({ ...prev,
      analyzedFrom: b?.analyzed?.min ?? '', analyzedTo: b?.analyzed?.max ?? '',
      receivedFrom: b?.received?.min ?? '', receivedTo: b?.received?.max ?? '',
      collectedFrom: b?.collected?.min ?? '', collectedTo: b?.collected?.max ?? '' }));
  }, [isTracking, rtDraft.batchId, rtDraft.id, rtDraft.test, rtDraft.matrix, rtDraft.clientName,
      rtDraft.projectId, rtDraft.projectName, rtDraft.sampleLocation, rtDateBounds]);

  // Prune any selected RT filter value that's no longer valid under the OTHER
  // filters (mirrors REE). Only removes values, so it converges; guarded vs loops.
  useEffect(() => {
    if (!isTracking) return;
    if (rtFacetJobs.length === 0 && rtFacetEdges.length === 0) return;
    const uniq = (vals: (string | null)[]) => new Set(vals.filter((v): v is string => !!v));
    const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
    const batchJobs = new Set(rtJobsFor('batchId').map(r => r.jobID).filter(Boolean));
    // Prune a QC Batch whose own test no longer matches the Test filter, and a Test that
    // isn't one of the selected batches' tests (task RTCFR, 2026-09-10) — keeps the
    // Test <-> QC Batch selection coherent after any change.
    const validBatch = new Set(rtFacetEdges.filter(e => e.qcBatchID && e.jobID && batchJobs.has(e.jobID) && inSel(rtDraft.test, e.batchTest)).map(e => e.qcBatchID as string));
    const validTest = new Set([...uniq(rtJobsFor('test').map(r => r.test))].filter(t => !rtBatchTestSet || rtBatchTestSet.has(t)));
    const validFor: Record<string, Set<string>> = {
      batchId:        validBatch,
      // Job ID options come from the CHIP set (rtJobIdOpts, == the Status chip count),
      // which can hold a job the facet #J drops (chip 60 vs facet 59 — that job is in the
      // per-stage list but not the facet's job-side combos). Validate the Job ID selection
      // against the OFFERED options, NOT the facet — else the extra chip-only job is offered
      // then instantly pruned on click, so it can never be ticked (RTCFR first-Job-ID fix).
      id:             new Set(rtJobIdOpts),
      matrix:         uniq(rtJobsFor('matrix').map(r => r.matrix)),
      test:           validTest,
      projectId:      uniq(rtJobsFor('projectId').map(r => r.projectID)),
      projectName:    uniq(rtJobsFor('projectName').map(r => r.projectName)),
      clientName:     uniq(rtJobsFor('clientName').map(r => r.client)),
      sampleLocation: uniq(rtJobsFor('sampleLocation').map(r => r.sampleLocation)),
    };
    const patch: Partial<ReeFilters> = {};
    for (const [k, valid] of Object.entries(validFor)) {
      const cur = rtDraft[k as keyof ReeFilters] as string[];
      const kept = cur.filter(v => valid.has(v));
      if (kept.length !== cur.length) (patch as Record<string, string[]>)[k] = kept;
    }
    if (Object.keys(patch).length) setRtDraft(d => ({ ...d, ...patch }));
  }, [isTracking, rtFacetJobs, rtFacetEdges, rtDraft, rtJobsFor, rtBatchTestSet, rtJobIdOpts]);

  // Query-panel filters passed to the RT window grid so the Sample/QC rows THEMSELVES
  // are filtered server-side (task RTCFR, 2026-09-07 — the filters must hit the grid,
  // not just the dropdowns). Built from rtApplied (what Load applied); '|'-joined
  // multi-values + date bounds. AND-combined server-side; the Status chip still
  // narrows client-side on top.
  const rtWindowFilters = useMemo<Record<string, string>>(() => {
    const j = (a: string[]) => a.filter(Boolean).join('|');
    const f: Record<string, string> = {};
    if (rtApplied.batchId.length)        f.batchId        = j(rtApplied.batchId);
    if (rtApplied.id.length)             f.jobId          = j(rtApplied.id);
    if (rtApplied.matrix.length)         f.matrix         = j(rtApplied.matrix);
    if (rtApplied.test.length)           f.test           = j(rtApplied.test);
    if (rtApplied.projectId.length)      f.projectId      = j(rtApplied.projectId);
    if (rtApplied.projectName.length)    f.projectName    = j(rtApplied.projectName);
    if (rtApplied.clientName.length)     f.client         = j(rtApplied.clientName);
    if (rtApplied.sampleLocation.length) f.sampleLocation = j(rtApplied.sampleLocation);
    if (rtApplied.analyzedFrom)  f.analyzedFrom  = rtApplied.analyzedFrom;
    if (rtApplied.analyzedTo)    f.analyzedTo    = rtApplied.analyzedTo;
    if (rtApplied.receivedFrom)  f.receivedFrom  = rtApplied.receivedFrom;
    if (rtApplied.receivedTo)    f.receivedTo    = rtApplied.receivedTo;
    if (rtApplied.collectedFrom) f.collectedFrom = rtApplied.collectedFrom;
    if (rtApplied.collectedTo)   f.collectedTo   = rtApplied.collectedTo;
    return f;
  }, [rtApplied]);

  // ── Free-text search across the columns the user looks at most ──────────
  // Result Tracking reads its OWN box (rtSearch) — client-side only, never
  // sent to the server; every other kind keeps the shared `search` box, which
  // load() still forwards as @Search for the kinds that use it.
  const searchFiltered = useMemo(() => {
    // Base = the panel-filtered set for whichever mode owns a Query Panel
    // (Result Tracking → rtFiltered, Result Approval → raFiltered); every other
    // kind passes rows straight through. The free-text term is that mode's OWN
    // client-side box (rtSearch / raSearch) or the shared `search` otherwise.
    const base = isTracking ? rtFiltered : isManualAppr ? raFiltered : isManualVal ? rvFiltered : rows;
    const term = (isTracking ? rtSearch : isManualAppr ? raSearch : isManualVal ? rvSearch : search).trim().toLowerCase();
    if (!term) return base;
    return base.filter(r => {
      // Index into all string-y fields on whatever row shape we have
      return Object.values(r as Record<string, unknown>).some(v =>
        typeof v === 'string' && v.toLowerCase().includes(term),
      );
    });
  }, [rtFiltered, raFiltered, rvFiltered, rows, search, rtSearch, raSearch, rvSearch, isTracking, isManualAppr, isManualVal]);

  // ── Status chip filter (only meaningful when hasStatusChips) ────────────
  const filtered = useMemo(() => {
    if (!hasStatusChips || statusChip === 'all') return searchFiltered;
    // Result Tracking groups statuses into Approved/Pending buckets (task RTCFR,
    // 2026-09-11): match the row's bucket. Every other kind keeps the exact-status chip.
    if (isTracking)
      return searchFiltered.filter(r => rtStatusBucketOf((r as { statusText?: string }).statusText) === statusChip);
    return searchFiltered.filter(r => (r as { statusText?: string }).statusText === statusChip);
  }, [searchFiltered, statusChip, hasStatusChips, isTracking]);

  // ── Status chip definitions derived from the loaded data ────────────────
  // Result Tracking shows the FULL status breakdown from the loaded set (rows),
  // NOT the query-filtered subset — so picking a QC Batch / Job ID never hides
  // the other statuses (user request 2026-09-03). Every other kind keeps counting
  // the query/search-filtered set (its chips live-filter the visible grid).
  const statusChips = useMemo(() => {
    if (!hasStatusChips) return [] as [string, number][];
    const m = new Map<string, number>();
    const source = isTracking ? (rows as { statusText?: string }[]) : searchFiltered;
    for (const r of source) {
      const s = (r as { statusText?: string }).statusText;
      if (!s) continue;
      m.set(s, (m.get(s) ?? 0) + 1);
    }
    // Result Tracking (user request 2026-09-02): workflow order, Pending Entry
    // → Sent, instead of the count-descending order every other kind still
    // uses. Any status not in this list (unexpected data) sorts after the
    // known ones, by count desc — nothing silently disappears.
    if (isTracking) {
      const order = RESULT_TRACKING_STATUS_ORDER;
      return Array.from(m.entries()).sort((a, b) => {
        const ia = order.indexOf(a[0]), ib = order.indexOf(b[0]);
        if (ia === -1 && ib === -1) return b[1] - a[1];
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [searchFiltered, rows, hasStatusChips, isTracking]);

  // RTT grouped-bucket counts (task RTCFR, 2026-09-11) — DISTINCT jobs per bucket over the
  // full loaded set (rows are per job×stage). A job is in a bucket if ANY of its stages is
  // there; All = distinct jobs. Drives the Approved / Pending / All Results tabs.
  const rtBucketCounts = useMemo(() => {
    const pending = new Set<string>(), approved = new Set<string>(), all = new Set<string>();
    if (isTracking) for (const r of rows as ResultTrackingRow[]) {
      const j = r.jobID; if (!j) continue; all.add(j);
      (rtStatusBucketOf(r.statusText) === 'pending' ? pending : approved).add(j);
    }
    return { all: all.size, pending: pending.size, approved: approved.size };
  }, [rows, isTracking]);

  // Per-kind shared-grid columns.  Result Tracking swaps its first column based
  // on the Search By dropdown, so that branch depends on `searchBy`.
  // Each kind returns a column set typed to its own row shape; `any` unifies them
  // in one memo without a sprawling union (the column defs stay individually typed).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cols = useMemo<AppColumn<any>[]>(() => {
    switch (kind) {
      case 'prep-queue':        return PREP_QUEUE_COLS;
      case 'analysis-queue':    return ANALYSIS_QUEUE_COLS;
      case 'prep-batches':      return PREP_BATCH_COLS;
      // (Wired 2026-09-10) Data Package link → OG :8910 create-or-find + open the
      // package review view as a modal popup (openQcDataPackage). The per-row
      // enable/disable stays the legacy DataPackageCheckList gate (r.dataPackageEnabled).
      case 'qc-batches':        return qcBatchCols(openQcDataPackage);
      case 'result-validation': return resultValidationCols(
        // Both the DataPackage link AND the per-row Edit icon open the SAME
        // detail view (CLAUDE.md rule 12; legacy parity — clicking Edit in the
        // legacy ALPACA list view also opens the full Batch Information /
        // Batch Results / Check Points / Review Log / Attachments form).
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPkgTarget(r as AnalyticalBatchRow),
        // Per-row Preview (eye) → the SDMS result-image PreviewModal, directly
        // on that row (no need to tick the checkbox + use the toolbar button).
        r => setPreviewTarget(r as AnalyticalBatchRow),
        openQcBatchModal,
        false,        // not the approval grid
        perm.write,   // Role-Based MAC — a Read-only level gets no Edit icon
      );
      // (Merged 2026-07-07) SDMS Result Approval (Level-3) — the SAME batch grid as
      // Result Validation (Open Package · Preview, NO Edit), plus DateVerified /
      // VerifiedBy.  Opens SdmsPackageDetail in approval mode.
      case 'sdms-approval':     return resultValidationCols(
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPkgTarget(r as AnalyticalBatchRow),
        r => setPreviewTarget(r as AnalyticalBatchRow),
        openQcBatchModal,
        true,         // approval → hide Edit + append DateVerified / VerifiedBy
        perm.write,   // Edit is already hidden here; passed so the two stay in step
      );
      // "Result Validation" + "Result Approval" (manual queues) — the Search By
      // dropdown SWAPS the whole grid (ALPACA ResultValidateSearchBy_SelectedItemChanged):
      // JOBID → job-level columns (Samplecheckin_ListView_Result{Validation|Approval}),
      // QCBatchID → QC-batch columns (QCBatch_ListView_Result{Validation|Approval}_Filter).
      // Both stages share the exact same column set — only the underlying Status differs.
      case 'result-approval':
      case 'result-validation-manual':
        return searchBy === 'JOBID' ? resultValidationJobCols() : resultValidationQcCols();
      case 'result-tracking':   return resultTrackingCols();
    }
  }, [kind, searchBy, notify, openQcDataPackage, openQcBatchModal, perm.write]);

  // Both Prep Queue and Analysis Queue drill down to a list of pending
  // sample-parameter rows for the chosen TestMethod (GoLive parity).
  const drillable = kind === 'prep-queue' || kind === 'analysis-queue';
  const getRowKey = (r: unknown): string | number => {
    if (kind === 'prep-queue' || kind === 'analysis-queue') return queueRowKey(r as AnalysisQueueRow);
    // Manual Result Validation QCBatchID mode = one row per (batch, job), so the
    // batch Oid alone isn't unique — key by batch + job (JOBID mode's id already
    // IS the job, so this stays unique there too).
    if (isManualDR) { const m = r as ResultValidationListRow; return `${m.id}::${m.jobOid ?? ''}`; }
    // Result Tracking is now per-stage (job × status), so a job at 2 stages yields
    // 2 rows sharing the same job Oid — key by job + status to keep rows unique.
    if (isTracking) { const t = r as ResultTrackingRow; return `${t.id}::${t.statusText ?? ''}`; }
    return (r as { id: string }).id;
  };

  // Export payload for the SDMS search-row Export icon (and reused by the toolbar).
  const gridEx = useMemo(() => gridExportMatrix(filtered, cols), [filtered, cols]);

  // ── QC Batches list — single-select + toolbar actions (Merged 2026-07-07) ───
  // Radio-like selection (one row at a time).  QC Batches drills to "open an
  // existing QC batch" (view mode) on row click — legacy row-click parity
  // (QCBatchController.cs :12079-12093 sets isview=true; the "Edit" toolbar
  // action is the only way to flip to edit mode).
  const handleQcSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setQcSel(next); return; }
    const added = [...next].find(id => !qcSel.has(id));
    setQcSel(added != null ? new Set([added]) : new Set());
  }, [qcSel]);

  // Analysis Queue single-select (radio-like) — drives "Create QC Batch". (Merged 2026-07-07.)
  // The Sample Prep Queue is MULTI-select (OG SelectedObjects, user report 2026-09-28 "only one
  // row selectable"): several Test Codes may share one Prep Batch ID — New then enforces the
  // same-Prep-Method rule (onNewFromQueue / AI_Analysis_CreatePrepBatch). A QC batch
  // is single-test, so the Analysis Queue stays radio-like.
  const handleAnalysisSelectionChange = useCallback((next: Set<string | number>) => {
    if (kind === 'prep-queue' || next.size <= 1) { setAnalysisSel(next); return; }
    const added = [...next].find(id => !analysisSel.has(id));
    setAnalysisSel(added != null ? new Set([added]) : new Set());
  }, [analysisSel, kind]);

  // SDMS Result Validation / Approval — single-select (radio-like). The workflow is
  // "Step 1 · Select A QC Batch" (singular) → Open Package / Load / Rollback act on
  // that ONE batch (handleSdmsLoad opens selectedBatches[0]; Rollback sends a
  // one-element batchIds array; the stepper keys off length 0 vs >0). Ticking a
  // second row REPLACES the first so only one QC batch is ever selected — the list
  // no longer lets two batches be checked at once. (User request 2026-08-03.)
  const handleSdmsSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);

  // Result Validation (manual) — single-select (radio-like); the Retrieve toolbar
  // button opens the ticked batch's detail (ALPACA: select → Retrieve → the
  // ResultEntry_Validation detail). A row-click opens it too.
  const handleManualSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);
  // (task RTCFR, 2026-09-05) The title-row "Load" that opened the second-page
  // detail for the manual Data-Review forms was removed — both Result Validation
  // and Result Approval now do the whole review on page one via the embedded
  // Sample/QC grid. `selectedManualRow` + `openManualDetail` (the second-page
  // openers) went with it; the full-page `rvDetail` render below is retained but
  // unreached until the second grid is formally removed.

  // Row-click SELECTS the row (ticks its checkbox → enables Load), matching Custom
  // Reporting's JobID-click flow (user request 2026-07-10) — it no longer navigates
  // straight to the detail.  The Load button is the ONLY way to open the detail (no
  // double-click).  Single-select (radio-like): clicking a row selects ONLY it;
  // clicking the already-ticked row clears it.  The manual checkbox + Load flow is unchanged.
  const onManualRowClick = useCallback((r: ResultValidationListRow) => {
    const key = `${r.id}::${r.jobOid ?? ''}`;
    setSelectedIds(prev =>
      (prev.size === 1 && prev.has(key)) ? new Set() : new Set<string | number>([key]));
  }, []);

  // ── Result Tracking — single-select checkbox + "Retrieve" drill-in ──────────
  // Legacy ResultEntryQueryPanel: tick ONE batch → Retrieve → the read-only
  // ResultView.  Row-click ticks the row (radio-like); Retrieve opens the detail.
  // Selection ids are the batch Oid (getRowKey returns r.id for this kind).
  const handleTrackingSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const added = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(added != null ? new Set([added]) : new Set());
  }, [selectedIds]);
  // Row key = job Oid + status (per-stage grain — matches getRowKey), so ticking a
  // stage-row selects exactly that row; opening it drills into the WHOLE job.
  const trackingRowKey = (r: ResultTrackingRow) => `${r.id}::${r.statusText ?? ''}`;
  const onTrackingRowClick = useCallback((r: ResultTrackingRow) => {
    const key = trackingRowKey(r);
    setSelectedIds(prev =>
      (prev.size === 1 && prev.has(key)) ? new Set() : new Set<string | number>([key]));
  }, []);
  const selectedTrackingRow = useMemo<ResultTrackingRow | null>(() => {
    if (!isTracking || selectedIds.size !== 1) return null;
    const [id] = selectedIds;
    return (rows.find(r => trackingRowKey(r as ResultTrackingRow) === id) as ResultTrackingRow | undefined) ?? null;
  }, [isTracking, selectedIds, rows]);
  const openTrackingDetail = useCallback((r: ResultTrackingRow) => {
    // JobID mode: r.id is a Samplecheckin.Oid → load the WHOLE job's results.
    setRtDetail({ id: r.id, qcBatchID: r.qcBatchID, client: r.client, projectID: r.projectID,
      jobID: r.jobID, isJob: searchBy === 'JOBID' });
  }, [searchBy]);

  // (The RT embed is now WINDOW-mode — it loads EVERY job's samples for the window
  // and narrows by the status chip client-side, so it no longer resolves a single
  // anchor job. See the ResultTrackingDetail render below, keyed by rtLoaded +
  // date-range window. user request 2026-09-05.)

  // Controller lifted from the embedded sample-detail grid (user request
  // 2026-09-03): the top control bar hosts its Refresh / Export to / Rollback.
  const [rtEmbed, setRtEmbed] = useState<RtEmbedState>({
    selectedCount: 0, canRollback: false, hasBatch: false, maximized: false,
    exportFilename: 'result-tracking', exportHeaders: [], exportRows: [], loadMs: null, loading: false });
  const rtEmbedApi = useRef<{ refresh: () => void; rollback: () => void } | null>(null);
  const onRtEmbedState = useCallback((s: RtEmbedState) => setRtEmbed(s), []);

  // ── Result Approval embedded Sample/QC grid (task RTCFR, 2026-09-04) ─────────
  // The RESOLVED row (first row of the current status + query set) — anchor on
  // filtered[0]. Drives BOTH the embedded grid (its job's Sample/QC results) AND
  // the top-right "Open". Resolves the FIRST pending-approval job by DEFAULT on
  // open (user request 2026-09-04: "show the grid section by default") — no query
  // needed; applying the Query panel just narrows which job anchors here.
  const raResolvedRow = useMemo<ResultValidationListRow | null>(() => {
    // Empty grid by DEFAULT (user request 2026-09-04) — resolve a job only after a
    // query is applied via Load; on open (no filters) stay null → empty grid.
    if (!isManualAppr || !hasActiveRapprFilters(raApplied)) return null;
    return (filtered as ResultValidationListRow[])[0] ?? null;
  }, [isManualAppr, raApplied, filtered]);
  // The embedded first-page grid IS the real approval detail (ResultValidationDetail,
  // mode='approval') — same data / columns / checkbox / approve logic as the
  // second-page detail (user request 2026-09-04: "second-page grid + its logic on
  // page one"). Build its batch from the resolved job (JOBID grain → scope 'job',
  // id = JobOid). Null until a query resolves a job.
  const raApprovalBatch = useMemo<ResultValidationBatch | null>(() => {
    if (!raResolvedRow) return null;
    return {
      id: raResolvedRow.id, qcBatchID: raResolvedRow.qcBatchID, client: raResolvedRow.client,
      projectID: raResolvedRow.projectID, projectName: raResolvedRow.projectName,
      jobID: raResolvedRow.jobID, jobOid: raResolvedRow.jobOid,
      scope: searchBy === 'JOBID' ? 'job' : 'batch', mode: 'approval',
    };
  }, [raResolvedRow, searchBy]);
  // Multi-JobID Load (task RTCFR 2026-09-21): the FULL set of eligible selected jobs
  // (distinct by JobOid across the current status + query set), NOT just filtered[0].
  // Passed to the embed so ≥2 selected JobIDs load ALL their Sample/QC/Surrogate/ISTD
  // rows (one single-job GET each, merged) and Approve/Rollback act across them. A job
  // appears here ONLY if it's in the pending-approval universe AND passes the applied
  // filters — so a selected-but-ineligible job is correctly excluded. Memoised for a
  // stable identity (the embed re-fetches only when the SET changes).
  const raJobs = useMemo<ResultValidationBatch[]>(() => {
    if (!isManualAppr || !hasActiveRapprFilters(raApplied)) return [];
    const seen = new Set<string>();
    const out: ResultValidationBatch[] = [];
    for (const r of (filtered as ResultValidationListRow[])) {
      // Dedup on the FETCH key (id + jobOid): JobID mode → one /job/{jobOid} per job;
      // QCBatchID mode → one /{batchOid}?jobOid per (batch,job). Correct for both.
      const key = `${r.id}|${r.jobOid ?? ''}`;
      if (!r.id || seen.has(key)) continue;
      seen.add(key);
      out.push({
        id: r.id, qcBatchID: r.qcBatchID, client: r.client,
        projectID: r.projectID, projectName: r.projectName,
        jobID: r.jobID, jobOid: r.jobOid,
        scope: searchBy === 'JOBID' ? 'job' : 'batch', mode: 'approval',
      });
    }
    return out;
  }, [isManualAppr, raApplied, filtered, searchBy]);
  // Export payload published UP by the embedded approval grid — drives the control
  // bar's "Export to" button (moved out of the embed toolbar, user request
  // 2026-09-04). Refresh key: the embed reloads its samples when this bumps, so the
  // control-bar Refresh can refresh the grid now that the embed's own Refresh is gone.
  const [raExport, setRaExport] = useState<{ filename: string; headers: string[]; rows: string[][] }>(
    { filename: 'result-approval', headers: [], rows: [] });
  const onRaExportData = useCallback((d: { filename: string; headers: string[]; rows: string[][] }) => setRaExport(d), []);
  const [raRefreshKey, setRaRefreshKey] = useState(0);
  // Review state published UP by the embedded approval grid — drives the
  // "Review · Approve" instruction bar rendered below the title (moved there from
  // the embed body, user request 2026-09-04). Defaults to a >0 count so the bar
  // reads "review" (not "complete") until the embed publishes.
  const [raReview, setRaReview] = useState<{ reviewLeft: number; actionVerb: string; stampLabel: string; completed: boolean; selectedCount: number }>(
    { reviewLeft: 1, actionVerb: 'Approve', stampLabel: 'ApprovedBy / Date', completed: false, selectedCount: 0 });
  const onRaReviewState = useCallback((s: { reviewLeft: number; actionVerb: string; stampLabel: string; completed: boolean; selectedCount: number }) => setRaReview(s), []);
  // Imperative handle to the embedded approval grid's Rollback / Approve — the
  // control bar's buttons (moved there near Clear, user request 2026-09-04) drive them.
  const raEmbedActionsRef = useRef<{ rollback: () => void; approve: () => void } | null>(null);

  // ── Result Validation embed (task RTCFR, 2026-09-05) — the EXACT parallel of the
  // Result Approval embed above, one Data-Review stage earlier (mode='validation').
  // The first-page grid IS the real Result Validation detail (ResultValidationDetail,
  // embedded, mode='validation'): same data / columns / checkbox / Validate logic as
  // the second-page detail. Empty by default; applying the Query panel + Load resolves
  // the first matching job and fills the Sample/QC grids with tick-to-validate.
  const rvResolvedRow = useMemo<ResultValidationListRow | null>(() => {
    if (!isManualVal || !hasActiveRapprFilters(rvApplied)) return null;
    return (filtered as ResultValidationListRow[])[0] ?? null;
  }, [isManualVal, rvApplied, filtered]);
  const rvValidationBatch = useMemo<ResultValidationBatch | null>(() => {
    if (!rvResolvedRow) return null;
    return {
      id: rvResolvedRow.id, qcBatchID: rvResolvedRow.qcBatchID, client: rvResolvedRow.client,
      projectID: rvResolvedRow.projectID, projectName: rvResolvedRow.projectName,
      jobID: rvResolvedRow.jobID, jobOid: rvResolvedRow.jobOid,
      scope: searchBy === 'JOBID' ? 'job' : 'batch', mode: 'validation',
    };
  }, [rvResolvedRow, searchBy]);
  // Multi-JobID Load (task RTCFR 2026-09-21) — the EXACT parallel of raJobs for
  // Result Validation. Full eligible selected set, distinct by JobOid.
  const rvJobs = useMemo<ResultValidationBatch[]>(() => {
    if (!isManualVal || !hasActiveRapprFilters(rvApplied)) return [];
    const seen = new Set<string>();
    const out: ResultValidationBatch[] = [];
    for (const r of (filtered as ResultValidationListRow[])) {
      const key = `${r.id}|${r.jobOid ?? ''}`;
      if (!r.id || seen.has(key)) continue;
      seen.add(key);
      out.push({
        id: r.id, qcBatchID: r.qcBatchID, client: r.client,
        projectID: r.projectID, projectName: r.projectName,
        jobID: r.jobID, jobOid: r.jobOid,
        scope: searchBy === 'JOBID' ? 'job' : 'batch', mode: 'validation',
      });
    }
    return out;
  }, [isManualVal, rvApplied, filtered, searchBy]);
  const [rvExport, setRvExport] = useState<{ filename: string; headers: string[]; rows: string[][] }>(
    { filename: 'result-validation', headers: [], rows: [] });
  const onRvExportData = useCallback((d: { filename: string; headers: string[]; rows: string[][] }) => setRvExport(d), []);
  const [rvRefreshKey, setRvRefreshKey] = useState(0);
  const [rvReview, setRvReview] = useState<{ reviewLeft: number; actionVerb: string; stampLabel: string; completed: boolean; selectedCount: number }>(
    { reviewLeft: 1, actionVerb: 'Validate', stampLabel: 'ValidatedBy / Date', completed: false, selectedCount: 0 });
  const onRvReviewState = useCallback((s: { reviewLeft: number; actionVerb: string; stampLabel: string; completed: boolean; selectedCount: number }) => setRvReview(s), []);
  const rvEmbedActionsRef = useRef<{ rollback: () => void; approve: () => void } | null>(null);

  // ── Pending Job / QC Batch selection stage — Result Validation + Result Approval
  // (RTCFR "Apply Consolidated REE Design to Result Validation & Result Approval",
  // 2026-09-28). The EXACT REE pattern, reusing its component + helpers: the forms
  // open on a Sample | QC Batch list of what is pending at THEIR stage (the list's
  // own "Pending Validation" / "Pending Approval" rows — never REE's Pending Entry);
  // Load swaps it for the form's existing embedded detail (ResultValidationDetail —
  // its validate / approve / rollback logic untouched); Back returns here.
  //
  // How it drives the EXISTING machinery: in the selection stage the applied filters
  // stay EMPTY, so `rows` is the full pending list for the Date Range and the Query
  // Panel narrows the tabs CLIENT-SIDE through the stage-scoped facet (instant).
  // Load commits applied = { id: [chosen jobs] } — the pre-existing raJobs / rvJobs
  // resolution then feeds the embed exactly as the old query-panel Load did. A QC
  // Batch load resolves the batch's pending jobs from the facet edges and scopes the
  // embed's rows to the batch (ResultValidationDetail batchIds). ONE shared state
  // set serves both kinds (they're mutually exclusive), reset on a kind change.
  const [drSelTab, setDrSelTab] = useState<ReeSelTab>('Sample');
  const [drSelJobIds, setDrSelJobIds] = useState<Set<string | number>>(new Set());
  const [drSelBatchIds, setDrSelBatchIds] = useState<Set<string | number>>(new Set());
  const [drLoaded, setDrLoaded] = useState(false);          // true = detail stage
  const [drBatchScope, setDrBatchScope] = useState<string[]>([]);
  const [drVisJobs, setDrVisJobs] = useState<PendingJob[]>([]);
  const [drVisBatches, setDrVisBatches] = useState<ReePendingBatch[]>([]);
  useEffect(() => {
    setDrSelTab('Sample'); setDrSelJobIds(new Set()); setDrSelBatchIds(new Set());
    setDrLoaded(false); setDrBatchScope([]);
  }, [kind]);

  const drDraft       = isManualAppr ? raDraft : rvDraft;
  const drFacetJobs   = isManualAppr ? raFacetJobs : rvFacetJobs;
  const drFacetEdges  = isManualAppr ? raFacetEdges : rvFacetEdges;
  const drDateBounds  = (isManualAppr ? raOptions : rvOptions).dateBounds;
  const drStageStatus = isManualAppr ? PENDING_APPROVAL_STATUS : PENDING_VALIDATION_STATUS;
  const setDrApplied  = isManualAppr ? setRaApplied : setRvApplied;
  const setDrQpCollapsed = isManualAppr ? setRaQpCollapsed : setRvQpCollapsed;

  // Facet rows matching the current Query Panel (see facetMatchedJobs).
  const drMatchedJobs = useMemo(
    () => (isManualDR ? facetMatchedJobs(drFacetJobs, drFacetEdges, drDraft, drDateBounds) : []),
    [isManualDR, drFacetJobs, drFacetEdges, drDraft, drDateBounds]);
  const drHasFilter = isManualDR && (hasActiveRapprFilters(drDraft));
  // Sample tab = the form's own pending job rows (JOBID grain), narrowed by the panel.
  const drPendingJobs = useMemo<PendingJob[]>(() => {
    if (!isManualDR) return [];
    const jobs: PendingJob[] = [];
    const seen = new Set<string>();
    for (const r of rows as ResultValidationListRow[]) {
      if (!r.jobID || seen.has(r.jobID)) continue;
      seen.add(r.jobID);
      jobs.push({ jobID: r.jobID, projectID: r.projectID, projectName: r.projectName,
        client: r.client, receivedDate: r.receivedDate, statusText: r.statusText || drStageStatus });
    }
    return filterPendingJobs(jobs, drMatchedJobs, drHasFilter);
  }, [isManualDR, rows, drMatchedJobs, drHasFilter, drStageStatus]);
  // QC Batch tab = the stage's pending batches over the matched jobs (facet edges),
  // limited to jobs that are really in this form's pending list.
  const drPendingBatches = useMemo<ReePendingBatch[]>(() => {
    if (!isManualDR) return [];
    const listed = new Set(drPendingJobs.map(j => j.jobID));
    return buildPendingBatchesFromEdges(drFacetEdges, drMatchedJobs.filter(j => j.jobID && listed.has(j.jobID)),
      drStageStatus, { test: drDraft.test, batchId: drDraft.batchId });
  }, [isManualDR, drFacetEdges, drMatchedJobs, drPendingJobs, drStageStatus, drDraft.test, drDraft.batchId]);

  // Drop selections a filter / Date Range change took off the list (only removes).
  useEffect(() => {
    if (!isManualDR) return;
    const prune = (sel: Set<string | number>, valid: Set<string>) => {
      const kept = new Set(Array.from(sel).filter(id => valid.has(String(id))));
      return kept.size === sel.size ? sel : kept;
    };
    setDrSelJobIds(s => prune(s, new Set(drPendingJobs.map(j => j.jobID))));
    setDrSelBatchIds(s => prune(s, new Set(drPendingBatches.map(b => b.qcBatchID))));
  }, [isManualDR, drPendingJobs, drPendingBatches]);

  const reloadDrFacet = useCallback(() => {
    if (isManualAppr) void loadManualFacet(true, '/api/v1/analysis/result-approval/facet-rows', setRaFacetJobs, setRaFacetEdges);
    if (isManualVal)  void loadManualFacet(true, '/api/v1/analysis/result-validation/facet-rows', setRvFacetJobs, setRvFacetEdges);
  }, [isManualAppr, isManualVal, loadManualFacet]);

  const drActiveSel = drSelTab === 'Sample' ? drSelJobIds : drSelBatchIds;
  const drCanLoad = isManualDR && !loading && !drLoaded && drActiveSel.size > 0;

  // Load the chosen Job IDs (Sample tab) or QC Batch IDs (QC Batch tab).
  const drLoadSelection = useCallback((tab: ReeSelTab, ids: string[]) => {
    if (ids.length === 0) {
      notify.warning(tab === 'Sample' ? 'Select one or more Job IDs to load.' : 'Select one or more QC Batch IDs to load.');
      return;
    }
    const listed = new Set(drPendingJobs.map(j => j.jobID));
    const jobIds = tab === 'Sample'
      ? ids
      : Array.from(new Set(drPendingBatches.filter(b => ids.includes(b.qcBatchID)).flatMap(b => b.jobIDs)));
    if (!jobIds.some(j => listed.has(j))) {
      notify.warning(`The selected ${tab === 'Sample' ? 'job is' : 'QC batch is'} no longer ${drStageStatus.toLowerCase()} — Refresh the list.`);
      return;
    }
    setDrBatchScope(tab === 'QC Batch' ? ids : []);
    setDrApplied({ ...EMPTY_RAPPR_FILTERS, id: jobIds.filter(j => listed.has(j)) });
    setDrLoaded(true);
    setDrQpCollapsed(true);                                   // REE parity: detail gets the room
    window.dispatchEvent(new CustomEvent('ai:nav-collapse'));
  }, [drPendingJobs, drPendingBatches, drStageStatus, notify, setDrApplied, setDrQpCollapsed]);
  const drLoadAction = useCallback(
    () => drLoadSelection(drSelTab, Array.from(drActiveSel, String)),
    [drLoadSelection, drSelTab, drActiveSel]);
  const drLoadRow = useCallback((tab: ReeSelTab, id: string) => {
    if (tab === 'Sample') setDrSelJobIds(new Set([id])); else setDrSelBatchIds(new Set([id]));
    drLoadSelection(tab, [id]);
  }, [drLoadSelection]);

  // Return to the form-open selection stage (Sample tab), pending lists re-pulled.
  const drResetToSelection = useCallback(() => {
    setDrLoaded(false); setDrBatchScope([]);
    setDrApplied(EMPTY_RAPPR_FILTERS);
    setDrSelJobIds(new Set()); setDrSelBatchIds(new Set());
    setDrSelTab('Sample');
    setDrQpCollapsed(false);
    void load();
    reloadDrFacet();
  }, [setDrApplied, setDrQpCollapsed, load, reloadDrFacet]);
  // Back (detail stage only): never validates / approves / saves — the shared
  // unsaved-changes confirm runs first (standing rule 15); keeps the Query filters.
  const drBack = useCallback(() => {
    void guardedExit(() => { if (!returnToOrigin()) drResetToSelection(); });
  }, [drResetToSelection]);
  // Clear keeps its own meaning — reset every Query criterion — and, like REE's
  // Clear, lands on the selection stage (confirming first if a grid is loaded).
  const drClear = useCallback(() => {
    const run = () => {
      if (isManualAppr) setRaDraft(EMPTY_RAPPR_FILTERS); else setRvDraft(EMPTY_RAPPR_FILTERS);
      drResetToSelection();
    };
    if (drLoaded) void guardedExit(run); else run();
  }, [isManualAppr, drLoaded, drResetToSelection]);
  // Refresh: the pending lists (+ counts) always; the loaded grid too when in detail.
  const drRefresh = useCallback(() => {
    void load();
    reloadDrFacet();
    if (drLoaded) { if (isManualAppr) setRaRefreshKey(k => k + 1); else setRvRefreshKey(k => k + 1); }
  }, [load, reloadDrFacet, drLoaded, isManualAppr]);
  // A Validate / Approve / Rollback changed the pending set → re-pull list + counts.
  const drOnDone = useCallback(() => { void load(); reloadDrFacet(); }, [load, reloadDrFacet]);
  // Once the re-pulled list no longer holds ANY loaded job (all of it validated /
  // approved / rolled back), there is nothing left to review here — return to the
  // selection stage (fresh counts) instead of leaving an empty grid behind. Nothing
  // is unsaved at that point (the action just committed), so no confirm is needed.
  const drLoadedJobs = isManualAppr ? raJobs : rvJobs;
  useEffect(() => {
    if (isManualDR && drLoaded && !loading && drLoadedJobs.length === 0) drResetToSelection();
  }, [isManualDR, drLoaded, loading, drLoadedJobs.length, drResetToSelection]);

  // "Export to" in the selection stage = the active pending tab's visible rows.
  const drSelExport = useMemo(() => drSelTab === 'Sample'
    ? { filename: `${isManualAppr ? 'result-approval' : 'result-validation'}-pending-jobs`, ...gridExportMatrix(drVisJobs, pendingJobCols()) }
    : { filename: `${isManualAppr ? 'result-approval' : 'result-validation'}-pending-qc-batches`, ...gridExportMatrix(drVisBatches, pendingBatchCols(false)) },
  [drSelTab, drVisJobs, drVisBatches, isManualAppr]);

  // Back = Esc while a grid is loaded — its OWN registration (not in the array
  // below) so it only re-registers when drLoaded flips; the embedded grid's
  // "Exit full screen" Esc, registered later when maximized, therefore still wins.
  useHotkeys(isManualDR && drLoaded
    ? { combo: 'esc', label: 'Back', group: title, order: 0, allowInInput: false, run: () => drBack() }
    : null);

  // ── Phase 2.3 · Module action keys for the Analysis Data-Review workflow
  //    (CLAUDE.md standing rule 14, §18.6, ADR-0013).  Mirror the control-bar
  //    forward actions — Result Validation → Validate (Alt+V), Result Approval →
  //    Approve (Alt+A) — calling the SAME embedded-grid ref the button does, with
  //    the SAME disabled gate, grouped under the form title.  Only the active kind
  //    registers (kinds are mutually exclusive), so the keys never collide.
  const dataReviewKeys: HotkeyDef[] = [];
  if (isManualVal) {
    dataReviewKeys.push({ combo: 'alt+v', label: 'Validate', group: title, order: 1,
      run: () => rvEmbedActionsRef.current?.approve(),
      disabled: !rvValidationBatch || rvReview.completed || !perm.write || rvReview.selectedCount === 0 });
    dataReviewKeys.push({ combo: 'alt+b', label: 'Rollback', group: title, order: 2,
      run: () => rvEmbedActionsRef.current?.rollback(),
      disabled: !rvValidationBatch || rvReview.completed || !perm.write || rvReview.selectedCount === 0 });
    dataReviewKeys.push({ combo: 'alt+l', label: 'Load', group: title, order: 3,
      run: () => drLoadAction(), disabled: !drCanLoad });
    dataReviewKeys.push({ combo: 'alt+q', label: 'Query', group: title, order: 4,
      run: () => setRvQpCollapsed(c => !c) });
    dataReviewKeys.push({ combo: 'alt+shift+c', label: 'Clear', group: title, order: 5,
      run: () => drClear(), disabled: loading });
  }
  if (isManualAppr) {
    dataReviewKeys.push({ combo: 'alt+a', label: 'Approve', group: title, order: 1,
      run: () => raEmbedActionsRef.current?.approve(),
      disabled: !raApprovalBatch || raReview.completed || !perm.write || raReview.selectedCount === 0 });
    dataReviewKeys.push({ combo: 'alt+b', label: 'Rollback', group: title, order: 2,
      run: () => raEmbedActionsRef.current?.rollback(),
      disabled: !raApprovalBatch || raReview.completed || !perm.write || raReview.selectedCount === 0 });
    dataReviewKeys.push({ combo: 'alt+l', label: 'Load', group: title, order: 3,
      run: () => drLoadAction(), disabled: !drCanLoad });
    dataReviewKeys.push({ combo: 'alt+q', label: 'Query', group: title, order: 4,
      run: () => setRaQpCollapsed(c => !c) });
    dataReviewKeys.push({ combo: 'alt+shift+c', label: 'Clear', group: title, order: 5,
      run: () => drClear(), disabled: loading });
  }
  if (isTracking) {
    dataReviewKeys.push({ combo: 'alt+l', label: 'Load', group: title, order: 1,
      run: () => { setRtApplied({ ...rtDraft, ...rtActiveDates }); setRtLoaded(true); }, disabled: loading });
    dataReviewKeys.push({ combo: 'alt+r', label: 'Refresh', group: title, order: 2,
      run: () => { void load(); rtEmbedApi.current?.refresh(); }, disabled: loading });
    if (rtEmbed.canRollback && !rtEmbed.maximized) {
      dataReviewKeys.push({ combo: 'alt+b', label: 'Rollback', group: title, order: 3,
        run: () => rtEmbedApi.current?.rollback(), disabled: rtEmbed.selectedCount === 0 });
    }
    dataReviewKeys.push({ combo: 'alt+q', label: 'Query', group: title, order: 4,
      run: () => setRtQpCollapsed(c => !c) });
    dataReviewKeys.push({ combo: 'alt+shift+c', label: 'Clear', group: title, order: 5,
      run: () => { setRtDraft(EMPTY_REE_FILTERS); setRtApplied(EMPTY_REE_FILTERS); setRtLoaded(false); },
      disabled: loading || rtQpCollapsed });
  }
  if (isSdms && !isApproval) {
    // SDMS Result Validation — the per-row Edit pencil (title "Edit this batch") opens
    // the batch package (setPkgTarget); Alt+E mirrors it for the single checked row.
    // SDMS Result Approval (isApproval) is read-only — no Edit icon, no Alt+E.
    // (user report 2026-09-22: "edit option is there but Alt+E not assigned".)
    dataReviewKeys.push({ combo: 'alt+e', label: 'Edit', group: title, order: 1,
      run: () => { const r = (rows as AnalyticalBatchRow[]).find(x => selectedIds.has(x.id)); if (r) setPkgTarget(r); },
      disabled: selectedIds.size !== 1 || !perm.write });
  }
  useHotkeys(dataReviewKeys);

  // All selected Prep Queue rows (multi-test). Selection ids are testMethodId.
  const selectedPrepRows = useMemo(() => {
    if (kind !== 'prep-queue') return [] as AnalysisQueueRow[];
    return (rows as AnalysisQueueRow[]).filter(r => analysisSel.has(queueRowKey(r)));
  }, [kind, analysisSel, rows]);

  const selectedAnalysisRow = useMemo(() => {
    if (kind !== 'analysis-queue' && kind !== 'prep-queue') return null;
    if (analysisSel.size < 1) return null;                    // >=1 (prep multi) → first row
    return (rows as AnalysisQueueRow[]).find(r => analysisSel.has(queueRowKey(r))) ?? null;
  }, [kind, analysisSel, rows]);

  // Sample Prep write gate. The per-form MAC model now carries create/write/delete
  // for 'preplqueue'/'prepbatches' (useNavActions above), so this is no longer a
  // fail-open stub: a read-only role cannot create, and an opened batch is view-only.
  // (owner report 2026-10-24.) perm falls open for admins / no-MAC / unmapped views.
  const canWritePrep = perm.create || perm.write;

  // New → open the QC Batch form in CREATE mode, seeded from the selected test
  // method. The form's own Job ID → Load → Sort → OK flow then builds the batch.
  const onNewFromQueue = () => {
    if (!selectedAnalysisRow) return;
    // Sample Prep Queue → open the Prep Batch detail (its own Job → Load → Sort →
    // OK flow); Analysis Queue → open the QC Batch detail.
    if (kind === 'prep-queue') {
      // Several Test Codes may share one Prep Batch ID only when they have the SAME Prep
      // Method (e.g. SPE — Solid Phase Extraction) — user requirement 2026-09-28. Compared on
      // the queue's PrepMethod column (the prep method's number, as the user sees it), trimmed
      // and case-insensitive. OG's extra "Only tier 1 will allow multiple tests." rule
      // (SamplePreparationController.NewObjectAction_Executing :1495) was REMOVED on owner
      // request 2026-09-29 — any tier may be batched together. (A multi-test prep batch can
      // still never be imported into a QC batch — owner decision 2026-09-28, kept.)
      const sel = selectedPrepRows.length > 0 ? selectedPrepRows : [selectedAnalysisRow];
      if (sel.length > 1) {
        const prepMethods = new Set(sel.map(r => (r.prepMethod ?? '').trim().toLowerCase()));
        if (prepMethods.size > 1) {
          notify.warning('Only tests with the same Prep Method can share a Prep Batch.');
          return;
        }
      }
      const first = sel[0];
      if (!first) return;
      const testMethodIds = [...new Set(sel.map(r => r.testMethodId))];
      const tierNum = Number((first.tierType ?? '').replace(/\D/g, '')) || 2;
      // OG: no SamplePreparation Write → the detail opens READ-ONLY (View mode).
      if (!canWritePrep) notify.warning('Read-only: you do not have write permission for Sample Preparation.');
      setPrepBatchSeed({
        testMethodId:  first.testMethodId,
        testMethodIds,
        tier:          tierNum,
        sort:          first.prepSort ?? 1,   // real PrepMethod Sort (was hardcoded 1)
        matrix:        first.matrix ?? undefined,
        test:          testMethodIds.length > 1
                         ? [...new Set(sel.map(r => r.test).filter(Boolean))].join('; ')
                         : (first.test ?? undefined),
        method:        first.method ?? undefined,
        viewOnly:      !canWritePrep,
        // The ticked rows' components — Job ID picker / Load offer only those (2026-09-30).
        componentIds:  [...new Set(sel.map(r => r.componentId ?? NO_COMPONENT))],
      });
      return;
    }
    setQcBatchSeed({
      testMethodId: selectedAnalysisRow.testMethodId,
      test:         selectedAnalysisRow.test,
      method:       selectedAnalysisRow.method,
      matrix:       selectedAnalysisRow.matrix,
      templateID:   selectedAnalysisRow.templateID,
    });
  };

  // Sample Details modal's OK-with-a-selection (legacy OG parity —
  // QCBatchController.cs Dc_Accepting ~line 2788 / Qcbatchinfo.lstSamples):
  // derive the DISTINCT job(s) + exact SampleLogIn oids from the checked rows
  // and seed QcBatchDetailPage with them directly, so Load narrows to exactly
  // those samples instead of pulling every pending sample for the test.
  const handleLoadSelectedSamples = (selectedRows: PrepDetailRow[]) => {
    if (!selectedQueue) return;
    const jobOids = [...new Set(selectedRows.map(r => r.jobOid).filter((v): v is string => !!v))];
    const sampleLogInOids = [...new Set(selectedRows.map(r => r.sampleLogInOid).filter((v): v is string => !!v))];
    setQcBatchSeed({
      testMethodId: selectedQueue.testMethodId,
      test:         selectedQueue.test,
      method:       selectedQueue.method,
      matrix:       selectedQueue.matrix,
      templateID:   selectedQueue.templateID,
      preSelectedJobOids:         jobOids,
      preSelectedSampleLogInOids: sampleLogInOids,
    });
  };

  // Prep Queue "View details" popup → OK: open the Prep Batch form seeded with the
  // ticked samples (their Job Oids pre-fill jobOids; their SampleLogIn Oids narrow
  // Load to exactly those). Mirrors the analysis-queue → QC flow above.
  const handleLoadSelectedPrepSamples = (selectedRows: PrepDetailRow[]) => {
    if (!selectedQueue) return;
    const jobOids = [...new Set(selectedRows.map(r => r.jobOid).filter((v): v is string => !!v))];
    const sampleLogInOids = [...new Set(selectedRows.map(r => r.sampleLogInOid).filter((v): v is string => !!v))];
    const tierNum = Number((selectedQueue.tierType ?? '').replace(/\D/g, '')) || 2;
    if (!canWritePrep) notify.warning('Read-only: you do not have write permission for Sample Preparation.');
    setSelectedQueue(null);
    setPrepBatchSeed({
      testMethodId:  selectedQueue.testMethodId,
      testMethodIds: [selectedQueue.testMethodId],
      tier:          tierNum,
      sort:          selectedQueue.prepSort ?? 1,
      matrix:        selectedQueue.matrix ?? undefined,
      test:          selectedQueue.test ?? undefined,
      method:        selectedQueue.method ?? undefined,
      viewOnly:      !canWritePrep,
      preSelectedJobOids:         jobOids,
      preSelectedSampleLogInOids: sampleLogInOids,
      componentIds:               [selectedQueue.componentId ?? NO_COMPONENT],
    });
  };

  const selectedQcRow = useMemo(() => {
    if (kind !== 'qc-batches' || qcSel.size !== 1) return null;
    const [id] = qcSel;
    return (rows as AnalyticalBatchRow[]).find(r => r.id === id) ?? null;
  }, [kind, qcSel, rows]);

  // Sample Prep Batches list — single-select (reuses qcSel; the kinds are mutually
  // exclusive) driving Edit (open the batch) + Delete (OG SamplePrepBatch_ListView:
  // Delete blocked once ResultCompleted=3). New is a QUEUE action, not here.
  const selectedPrepBatchRow = useMemo(() => {
    if (kind !== 'prep-batches' || qcSel.size !== 1) return null;
    const [id] = qcSel;
    return (rows as PrepBatchRow[]).find(r => r.id === id) ?? null;
  }, [kind, qcSel, rows]);
  const prepDeletable = (r: PrepBatchRow | null) => !!r && r.statusCode !== 3;
  const onPrepBatchEdit = () => {
    const row = selectedPrepBatchRow;
    if (!row) return;
    setPrepBatchSeed({
      testMethodId: '', tier: row.tier ?? 2, sort: row.sort ?? 1,
      matrix: row.matrix ?? undefined, test: row.test ?? undefined, method: row.method ?? undefined,
      prepOid: row.id,
    });
  };
  // Row click → open the existing prep batch in VIEW mode (QC-form parity,
  // onQcBatchRowClick). Same seed as Edit but viewOnly:true so the detail opens
  // read-only regardless of status; Edit remains the editable path.
  const onPrepBatchRowClick = (r: unknown) => openPrepBatchDetail(r as PrepBatchRow);
  const onPrepBatchDelete = () => {
    const row = selectedPrepBatchRow;
    if (!prepDeletable(row) || !row) return;
    void runDelete({
      formName: 'Sample Prep Batches',
      message: `Delete Prep Batch ${row.batchId ?? ''}? Its samples return to the prep queue and this cannot be undone.`,
      delete: () => prepBatchApi.deleteBatch(String(row.id)).then(() => undefined),
      onDeleted: () => { setQcSel(new Set()); void load(); },
      onError: (e) => notify.error(qcApiErr(e, 'Deleting the prep batch failed.')),
    });
  };

  // Status gates mirror the legacy list-view action criteria: Edit/Delete are
  // enabled for NonStatus In(0,1) (persistent Status 0 = a fresh AI batch, 1 =
  // Pending Submission), Rollback for NOT In(0,1) (Pending Validation onward).
  const qcEditable     = (r: AnalyticalBatchRow | null) => !!r && (r.statusCode === 0 || r.statusCode === 1);
  const qcRollbackable = (r: AnalyticalBatchRow | null) => !!r && !(r.statusCode === 0 || r.statusCode === 1);

  // Row click → open the existing QC batch in VIEW mode (legacy isview=true).
  // testMethodId/test/method/matrix/templateID aren't fully known yet — the
  // AnalyticalBatchRow list DTO carries only the display Test/Method/Matrix NAME
  // strings; QcBatchDetailPage's detail fetch (keyed on existingAbOid) resolves the
  // real values and overwrites these once it loads.
  const onQcBatchRowClick = (r: unknown) => openQcBatchDetail(r as AnalyticalBatchRow);

  // Open the deep-linked QC batch's detail form once the list rows arrive (the
  // QCBatchID was parked in pendingQcBatchId on mount). The full dataset is in
  // `rows`, so the batch is found regardless of which grid page it would sit on.
  useEffect(() => {
    if (qcDeepLinkOpenedRef.current || !pendingQcBatchId || kind !== 'qc-batches') return;
    const row = (rows as AnalyticalBatchRow[]).find(r => r.batchId === pendingQcBatchId);
    if (row) { qcDeepLinkOpenedRef.current = true; openQcBatchDetail(row); }
  }, [pendingQcBatchId, rows, kind, openQcBatchDetail]);

  // A batch's result entry, the way the list's SDMS and PLM buttons open it. Asks the PLM
  // list itself because plmBatchOids may still be loading. If that fails, the batch record
  // opens instead: it has both buttons.
  const openResultEntry = async (row: AnalyticalBatchRow) => {
    const plm = await fetchPlmBatches().catch(() => null);
    if (!plm) { onQcBatchRowClick(row); return; }
    const isPlm = plm.some(b => b.batchOid.toLowerCase() === row.id.toLowerCase());
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
      ...(isPlm ? { autoOpenPlm: true } : { autoOpenSdms: true }),
    });
  };

  // Open what the job link asked for, once the list has loaded: the QC batch, its result
  // entry, or the job's results. Only ever acts once.
  useEffect(() => {
    if (loading) { jobLinkSawLoad.current = true; return; }
    if (!jobLink.current || !jobLinkSawLoad.current) return;
    const { jobId, batchOid, resultEntry } = jobLink.current;
    jobLink.current = null;
    const same = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();
    if (kind === 'qc-batches') {
      const batches = rows as AnalyticalBatchRow[];
      const row = batches.find(r => same(r.id, batchOid)) ?? batches.find(r => r.jobIds?.includes(jobId));
      if (!row) notify.warning(`No QC batch for job ${jobId} is listed here.`);
      else if (resultEntry) void openResultEntry(row);
      else onQcBatchRowClick(row);
    } else if (isManualDR) {
      if ((rows as ResultValidationListRow[]).some(r => r.jobID === jobId)) drLoadRow('Sample', jobId);
      else notify.warning(`Job ${jobId} isn't listed here. Its results may be outside your department.`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- acts only when a load finishes
  }, [loading, rows]);

  // New → jump to the Analysis Queue (where a QC batch is started by ticking a test).
  const onQcNew = () => onOpenNav?.('analysisq');

  // Edit → open the selected batch in EDIT mode (legacy EditAction — status-gated).
  const onQcEdit = () => {
    const row = selectedQcRow;
    if (!qcEditable(row) || !row) return;
    setQcBatchSeed({
      testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
      templateID: row.templateID, existingAbOid: row.id, viewOnly: false,
    });
  };

  // Delete → shared delete flow → the cascade endpoint (samples return to the
  // queue); status-gated + server guards. Refresh the list on success.
  const onQcDelete = () => {
    const row = selectedQcRow;
    if (!qcEditable(row) || !row) return;
    void runDelete({
      formName: 'QC Batches',
      message: `Delete QC Batch ${row.batchId ?? ''}? Its samples return to the analysis queue and this cannot be undone.`,
      delete: () => qcBatchApi.deleteBatch(row.id).then(() => undefined),
      onDeleted: () => { setQcSel(new Set()); void load(); },
      onError: (e) => notify.error(qcApiErr(e, 'Deleting the QC batch failed.')),
    });
  };

  // Rollback → reason prompt (legacy "Rollbackempty" block) → rollback endpoint;
  // enabled only past Pending Submission (NonStatus Not In(0,1)). Refresh on success.
  const onQcRollback = async () => {
    const row = selectedQcRow;
    if (!qcRollbackable(row) || !row) return;
    const reason = await prompt({
      title: 'QC Batch Rollback', message: 'Enter a reason for the rollback.',
      placeholder: 'Rollback reason…', required: true, confirmLabel: 'Rollback',
    });
    if (reason === null) return;
    try {
      await qcBatchApi.rollback(row.id, reason);
      void showDialog({ type: 'success', title: 'Rollback', message: `QC Batch ${row.batchId ?? ''} rolled back.` });
      setQcSel(new Set());
      void load();
    } catch (e) { notify.error(qcApiErr(e, 'Rollback failed.')); }
  };

  // "Result Validation" (manual) — Validate / Rollback live ONLY in the DETAIL
  // view (ResultValidationDetail), matching ALPACA: the LIST just browses and
  // OPENS a batch (row-click → detail); the batch is validated / rolled back from
  // its detail's toolbar.  So the list carries no Validate/Rollback and no
  // multi-select checkboxes.

  // ── SDMS workflow handlers (Validate / Rollback / Approve applied optimistically) ───
  // (Merged 2026-07-07) After the POST succeeds, mutate the matching rows in place
  // so the grid reflects the new state immediately.  A Refresh re-pulls
  // authoritative server state once the Phase 2 SP writes are in place.
  const applyValidate = (batchIds: string[], whoCode: string, whenIso: string) => {
    const idSet = new Set(batchIds);
    setRows(prev => prev.map(r => {
      const b = r as AnalyticalBatchRow;
      if (!idSet.has(b.id)) return r;
      return { ...b,
        statusText:       'SDMS Validated',
        statusId:         'DV-1-2',
        validatedByList:  whoCode,
        validatedDateMax: whenIso,
      };
    }));
    setSelectedIds(new Set());
    // Success confirmation is shown by ValidateModal's phase-2 popup (centred
    // dialog + green tick + OK button — ALPACA-parity).  No toast here to
    // avoid the "modal + toast for the same event" double-notify.
  };
  const applyRollback = (batchIds: string[]) => {
    const idSet = new Set(batchIds);
    // Full ALPACA rollback sends the batch all the way back to result entry
    // (Status 1 / QB-1-3), so it LEAVES the SDMS Result Validation list. Drop
    // the rolled-back rows optimistically; a Refresh re-pulls authoritative state.
    setRows(prev => prev.filter(r => !idSet.has((r as AnalyticalBatchRow).id)));
    setSelectedIds(new Set());
  };
  const applyApprove = (batchIds: string[], whoCode: string, whenIso: string) => {
    const idSet = new Set(batchIds);
    // An approved batch STAYS in the SDMS Result Approval grid, shown as
    // "SDMS Approved" with its verify stamp (ALPACA parity — the Level-3 grid
    // lists both validated and approved).  Update in place; a Refresh re-pulls
    // authoritative state (with the approver resolved to an E-code).
    setRows(prev => prev.map(r => {
      const b = r as AnalyticalBatchRow;
      if (!idSet.has(b.id)) return r;
      return { ...b,
        statusText:     'SDMS Approved',
        statusId:       'DV-6-4',
        verifiedByName: whoCode,
        verifiedDate:   whenIso,
      };
    }));
    setSelectedIds(new Set());
  };

  // Selected rows (typed) + Validate/Rollback enablement (Pending vs Validated).
  const selectedBatches = useMemo<AnalyticalBatchRow[]>(() => {
    if (!isSdms) return [];
    return (rows as AnalyticalBatchRow[]).filter(r => selectedIds.has(r.id));
  }, [rows, selectedIds, isSdms]);
  // Rollback-able = ANY selected SDMS batch on this list — Pending SDMS
  // Validation (Status 2) AND SDMS Validated / Approved (Status 5). LIMS parity
  // (DataReviewBatchRollbackAction_Execute): rollback rolls BOTH all the way back
  // to Result Entry (QB-1-3); it imposes no "must be validated first" gate — only
  // a Reported sample blocks it, which the server precheck enforces. (Previously
  // Pending SDMS Validation was excluded, greying out Rollback for those rows.)
  const rollbackTargets = selectedBatches;

  // Top-bar stepper progress (reference: Field-Data-Entry-Prototype.html — each
  // step ticks green as it is completed).  On the LIST view the ONLY step the
  // analyst performs is "Select a QB"; the remaining steps (Batch Results …
  // Validate/Approve) happen after Open Package, inside the detail overlay (which
  // drives its own stepper).  So the list bar shows exactly one transition:
  // nothing selected → step 1 active; ≥1 selected → step 1 done + step 2 active.
  const sdmsStepStates = useMemo<Array<'done' | 'active' | 'todo'>>(() => {
    // Package detail open: the QB is selected + loaded, and the analyst is now
    // reviewing (Batch Results / Check Points / Review Log) with Validate/Approve
    // pending — so the pill advances past Load. (User request 2026-07-07.)
    if (pkgTarget) return ['done', 'done', 'active', 'active', 'active', 'todo'];
    return selectedBatches.length === 0
      ? ['active', 'todo', 'todo', 'todo', 'todo', 'todo']
      : ['done', 'active', 'todo', 'todo', 'todo', 'todo'];
  }, [selectedBatches.length, pkgTarget]);

  // SDMS title-row actions (prototype (7)): Rollback + Load moved OUT of the
  // toolbar into the consolidated title row.  Load opens the ticked batch's
  // package (same as the per-row "Open Package" link).
  const handleSdmsRollback = async () => {
    // ALPACA parity: the reported-sample guard fires BEFORE the reason popup.
    // Pre-check the selected batches; if any is blocked, show the red "Rollback
    // failed…" notice and DON'T ask for a reason.
    const ids = rollbackTargets.map(t => t.id);
    if (ids.length === 0) return;
    try {
      const res = await api.post('/api/v1/analysis/result-validation/rollback/precheck', { batchIds: ids });
      const blocked = (res.data as { blocked?: Record<string, string> }).blocked ?? {};
      const blockedCount = Object.keys(blocked).length;
      if (blockedCount > 0) {
        const firstReason = Object.values(blocked)[0] ?? 'Rollback failed.';
        // §13: a full refusal is an error (red modal); a partial skip is a
        // warning (amber modal). Both via the shared global service.
        if (blockedCount === ids.length) notify.error(firstReason);
        else notify.warning(`${blockedCount} batch(es) skipped: ${firstReason}`);
        return;   // guard failed → no reason dialog (LIMS behaviour)
      }
      setRollbackOpen(true);   // all eligible → ask for the reason
    } catch {
      // Pre-check failed (network/API) — fall back to the modal; the server
      // re-checks the same gate on submit anyway.
      setRollbackOpen(true);
    }
  };
  const handleSdmsLoad = () => {
    if (selectedBatches.length > 0) setPkgTarget(selectedBatches[0] as AnalyticalBatchRow);
  };

  // (Merged 2026-07-07) Full-page swap: clicking New (→ Analysis Queue is handled
  // by onOpenNav) or a QC Batch row Edit/row-click opens the inline detail form.
  // Sample Prep Queue → New (or a Prep Batches row) swaps the list for the inline
  // Prep Batch detail form (its own Job → Load → Sort → OK flow).
  if (prepBatchSeed) {
    return (
      <PrepBatchDetailPage
        seed={prepBatchSeed}
        // Clear the queue row selection on return so the row isn't left ticked
        // after New (Prep/Analysis Queue selection lives in analysisSel). (fix 2026-08-14)
        onBack={() => { setPrepBatchSeed(null); setAnalysisSel(new Set()); void load(); }}
        onExportToQc={(ex) => {
          // OG btnExportToQCBatch_Execute: swap the prep detail for the QC Batch
          // form, seeded with this prep batch so it auto-imports the samples.
          setPrepBatchSeed(null);
          setQcBatchSeed({
            testMethodId: ex.testMethodId,
            test:         ex.test,
            method:       ex.method,
            matrix:       ex.matrix,
            templateID:   null,
            preSelectedPrepBatchId: ex.prepBatchId,
          });
        }}
      />
    );
  }

  if (qcBatchSeed) {
    return (
      <QcBatchDetailPage
        seed={{
          testMethodId: qcBatchSeed.testMethodId,
          test:         qcBatchSeed.test,
          method:       qcBatchSeed.method,
          matrix:       qcBatchSeed.matrix,
          templateID:   qcBatchSeed.templateID,
          // Prep->QC export (OG btnExportToQCBatch_Execute): forward the prep batch id
          // so the QC form auto-imports the prepared samples. It was set on qcBatchSeed
          // in the export handler but dropped here, so Export To QC opened an empty batch.
          preSelectedPrepBatchId:     qcBatchSeed.preSelectedPrepBatchId,
          preSelectedJobOids:         qcBatchSeed.preSelectedJobOids,
          preSelectedSampleLogInOids: qcBatchSeed.preSelectedSampleLogInOids,
          existingAbOid: qcBatchSeed.existingAbOid,
          viewOnly:      qcBatchSeed.viewOnly,
          autoOpenSdms:  qcBatchSeed.autoOpenSdms,
          // The PLM twin.  This block re-maps the seed FIELD BY FIELD, so a new
          // flag that is not listed here is silently dropped — exactly what
          // happened to preSelectedPrepBatchId above, and what happened to
          // autoOpenPlm: the list's PLM button set it, this mapping discarded it,
          // and the QC Batch detail opened instead of PLM (user report 2026-09-23).
          autoOpenPlm:   qcBatchSeed.autoOpenPlm,
        }}
        // Clear the Analysis Queue row selection on return so the row isn't left
        // ticked after New (analysis-queue selection lives in analysisSel). (fix 2026-08-14)
        onBack={() => { if (returnToOrigin()) return; setQcBatchSeed(null); setAnalysisSel(new Set()); void load(); }}
      />
    );
  }

  // Result Validation → detail drill-in (full-page swap, like the QC Batch detail).
  if (rvDetail) {
    return (
      <ResultValidationDetail
        batch={rvDetail}
        canWrite={perm.write}
        onClose={() => { if (!returnToOrigin()) setRvDetail(null); }}
        onDone={() => { setRvDetail(null); void load(); }}
      />
    );
  }

  // Result Tracking → read-only "Retrieve" drill-in (full-page swap).  Purely a
  // viewer — closing returns to the list (no reload needed; nothing changed).
  if (rtDetail) {
    return <ResultTrackingDetail batch={rtDetail} onClose={() => setRtDetail(null)} />;
  }

  return (
    <div className={`bl-page${isSdms ? ' sdms-compact' : ''}`}>
      {/* SDMS Result Validation / Approval list QCBatchID link → the batch's QC
          Batch detail as a MODAL POPUP over this list, keeping it mounted behind
          (user decision 2026-10-30). The dedicated QC Batches list still opens the
          full-page detail (openQcBatchDetail). Portalled to <body>. */}
      {qcBatchModalRow && (
        <QcBatchModal
          batchOid={qcBatchModalRow.id}
          batchId={qcBatchModalRow.batchId}
          onClose={() => setQcBatchModalRow(null)}
        />
      )}
      {/* Result Validation workflow stepper (BO UI Standard §5) — docked in the
          top bar. On the LIST, step 1 (Select Job) is active; the detail view
          drives steps 2-4 (Sample Results · QC Sample Results · Validate). */}
      {isManualDR && (
        <TopbarStepper steps={[
          { id: 'select',   label: 'Select Job/Batch',  state: drLoaded ? 'done' : 'active' },
          { id: 'sample',   label: 'Sample Results',    state: drLoaded ? 'active' : 'todo' },
          { id: 'qc',       label: 'QC Sample Results',  state: 'todo' },
          { id: 'action',   label: isManualAppr ? 'Approve' : 'Validate', state: 'todo' },
        ] as IslandStep[]} />
      )}
      {/* Result Tracking Dynamic-Island stepper (BO UI Standard §5, prototype
          Result-Entry): LIST = step 1 (Select Batch) active; the detail view marks
          both steps done. */}
      {isTracking && (
        <TopbarStepper steps={[
          { id: 'select', label: 'Select Batch', state: 'active' },
          { id: 'load',   label: 'Load Samples', state: 'todo' },
        ] as IslandStep[]} />
      )}
      {/* Rollback outcomes surface via the shared §13 notifications (useNotify):
          success → transient top banner, refusal → centered modal. The old
          per-form red/green banners were removed (standing rule 7). */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>{group}</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{title}</span>
          </div>
          {/* Non-SDMS keeps the ONE shared <FormTitle> (nav icon/colour via
              navMeta(KIND_NAV_ID[kind]) + the TOP load-time chip, §18.6).
              SDMS renders its own consolidated title row (prototype (7) full
              match): check + title + load-time chip + Audit Trail on the left;
              Rollback + Load on the right. (Merged 2026-07-07.) */}
          <div className="dp-title-row">
            {isSdms ? (
              <>
                {/* v3 (user request 2026-09-19): the big title row is dropped — the
                    page title already shows in the top bar. Use the shared
                    <FormTitle> which PUBLISHES load-time + Audit to FormMetaContext
                    and DEFERS its own render (a toolbar is present), so the
                    "Loaded in X" + Audit chips appear at the END of the action
                    toolbar (after Export to), exactly like every other form.
                    Rollback + Load stay top-right. */}
                <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading}
                  auditFormName={isApproval ? 'Result Approval' : 'SDMS Result Validation'} />
                <div className="dp-title-actions">
                  {perm.write && (
                    <button className="ca-btn" onClick={handleSdmsRollback}
                      disabled={rollbackTargets.length === 0}
                      title="Rollback the selected batch to Pending Submission (Result Entry)">
                      <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-blue)' }} /> Rollback
                    </button>
                  )}
                  <button className="ca-btn ca-btn-solid dp-load" onClick={handleSdmsLoad}
                    disabled={selectedBatches.length === 0}
                    title="Open the ticked QC batch's package for review">
                    <i className="ti ti-download" /> Load
                  </button>
                </div>
              </>
            ) : isManualDR ? (
              <>
                {/* Audit — the shared config-gated <AuditTrailButton> chip next to
                    the load-time chip (standing rule 11, prototype parity). */}
                <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading}
                  auditFormName={isManualAppr ? 'Result Approval' : 'Result Validation'} />
                {/* Open — TOP-RIGHT primary action (Custom Reporting parity): DISABLED
                    until exactly one QC batch / job is ticked, then opens it in the
                    Result Validation / Approval detail. On Result Approval this reads
                    "Open" (not "Load") so it never collides with the query panel's
                    "Load" apply button in the control bar below (task RTCFR); Result
                    Validation keeps "Load" (it has no query-panel Load). */}
                {/* Both Result Validation AND Result Approval now HIDE the title-row
                    Load button (task RTCFR, 2026-09-05): the whole review happens on
                    page one via the embedded Sample/QC grid + the control-bar
                    Validate/Approve + Rollback. The second-page detail is no longer
                    opened from the title. (RA hid it 2026-09-04; RV matches it now.) */}
              </>
            ) : isTracking ? (
              // Audit — shared config-gated <AuditTrailButton> chip next to the
              // load-time chip (standing rule 11, prototype parity). Surfaces the
              // Rollback audit rows (FormName "Result Tracking"). Load moved down
              // into the grid's own top row, parallel to the fuzzy search (user
              // request 2026-09-02) — no title-row action here anymore.
              // Load-time reflects the ACTUAL grid data fetch once Load has run (the
              // embed's window/samples load), not the cached ~0ms status-list load
              // (user report 2026-09-05). Before Load, show the list load.
              <FormTitle pageId={KIND_NAV_ID[kind]} title={title}
                loadMs={rtLoaded ? rtEmbed.loadMs : loadMs}
                loading={loading || (rtLoaded && rtEmbed.loading)}
                auditFormName="Result Tracking" />
            ) : (
              <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading} />
            )}
          </div>
          {/* Result Approval (user request 2026-09-04): the "Review · Approve"
              instruction (published up by the embedded grid) renders HERE, directly
              below the title, as PLAIN subtitle TEXT — NOT the coloured StepHint
              panel (user request 2026-09-04: "remove the panel theme & colour, show
              the text as the form title as before"). Only when a job is resolved;
              otherwise nothing (the empty-grid prompt below explains). Every other
              kind keeps its normal subtitle. */}
          {isManualAppr ? (raApprovalBatch && (
            <div className="dp-subtitle">
              {raReview.reviewLeft > 0 ? (
                <><b>Review · {raReview.actionVerb}.</b> Review each result and <b>tick it</b> — ticking stamps <b>{raReview.stampLabel}</b>. Tick <b>every Sample Result and QC Sample Result</b> ({raReview.reviewLeft} left), then <b>{raReview.actionVerb}</b>. Each grid's header checkbox selects all.</>
              ) : (
                <><b>Review complete.</b> Every Sample &amp; QC result is ticked — click <b>{raReview.actionVerb}</b> to advance the job, or <b>Rollback</b>.</>
              )}
            </div>
          )) : isManualVal ? (rvValidationBatch && (
            /* Result Validation review instruction (task RTCFR, 2026-09-05) — the
               exact parallel of Result Approval above, published up by the embedded
               validation grid. Plain subtitle text (no coloured panel). */
            <div className="dp-subtitle">
              {rvReview.reviewLeft > 0 ? (
                <><b>Review · {rvReview.actionVerb}.</b> Review each result and <b>tick it</b> — ticking stamps <b>{rvReview.stampLabel}</b>. Tick <b>every Sample Result and QC Sample Result</b> ({rvReview.reviewLeft} left), then <b>{rvReview.actionVerb}</b>. Each grid's header checkbox selects all.</>
              ) : (
                <><b>Review complete.</b> Every Sample &amp; QC result is ticked — click <b>{rvReview.actionVerb}</b> to advance the job, or <b>Rollback</b>.</>
              )}
            </div>
          )) : (
            <div className="dp-subtitle">
              {/* SDMS: single clean sentence (prototype (7)) — the item count lives
                  in the grid footer and selection is shown by the stepper + hint, so
                  no "· N items / · N selected" clutter here. */}
              {isSdms
                ? (isApproval
                    ? 'Analytical data packages awaiting level-3 (SDMS) approval.'
                    : 'Analytical data packages awaiting level-2 (SDMS) validation.')
                : isManualDR
                  /* Result Validation subtitle (Data-Review prototype). */
                  ? <>Jobs whose QC batches are all <b>SDMS Approved</b> (DV-1-3), now awaiting job-level <b>Result Validation</b> (DV-10-2). Tick a Job ID / QC batch and click <b>Load</b>.</>
                  : isTracking
                    /* Result Tracking subtitle — prototype (Result-Entry) content, with
                       AI-accurate terms (Load, Pending Entry). */
                    ? <>Every batch that has been through result entry — who entered / submitted / rolled back, and when. <b>Load</b> opens the full record; <b>Rollback</b> (mandatory reason, audited) returns its results to <b>Pending Entry</b>.</>
                    : <>{'Read-only'}{' · '}{loading ? '…' : rows.length} items{subtitle ? ' · ' : ''}{subtitle}</>}
            </div>
          )}
        </div>
        {/* Step-1 instruction band removed (user request 2026-09-19) — the step
            island above already carries the workflow guidance. */}
      </div>

      {/* Dynamic Island stepper — docked into the TOP APP BANNER via a portal
          (Class C/D standard): topmost + always visible.  Renders into AppShell's
          #ai-topbar-island-slot; unmounts (and clears the slot) on navigate-away.
          The step list is NOT duplicated as caption text — this pill is the sole
          stepper. (Merged 2026-07-07.) */}
      {/* Show on the list AND when the package detail is open (user request
          2026-07-07) — the detail now renders inside the content area with the
          top bar kept, so the stepper stays visible instead of vanishing. */}
      {isSdms && bannerSlot && createPortal(
        <div className="dv-flow dv-island-top">
          {['Select a QB', 'Load', 'Batch Results', 'Check Points', 'Review Log', isApproval ? 'Approve' : 'Validate']
            .map((label, i, arr) => (
            <span key={label} className="dv-fseg">
              <span className={`dv-fstep dv-${sdmsStepStates[i]}`}>
                <span className="dv-fn">{sdmsStepStates[i] === 'done' ? '✓' : i + 1}</span>{label}
              </span>
              {i < arr.length - 1 && (
                <span className={`dv-farrow${sdmsStepStates[i] === 'done' ? ' dv-done' : ''}`} />
              )}
            </span>
          ))}
        </div>,
        bannerSlot,
      )}

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">
        {/* Contextual guidance strip (BO UI Standard §5, shared <StepHint>) —
            narrates the ACTIVE step only ("Step N · Name." + one imperative, bold
            controls) and updates live as the stepper advances; ONE per workflow
            screen, under the title + above the toolbar. Here = Step 1 (Select Job);
            the detail view carries Steps 2-3. */}
        {/* Result Validation's guidance StepHint was removed (task RTCFR,
            2026-09-05) — it now matches Result Approval, whose review instruction
            renders as the plain subtitle below the title (see dp-subtitle above).
            Result Approval's segregation-of-duties banner was removed earlier
            (2026-09-04); the server still enforces analyst≠validator/approver. */}
        {/* Result Tracking's guidance strip removed (user request 2026-09-02) —
            the Load action + its tooltip, plus the fuzzy search, now sit
            directly in the grid's own top row, so the separate banner was
            redundant clutter. */}
        {/* Result Tracking's filter section — the SAME Query Panel component/style
            as Result Entry Express (user request 2026-09-02): a compact control
            bar [Date Range | Refresh | Query toggle]  [Export to | Clear | Load]
            plus the structured filter grid. Loading/filtering stays Result
            Tracking's own (client-side over the already-loaded rows; the server
            fetch still uses the same sinceDate/searchBy as before) — only the UI
            changed. All other list kinds keep the existing toolbar + search row. */}
        {isTracking ? (
          <>
            {/* Top control bar (user request 2026-09-03): the SINGLE toolbar for the
                whole form. Left: Date Range · Refresh · Export to · Query. Right:
                Clear · Load · Rollback. Refresh / Export to / Rollback act on the
                embedded sample-detail grid (rtEmbed / rtEmbedApi); the detail's own
                toolbar was removed (Search moved next to its tabs). */}
            <div className="ree-ctrlbar">
              <div className="ree-ctrlbar-left">
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                {/* ONE Refresh — reloads the list (status counts) AND the embedded
                    sample grid. */}
                <button type="button" className="ca-btn"
                  onClick={() => { void load(); rtEmbedApi.current?.refresh(); }} disabled={loading}
                  title={`Reload the results. (${formatCombo('alt+r')})`}>
                  <BusyIcon busy={loading} icon="ti-refresh" />&nbsp;Refresh
                </button>
                {/* Export to — exports the embedded sample grid (its live rows). */}
                <ExportMenu filename={rtEmbed.exportFilename} headers={rtEmbed.exportHeaders} rows={rtEmbed.exportRows} />
                <button type="button"
                  className={`ca-btn ree-query-btn${rtQpCollapsed ? '' : ' ree-query-btn-active'}`}
                  onClick={() => setRtQpCollapsed(c => !c)}
                  title={(rtQpCollapsed ? 'Show query filters' : 'Hide query filters') + ` (${formatCombo('alt+q')})`}>
                  <i className="ti ti-filter" aria-hidden="true" />&nbsp;Query&nbsp;
                  <i className={`ti ti-chevron-${rtQpCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
                </button>
                {/* Loaded-in-X + Audit chips at the END of the left action group
                    (after Export to / Query), like every shared toolbar form — BO
                    UI Standard v3, user request 2026-09-18 (global). */}
                <FormMetaChips />
              </div>
              {/* Right group order (user request 2026-09-03): Clear · Rollback · Load. */}
              <div className="ree-ctrlbar-right">
                <button type="button" className="ca-btn"
                  onClick={() => { setRtDraft(EMPTY_REE_FILTERS); setRtApplied(EMPTY_REE_FILTERS); setRtLoaded(false); }}
                  disabled={loading || rtQpCollapsed} title={(rtQpCollapsed ? 'Expand Query to reset criteria.' : 'Reset all query criteria.') + ` (${formatCombo('alt+shift+c')})`}>
                  <i className="ti ti-eraser" aria-hidden="true" />&nbsp;Clear
                </button>
                {rtEmbed.canRollback && !rtEmbed.maximized && (
                  <button type="button" className="ca-btn"
                    onClick={() => rtEmbedApi.current?.rollback()} disabled={rtEmbed.selectedCount === 0}
                    title={(rtEmbed.selectedCount === 0
                      ? 'Tick one or more result rows to roll back.'
                      : 'Roll the ticked results back to Pending Entry (reason required).') + ` (${formatCombo('alt+b')})`}>
                    <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} aria-hidden="true" />&nbsp;Rollback
                  </button>
                )}
                {/* Load is allowed with the Query panel ALL-BLANK (user request
                    2026-09-05) — the "select at least one filter first" warning was
                    removed. With no query criteria the load is driven by the selected
                    Status chip (All → first eligible record; a specific status → that
                    status). setRtLoaded(true) reveals the embed. */}
                <button type="button" className="ca-btn ca-btn-solid"
                  onClick={() => { setRtApplied({ ...rtDraft, ...rtActiveDates }); setRtLoaded(true); }}
                  disabled={loading}
                  title={`Load the samples for the selected Status (and any Query criteria). (${formatCombo('alt+l')})`}>
                  <i className="ti ti-arrow-right" aria-hidden="true" />&nbsp;Load
                </button>
              </div>
            </div>
            {/* Status filter panel — sits ABOVE the Query panel (user request
                2026-09-03; kept here for RT, user request 2026-09-04). Its OWN panel
                (cool-tinted card, orange left accent), matching the Query Panel's
                "distinct filter region" treatment. */}
            {statusChips.length > 0 && (
              <div className="ree-status-panel">
                <span className="sa-filter-lbl">Status:</span>
                {/* RTT status panel (task RTCFR, 2026-09-11): the long per-stage chip row
                    is replaced by THREE grouped buckets — Approved Results · Pending
                    Results · All Results — each showing its DISTINCT-JOB count (rows are
                    job × stage; a job is in a bucket if any of its stages is there).
                    Selecting a bucket scopes the facet server-side (its member statuses
                    '|'-joined via @Status) and filters the loaded grid to those statuses.
                    Clicking fully RESETS the query panel (clears rtDraft/rtApplied + the
                    loaded grid) so stale picks don't linger, then the analyst re-Loads. */}
                {RT_BUCKET_TABS.map(({ key, label }) => (
                  <button key={key}
                    className={`sa-filter-btn${statusChip === key ? ' active' : ''}`}
                    onClick={() => { setStatusChip(key); setRtDraft(EMPTY_REE_FILTERS); setRtApplied(EMPTY_REE_FILTERS); setRtLoaded(false); }}
                  >{label} ({rtBucketCounts[key].toLocaleString()})</button>
                ))}
              </div>
            )}
            <ResultEntryQueryPanel
              filters={rtDraft}
              onChange={(patch) => setRtDraft(prev => ({ ...prev, ...patch }))}
              options={rtOptions}
              onApply={() => setRtApplied({ ...rtDraft, ...rtActiveDates })}
              onClear={() => { setRtDraft(EMPTY_REE_FILTERS); setRtApplied(EMPTY_REE_FILTERS); setRtLoaded(false); }}
              collapsed={rtQpCollapsed}
              onCollapsedChange={setRtQpCollapsed}
              // Date Received maps to CreatedDate; Date Analyzed maps to the
              // batch's AnalyzedDate (AI_Analysis_ResultTracking_SP, updated
              // 2026-09-02) — both now filter for real (inLocalDateRange,
              // above). Date Collected stays SOON — there's truly no such data
              // at this grain (no CollectDate on a QC batch).
              dateSoon={{ analyzed: false, received: false, collected: false }}
            />
          </>
        ) : isManualAppr ? (
          <>
            {/* Result Approval control bar (task RTCFR, 2026-09-04) — the SAME
                REE/RTT control-bar chrome, but Search By is KEPT (it drives the
                approval grain: JobID = approve the whole job, QCBatchID = approve
                per batch — user decision 2026-09-04). Left: Date Range · Refresh ·
                Export · Query · Search By. Right: Clear · Load (apply the query).
                Picking a row + the TOP-RIGHT "Open" opens it for approval — the
                approval workflow itself is unchanged. */}
            <div className="ree-ctrlbar">
              <div className="ree-ctrlbar-left">
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                {/* Refresh — reloads the pending-approval list AND the embedded
                    Sample/QC grid (raRefreshKey bumps the embed's key → it reloads).
                    The embed's own Refresh button was removed (user request 2026-09-04). */}
                <button type="button" className="ca-btn"
                  onClick={drRefresh} disabled={loading}
                  title="Reload the pending-approval list and counts (and the loaded grid).">
                  <BusyIcon busy={loading} icon="ti-refresh" />&nbsp;Refresh
                </button>
                {/* Export to — the pending tab's rows in the selection stage, the
                    embedded grid's live rows once a job/batch is loaded. */}
                {drLoaded
                  ? <ExportMenu filename={raExport.filename} headers={raExport.headers} rows={raExport.rows} />
                  : <ExportMenu filename={drSelExport.filename} headers={drSelExport.headers} rows={drSelExport.rows} />}
                <button type="button"
                  className={`ca-btn ree-query-btn${raQpCollapsed ? '' : ' ree-query-btn-active'}`}
                  onClick={() => setRaQpCollapsed(c => !c)}
                  title={(raQpCollapsed ? 'Show query filters' : 'Hide query filters') + ` (${formatCombo('alt+q')})`}>
                  <i className="ti ti-filter" aria-hidden="true" />&nbsp;Query&nbsp;
                  <i className={`ti ti-chevron-${raQpCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
                </button>
                {/* Loaded-in-X + Audit chips at the END of the left action group
                    (after Export to / Query) — BO UI Standard v3 (global). */}
                <FormMetaChips />
                {/* Search By control removed (user request 2026-09-04). The
                    approval grain is fixed to JOBID (job-level — "approval
                    releases the job to Reporting"); the panel's own QC Batch ID /
                    Job ID filters cover per-value narrowing. See the isManualAppr
                    grain-pin effect. */}
              </div>
              {/* Right group — canonical order Clear · Rollback · Approve · Load
                  (user request 2026-09-04). Rollback / Approve were moved here from
                  the grid toolbar; they drive the embedded approval grid's actions
                  via raEmbedActionsRef (the grid's tick-selection + guards are
                  unchanged). Enabled only when a job is resolved. */}
              <div className="ree-ctrlbar-right">
                {/* Back — detail stage only (REE parity): return to the pending
                    Job / QC Batch selection. Never approves or saves. */}
                {drLoaded && (
                  <button type="button" className="ca-btn" onClick={drBack}
                    title={`Close the result grid and return to the pending Job / QC Batch selection. (${formatCombo('esc')})`}>
                    <span aria-hidden="true">←</span>&nbsp;Back
                  </button>
                )}
                <button type="button" className="ca-btn"
                  onClick={drClear}
                  disabled={loading} title={`Reset all query criteria and return to the pending selection. (${formatCombo('alt+shift+c')})`}>
                  <i className="ti ti-eraser" aria-hidden="true" />&nbsp;Clear
                </button>
                {perm.write && (
                  <button type="button" className="ca-btn"
                    onClick={() => raEmbedActionsRef.current?.rollback()}
                    disabled={!raApprovalBatch || raReview.completed || raReview.selectedCount === 0}
                    title={!raApprovalBatch ? 'Load a job first.'
                      : raReview.selectedCount === 0 ? 'Tick one or more Sample / QC results to roll back.'
                      : 'Roll the ticked results back to Pending Validation (reason required).'}>
                    <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} aria-hidden="true" />&nbsp;Rollback
                  </button>
                )}
                {perm.write && (
                  <button type="button" className="ca-btn ca-btn-solid"
                    onClick={() => raEmbedActionsRef.current?.approve()}
                    disabled={!raApprovalBatch || raReview.completed || raReview.selectedCount === 0}
                    title={(!raApprovalBatch ? 'Load a job first.'
                      : raReview.selectedCount === 0 ? 'Tick one or more Sample / QC results to approve.'
                      : "Approve the ticked results (advances the job to Pending Reporting).") + ` (${formatCombo('alt+a')})`}>
                    <i className="ti ti-circle-check" aria-hidden="true" />&nbsp;{raReview.completed ? 'Approved' : 'Approve'}
                  </button>
                )}
                {/* Load = apply the query criteria (REE/RTT parity). Empty criteria
                    is allowed — Result Approval is a work-queue, so Load with no
                    filter simply (re)loads the full pending-approval set. */}
                {/* Load is secondary (BO-UI V3 §7 — one primary at a time; Approve
                    is the primary workflow action on this bar). */}
                <button type="button" className="ca-btn"
                  onClick={drLoadAction}
                  disabled={!drCanLoad}
                  title={(drLoaded ? 'Back, or Clear, to select a different job / QC batch.'
                    : drActiveSel.size === 0 ? `Select one or more ${drSelTab === 'Sample' ? 'Job IDs' : 'QC Batch IDs'}, then Load.`
                    : `Load the ${drActiveSel.size} selected ${drSelTab === 'Sample' ? (drActiveSel.size === 1 ? 'job' : 'jobs') : (drActiveSel.size === 1 ? 'QC batch' : 'QC batches')} for approval.`) + ` (${formatCombo('alt+l')})`}>
                  <i className="ti ti-arrow-right" aria-hidden="true" />&nbsp;Load
                </button>
              </div>
            </div>
            {/* Status shows as the REE-style pending summary bar at the bottom of
                the panel (user request 2026-09-04): "Pending Result Approval ·
                JobID (n) · QCBatchID (n)". JobID count = the loaded pending-approval
                job rows; QCBatchID count = distinct pending-approval QC batches from
                the filter-options SP (raSpOptions.batchIds). */}
            <ResultApprovalQueryPanel
              filters={raDraft}
              onChange={(patch) => setRaDraft(prev => ({ ...prev, ...patch }))}
              options={raOptions}
              // Summary strip hidden for now — the Sample / QC Batch tab badges
              // carry the counts (SHOW_DR_PENDING_SUMMARY restores it).
              pendingJobs={SHOW_DR_PENDING_SUMMARY ? raOptions.jobIds.length : undefined}
              pendingBatches={SHOW_DR_PENDING_SUMMARY ? raOptions.batchIds.length : undefined}
              pendingLoading={loading}
              onApply={drLoadAction}
              onClear={drClear}
              collapsed={raQpCollapsed}
              onCollapsedChange={setRaQpCollapsed}
              // All three date ranges now filter (2026-09-05): Date Received is
              // client-side (row ReceivedDate); Date Analyzed (SampleParameter.
              // AnalyzedDate) and Date Collected (SampleLogIn.CollectDate) are
              // server-side via AI_Analysis_ResultApproval_SP.
              dateSoon={{ analyzed: false, received: false, collected: false }}
            />
          </>
        ) : isManualVal ? (
          <>
            {/* Result Validation control bar (task RTCFR, 2026-09-05) — the EXACT
                parallel of the Result Approval control bar above, one Data-Review
                stage earlier. Left: Date Range · Refresh · Export · Query. Right:
                Clear · Rollback · Validate · Load. No Search By (JOBID-pinned, same
                as RA). Rollback/Validate drive the embedded validation grid via
                rvEmbedActionsRef; Validate = the embed's terminal action (which is
                /validate in validation mode). */}
            <div className="ree-ctrlbar">
              <div className="ree-ctrlbar-left">
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                  onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                <button type="button" className="ca-btn"
                  onClick={drRefresh} disabled={loading}
                  title="Reload the pending-validation list and counts (and the loaded grid).">
                  <BusyIcon busy={loading} icon="ti-refresh" />&nbsp;Refresh
                </button>
                {drLoaded
                  ? <ExportMenu filename={rvExport.filename} headers={rvExport.headers} rows={rvExport.rows} />
                  : <ExportMenu filename={drSelExport.filename} headers={drSelExport.headers} rows={drSelExport.rows} />}
                <button type="button"
                  className={`ca-btn ree-query-btn${rvQpCollapsed ? '' : ' ree-query-btn-active'}`}
                  onClick={() => setRvQpCollapsed(c => !c)}
                  title={(rvQpCollapsed ? 'Show query filters' : 'Hide query filters') + ` (${formatCombo('alt+q')})`}>
                  <i className="ti ti-filter" aria-hidden="true" />&nbsp;Query&nbsp;
                  <i className={`ti ti-chevron-${rvQpCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
                </button>
                {/* Loaded-in-X + Audit chips at the END of the left action group
                    (after Export to / Query) — BO UI Standard v3 (global). */}
                <FormMetaChips />
              </div>
              <div className="ree-ctrlbar-right">
                {drLoaded && (
                  <button type="button" className="ca-btn" onClick={drBack}
                    title={`Close the result grid and return to the pending Job / QC Batch selection. (${formatCombo('esc')})`}>
                    <span aria-hidden="true">←</span>&nbsp;Back
                  </button>
                )}
                <button type="button" className="ca-btn"
                  onClick={drClear}
                  disabled={loading} title={`Reset all query criteria and return to the pending selection. (${formatCombo('alt+shift+c')})`}>
                  <i className="ti ti-eraser" aria-hidden="true" />&nbsp;Clear
                </button>
                {perm.write && (
                  <button type="button" className="ca-btn"
                    onClick={() => rvEmbedActionsRef.current?.rollback()}
                    disabled={!rvValidationBatch || rvReview.completed || rvReview.selectedCount === 0}
                    title={!rvValidationBatch ? 'Load a job first.'
                      : rvReview.selectedCount === 0 ? 'Tick one or more Sample / QC results to roll back.'
                      : 'Roll the ticked results back to Pending Entry (reason required).'}>
                    <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} aria-hidden="true" />&nbsp;Rollback
                  </button>
                )}
                {perm.write && (
                  <button type="button" className="ca-btn ca-btn-solid"
                    onClick={() => rvEmbedActionsRef.current?.approve()}
                    disabled={!rvValidationBatch || rvReview.completed || rvReview.selectedCount === 0}
                    title={(!rvValidationBatch ? 'Load a job first.'
                      : rvReview.selectedCount === 0 ? 'Tick one or more Sample / QC results to validate.'
                      : 'Validate the ticked results (advances the job to Pending Result Approval).') + ` (${formatCombo('alt+v')})`}>
                    <i className="ti ti-circle-check" aria-hidden="true" />&nbsp;{rvReview.completed ? 'Validated' : 'Validate'}
                  </button>
                )}
                {/* Load is secondary (BO-UI V3 §7 — one primary at a time; Validate
                    is the primary workflow action on this bar). */}
                <button type="button" className="ca-btn"
                  onClick={drLoadAction}
                  disabled={!drCanLoad}
                  title={(drLoaded ? 'Back, or Clear, to select a different job / QC batch.'
                    : drActiveSel.size === 0 ? `Select one or more ${drSelTab === 'Sample' ? 'Job IDs' : 'QC Batch IDs'}, then Load.`
                    : `Load the ${drActiveSel.size} selected ${drSelTab === 'Sample' ? (drActiveSel.size === 1 ? 'job' : 'jobs') : (drActiveSel.size === 1 ? 'QC batch' : 'QC batches')} for validation.`) + ` (${formatCombo('alt+l')})`}>
                  <i className="ti ti-arrow-right" aria-hidden="true" />&nbsp;Load
                </button>
              </div>
            </div>
            {/* Pending summary bar: "Pending Result Validation · JobID (n) ·
                QCBatchID (n)". JobID count = loaded pending-validation job rows;
                QCBatchID count = distinct pending-validation QC batches from the
                filter-options SP (rvSpOptions.batchIds). */}
            <ResultApprovalQueryPanel
              filters={rvDraft}
              onChange={(patch) => setRvDraft(prev => ({ ...prev, ...patch }))}
              options={rvOptions}
              pendingLabel="Pending Result Validation"
              pendingJobs={SHOW_DR_PENDING_SUMMARY ? rvOptions.jobIds.length : undefined}
              pendingBatches={SHOW_DR_PENDING_SUMMARY ? rvOptions.batchIds.length : undefined}
              pendingLoading={loading}
              onApply={drLoadAction}
              onClear={drClear}
              collapsed={rvQpCollapsed}
              onCollapsedChange={setRvQpCollapsed}
              // Same three date ranges filter as Result Approval: Date Received
              // client-side (row ReceivedDate); Date Analyzed / Date Collected
              // server-side via AI_Analysis_ResultValidation_SP.
              dateSoon={{ analyzed: false, received: false, collected: false }}
            />
          </>
        ) : (
          <>
            {/* Toolbar on its OWN full-width row on the grey page. */}
            <div style={{ margin: '0 0 8px' }}>
              <AnalysisToolbar
                kind={kind}
                loading={loading}
                dateRange={dateRange} setDateRange={setDateRange}
                searchBy={searchBy} setSearchBy={setSearchBy}
                onRetrieve={load}
                exportData={{ filename: kind, ...gridEx }}
                // SDMS Result Validation / Approval workflow actions. Rollback moved
                // to the consolidated title row (prototype (7)); Preview stays here.
                // Validate/Approve is only reachable via Open Package (SdmsPackageDetail).
                sdmsCanPreview={selectedBatches.length === 1}
                onSdmsPreview={() => {
                  const only = selectedBatches.length === 1 ? selectedBatches[0] : undefined;
                  if (only) setPreviewTarget(only);
                }}
                // (Merged 2026-07-07) QC Batches list-view actions — New (→ Analysis
                // Queue), Edit / Delete (status In(0,1)), Rollback (Not In(0,1)), SDMS.
                // MAC gate (owner report 2026-10-26): New -> create, Edit -> write,
                // Delete -> delete, Rollback -> write. The handler is passed only when
                // the role holds the right, so the button is HIDDEN and its hotkey
                // never registers -- same pattern as Sample Transfer / Registration.
                onQcNew={perm.create ? onQcNew : undefined}
                onQcEdit={perm.write ? onQcEdit : undefined}
                qcEditable={qcEditable(selectedQcRow)}
                onQcDelete={perm.delete ? onQcDelete : undefined}
                onQcRollback={perm.write ? (() => void onQcRollback()) : undefined}
                qcRollbackable={qcRollbackable(selectedQcRow)}
                onQcSdms={() => {
                  const row = selectedQcRow;
                  if (!row) return;
                  // Legacy refuses to open the SDMS sheet for a PLM batch and says
                  // so (QCBatchController.cs :13510) — the two screens are not
                  // interchangeable, and silently opening the wrong one is how a
                  // PLM batch ends up entered as SDMS.
                  if (plmBatchOids.has(row.id)) {
                    notify.error('This is a PLM test. Please use the PLM button.');
                    return;
                  }
                  // Open the selected batch's detail (fetches keyed on its Oid) and
                  // auto-swap into the SDMS sheet — the same sheet the detail-view
                  // SDMS button opens. Read-only load; Back returns to this list.
                  setQcBatchSeed({
                    testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
                    templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
                    autoOpenSdms: true,
                  });
                }}
                onQcPlm={() => {
                  const row = selectedQcRow;
                  if (!row) return;
                  // The reciprocal refusal (QCBatchController.cs :13618).
                  if (!plmBatchOids.has(row.id)) {
                    notify.error('This is not a PLM test. Please use the SDMS button.');
                    return;
                  }
                  setQcBatchSeed({
                    testMethodId: '', test: row.test, method: row.method, matrix: row.matrix,
                    templateID: row.templateID, existingAbOid: row.id, viewOnly: true,
                    autoOpenPlm: true,
                  });
                }}
                qcHasSelection={!!selectedQcRow}
                // (Merged 2026-07-07) Analysis Queue → Create QC Batch.
                onQueueNew={perm.create ? onNewFromQueue : undefined}
                queueHasSelection={!!selectedAnalysisRow}
                // Sample Prep Batches list — Edit (write) / Delete (delete), hidden
                // for a role without the right (MAC gate, owner report 2026-10-24).
                onPrepBatchEdit={perm.write ? onPrepBatchEdit : undefined}
                onPrepBatchDelete={perm.delete ? onPrepBatchDelete : undefined}
                prepBatchHasSelection={!!selectedPrepBatchRow}
                prepBatchDeletable={prepDeletable(selectedPrepBatchRow)}
              />
            </div>

            {/* Search — shared .list-search-row on the grey page, never inside the card. */}
            <div className="list-search-row">
              <div className="list-search">
                <i className="ti ti-search" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search…" />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </div>
              {filtered.length !== rows.length && (
                <span className="search-match-count">
                  {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
                </span>
              )}
              {/* SDMS list — Export + Maximize icons on the search row (search left, icons
                  right), matching the detail-view tab grids (user request 2026-08-31). */}
              {isSdms && (
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <ExportMenu filename={kind} headers={gridEx.headers} rows={gridEx.rows}
                    iconOnly triggerClassName="adg-tool" />
                  <button type="button"
                    className={`adg-tool${gridMaxed ? ' adg-tool-on' : ''}`}
                    title={gridMaxed ? 'Restore' : 'Maximize'} aria-label={gridMaxed ? 'Restore' : 'Maximize'}
                    aria-pressed={gridMaxed} onClick={() => setGridMaxed((m) => !m)}
                    /* Float above the maximized grid (z > .adg-maxed), at the content's
                       top-right (below the top bar), so Restore stays clickable. */
                    style={gridMaxed ? { position: 'fixed', top: 50, right: 24, zIndex: 1401 } : undefined}>
                    <i className={`ti ${gridMaxed ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} />
                  </button>
                </span>
              )}
            </div>
          </>
        )}

        {/* (Result Tracking's Status filter panel now renders ABOVE the Query
            panel — see the isTracking control bar above, moved 2026-09-03.) */}

        {/* Result Tracking's batch-list grid is replaced in-place by the selected
            batch's SAMPLE detail grid (user request 2026-09-03): pick a filter in
            the Query panel → Load → the Sample Results / QC Results grid renders
            here (embedded ResultTrackingDetail). Until a query is applied, empty.
            The real list grid card below stays MOUNTED but display:none so the
            per-stage Status chip counts + Query-panel options still derive from the
            loaded rows. */}
        {/* WINDOW mode (user request 2026-09-05): after Load the grid shows EVERY
            job's samples for the window, narrowed to the selected Status chip — not
            just the first job. batch is null; windowSince drives the all-jobs fetch
            (empty until Load, i.e. rtLoaded). statusFilter narrows to the chip. Keyed
            by the window date so a Date Range change reloads clean. */}
        {isTracking && (
          <ResultTrackingDetail key={`rtwin:${rtLoaded ? (sinceFromKey(dateRange) ?? 'all') : 'empty'}`}
            embedded batch={null}
            windowSince={rtLoaded ? (sinceFromKey(dateRange) ?? null) : null}
            windowFilters={rtWindowFilters}
            statusFilter={statusChip === 'approved' || statusChip === 'pending'
              ? RT_BUCKET_STATUSES[statusChip].join('|') : 'all'}
            embedApiRef={rtEmbedApi} onEmbedState={onRtEmbedState} onClose={() => {}} />
        )}

        {/* Result Approval embedded grid (task RTCFR, 2026-09-04) — the first-page
            Job grid is REPLACED by the REAL approval detail grid (ResultValidationDetail,
            mode='approval', embedded): the SAME data / columns / checkbox / approve
            logic as the second-page detail, on page one. Query-driven: apply the
            Query panel + Load → the resolved job's Sample/QC results render here with
            tick-to-approve. The hidden list card below stays MOUNTED so the status
            chips + raResolvedRow still derive from the loaded rows. Keyed by the
            resolved job so switching jobs reloads clean. Shows the FIRST pending-
            approval job's grid BY DEFAULT on open (user request 2026-09-04); the
            empty prompt appears only when there are no pending-approval jobs. */}
        {/* Always render the embedded approval grid. batch is NULL by default (no
            query applied) → it shows the EMPTY grid with headers (user request
            2026-09-04); applying the Query panel + Load resolves a job and fills it.
            Keyed by the resolved job (or 'empty') so switching reloads clean. */}
        {/* RTCFR 2026-09-28 — Result Validation / Approval open on the SAME pending
            Job / QC Batch selection panel as REE (shared ResultEntryPendingSelector),
            and the form's embedded detail replaces it IN PLACE after Load — never
            both, never the empty grid on open. Back returns to the selection. */}
        {isManualDR && !drLoaded && (
          <ResultEntryPendingSelector
            tab={drSelTab}
            onTabChange={setDrSelTab}
            jobs={drPendingJobs}
            batches={drPendingBatches}
            selectedJobIds={drSelJobIds}
            onJobSelectionChange={setDrSelJobIds}
            selectedBatchIds={drSelBatchIds}
            onBatchSelectionChange={setDrSelBatchIds}
            loading={loading}
            onLoadRow={drLoadRow}
            onVisibleJobsChange={setDrVisJobs}
            onVisibleBatchesChange={setDrVisBatches}
            showBatchSampleCount={false}
          />
        )}
        {isManualAppr && drLoaded && (
          <ResultValidationDetail key={`${raApprovalBatch?.id ?? 'empty'}:${raRefreshKey}:${drBatchScope.join('|')}`} embedded mode="approval" batch={raApprovalBatch} jobs={raJobs}
            batchIds={drBatchScope.length ? drBatchScope : undefined}
            canWrite={perm.write} onClose={() => {}} onDone={drOnDone}
            onExportData={onRaExportData} onReviewState={onRaReviewState} actionsRef={raEmbedActionsRef} />
        )}

        {/* Result Validation embedded grid (task RTCFR, 2026-09-05) — the EXACT
            parallel of the Result Approval embed above, mode='validation': the real
            validation detail grid (same data / columns / checkbox / Validate logic)
            on page one. batch is NULL by default → empty grid with headers; applying
            the Query panel + Load resolves the first matching job and fills it. */}
        {isManualVal && drLoaded && (
          <ResultValidationDetail key={`${rvValidationBatch?.id ?? 'empty'}:${rvRefreshKey}:${drBatchScope.join('|')}`} embedded mode="validation" batch={rvValidationBatch} jobs={rvJobs}
            batchIds={drBatchScope.length ? drBatchScope : undefined}
            canWrite={perm.write} onClose={() => {}} onDone={drOnDone}
            onExportData={onRvExportData} onReviewState={onRvReviewState} actionsRef={rvEmbedActionsRef} />
        )}

        {/* ONE white grid card — filter-chip strip + grid + its single footer.
            Result Tracking + Result Approval + Result Validation hide it (their
            embedded sample grids render above); it stays mounted so status chips +
            options + the resolved-row derive from the loaded rows. */}
        <div className="bl-card bl-card-flex" style={isTracking || isManualAppr || isManualVal ? { display: 'none' } : undefined}>
          {/* Result Tracking's grid-top row (user request 2026-09-02) — same
              markup as the REE detail's toolbar (ree-toolbar-row + .list-search
              sized/iconed identically), but inset 14px on both sides (matching
              the status panel / card-header convention elsewhere in this card
              family) so the search box and the View button line up with the
              grid's own content instead of sitting flush against the card
              edges. Export to moved down here, next to the fuzzy search box
              (user request 2026-09-02) — left group is Search + Export to,
              View (open the ticked batch's result rows) stays at the far
              right. */}
          {isTracking && (
            <div className="ree-toolbar-row" style={{ justifyContent: 'space-between', padding: '6px 14px 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="list-search" style={{ width: 260, flexShrink: 0 }}>
                  <span className="tb-i">🔍</span>
                  <input type="text" placeholder="Search…"
                    value={rtSearch} onChange={e => setRtSearch(e.target.value)} />
                  {rtSearch && <span className="list-search-clear" onClick={() => setRtSearch('')}>×</span>}
                </div>
                <ExportMenu filename={kind} headers={gridEx.headers} rows={gridEx.rows} />
              </div>
              <button className="ca-btn ca-btn-solid dp-load"
                onClick={() => { if (selectedTrackingRow) openTrackingDetail(selectedTrackingRow); }}
                disabled={!selectedTrackingRow}
                title={selectedTrackingRow
                  ? "View the ticked QC batch's result rows"
                  : 'Tick a QC batch to enable View.'}>
                <i className="ti ti-eye" /> View{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </button>
            </div>
          )}
          {/* (Result Approval's free-text search now lives INSIDE the embedded
              Sample/QC grid, next to its tabs — RTT parity; the old in-card
              raSearch row was removed with the list grid. Its status chips moved to
              the panel above.) */}
          {/* Status filter chips — only when the dataset carries a status column.
              Hidden on SDMS Result Validation / Approval (not in legacy ALPACA's list),
              Result Tracking and Result Approval (both moved to their own panel above). */}
          {!isSdms && !isTracking && !isManualAppr && statusChips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button
                className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                onClick={() => setStatusChip('all')}
              >All ({searchFiltered.length})</button>
              {statusChips.map(([label, n]) => (
                <button key={label}
                  className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                  onClick={() => setStatusChip(label)}
                >{label} ({n})</button>
              ))}
            </div>
          )}

          {error ? (
            <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{error}</div>
            </div>
          ) : (
            /* Shared data grid — styling, per-column filters, sort, footer /
               pagination and load-time are all standardised in <AppDataGrid>.
               SDMS Result Validation / Approval enable row selection so the
               Rollback / Preview / Load actions can act on the selected batches. */
            <AppDataGrid<unknown>
              rows={filtered}
              columns={cols}
              // Persist this list's column layout + sort per screen (kind), so the
              // user's Hide/Reorder/Resize/Sort choices restore on return (§18.6).
              persistKey={`analysis-${kind}`}
              getRowId={getRowKey}
              loading={loading}
              loadMs={loadMs}
              // SDMS list draws its OWN Export + Maximize cluster on the search
              // row (above), so suppress the grid's built-in one there — else it
              // shows twice (user report 2026-09-23). Other kinds keep the
              // grid's own maximize (the global default).
              hideHeaderTools={isSdms}
              // QC Batches row-click opens the existing batch inline (view mode);
              // Prep/Analysis Queue rows drill to the sample slide-over. (Merged 2026-07-07.)
              // Result Validation (manual): a row-click SELECTS the row (ticks its box →
              // enables Load) like Custom Reporting — it does NOT navigate. The Load
              // button is the ONLY way to open the detail (no double-click). (User 2026-07-10.)
              // Sample Prep Batches row-click opens the existing prep batch inline
              // (view mode). Prep/Analysis Queue rows still drill to the sample panel.
              onRowClick={kind === 'qc-batches' ? onQcBatchRowClick
                : kind === 'prep-batches' ? onPrepBatchRowClick
                : isManualDR ? (r => onManualRowClick(r as ResultValidationListRow))
                : isTracking ? (r => onTrackingRowClick(r as ResultTrackingRow))
                : drillable ? (r => setSelectedQueue(r as AnalysisQueueRow)) : undefined}
              // SDMS Result Validation / Approval enable single-select (radio-like) —
              // the workflow selects ONE QC batch at a time (user request 2026-08-03).
              // QC Batches / Analysis + Prep Queue / Prep Batches / Result Validation +
              // Approval (manual) / Result Tracking are all single-select too. Prep
              // Queue's checkbox drives the "New" prep-batch action (row-click still
              // drills to the sample panel); Prep Batches' checkbox drives Edit/Delete
              // (reusing qcSel — the kinds are mutually exclusive).
              selectable={isSdms || isManualDR || isTracking || kind === 'qc-batches' || kind === 'analysis-queue' || kind === 'prep-queue' || kind === 'prep-batches'}
              selectedIds={isSdms || isManualDR ? selectedIds : kind === 'qc-batches' || kind === 'prep-batches' ? qcSel : kind === 'analysis-queue' || kind === 'prep-queue' ? analysisSel : isTracking ? selectedIds : undefined}
              onSelectionChange={isSdms ? handleSdmsSelectionChange : isManualDR ? handleManualSelectionChange : kind === 'qc-batches' || kind === 'prep-batches' ? handleQcSelectionChange : kind === 'analysis-queue' || kind === 'prep-queue' ? handleAnalysisSelectionChange : isTracking ? handleTrackingSelectionChange : undefined}
              // QC Batches default sort = QCBatchID descending (legacy list-view
              // SortIndex 0 / SortOrder Descending) — newest batch first. (Merged 2026-07-07.)
              initialSort={kind === 'qc-batches' ? { key: 'QCBatchID', dir: 'desc' } : undefined}
              // SDMS Result Validation / Approval — full-screen via className, toggled by
              // the Maximize icon in the search row (user request 2026-08-31).
              // Result Tracking / Validation / Approval lists show no zebra (user
              // request 2026-09-30); every other screen on this shared list keeps it.
              className={[
                isSdms && gridMaxed ? 'adg-maxed' : '',
                isTracking || isManualDR ? RESULT_GRID_NO_ZEBRA : '',
              ].filter(Boolean).join(' ') || undefined}
              emptyText="No rows match the current filters."
            />
          )}
        </div>
      </div>

      {selectedQueue && (
        // Analysis Queue has its OWN readiness criteria (AI_Analysis_AnalysisQueueDetail_SP),
        // so it drills in through SampleDetailsModal — a sample PICKER whose OK seeds the
        // QC Batch with exactly the checked samples. Prep Queue keeps the read-only panel.
        kind === 'analysis-queue'
          ? <SampleDetailsModal row={selectedQueue} onClose={() => setSelectedQueue(null)} onLoadSelected={handleLoadSelectedSamples} />
          : <PrepQueueDetailPanel row={selectedQueue} onClose={() => setSelectedQueue(null)} onLoadSelected={handleLoadSelectedPrepSamples} />
      )}

      {/* SDMS Result Validation / Approval modals — Validate / Approve is opened
          from <SdmsPackageDetail>'s in-view toolbar, not from here. (Merged 2026-07-07.) */}
      {rollbackOpen && rollbackTargets.length > 0 && (
        <RollbackModal
          targets={rollbackTargets as unknown as SdmsBatch[]}
          onClose={() => setRollbackOpen(false)}
          onConfirmed={(resp) => {
            // Enhanced Re-analysis / Re-prep: only the selected results left the batch
            // for a queue — the batch keeps its status, so reload instead of flipping it.
            if (resp.partial) {
              const refusal = Object.values(resp.skipped ?? {})[0];
              if (refusal) notify.error(refusal);
              else {
                void load();
                void showDialog({
                  type: 'success',
                  title: 'Rollback',
                  message: `${resp.returned ?? 0} result(s) sent back to the ${resp.queue ?? 'queue'}.`,
                });
              }
              setRollbackOpen(false);
              return;
            }
            // Only apply optimistic rollback to the batches the server actually
            // rolled back (server may skip some — e.g. reported ones — while
            // succeeding on others in a bulk request).
            const skipped = resp.skipped ?? {};
            const skippedIds = new Set(Object.keys(skipped));
            const succeededIds = rollbackTargets
              .map(t => t.id)
              .filter(id => !skippedIds.has(id));
            if (succeededIds.length > 0) {
              applyRollback(succeededIds);
            }
            // Surface the FIRST skip reason as an ALPACA-style red banner across
            // the top of the list.  All-skip: use the single message.  Mixed:
            // "N batch(es) skipped: {reason}" so partial success is truthful.
            const skipCount = Object.keys(skipped).length;
            if (skipCount > 0) {
              const firstReason = Object.values(skipped)[0] ?? 'Rollback failed.';
              // §13: full skip → error modal; partial → warning modal.
              if (skipCount === rollbackTargets.length) notify.error(firstReason);
              else notify.warning(`${skipCount} batch(es) skipped: ${firstReason}`);
            } else if (succeededIds.length > 0) {
              // Milestone success (§18.6) → the shared CENTERED modal, not the
              // Tier-2 top banner: rolling a validated batch back is a significant
              // state change the reviewer should acknowledge (client 2026-08-03).
              void showDialog({ type: 'success', title: 'Rollback', message: 'Rollbacked successfully.' });
            }
            setRollbackOpen(false);
          }}
        />
      )}
      {previewTarget && (
        // ALPACA parity: Preview opens the SDMS spreadsheet DIRECTLY, read-only
        // and RIBBON-LESS (preview) — LIMS's Preview shows just the grid + sheet
        // tabs, no ribbon.  Open Package → SDMS keeps the full ribbon.
        <SdmsSpreadsheet
          preview
          batch={previewTarget as unknown as SdmsBatch}
          allBatches={(rows as AnalyticalBatchRow[]).map(b => ({
            id: b.id, batchId: b.batchId, test: b.test, method: b.method,
            matrix: b.matrix, templateId: b.templateID, templateName: b.templateName,
            jobIds: b.jobIds, statusText: b.statusText,
          }))}
          onClose={() => setPreviewTarget(null)}
          onRolledBack={() => { void load(); setPreviewTarget(null); }}
        />
      )}
      {pkgTarget && (
        <SdmsPackageDetail
          batch={pkgTarget as unknown as SdmsPackageRow}
          approval={isApproval}
          canWrite={perm.write}
          canSeeForm={canSee}
          /* A PLM batch opens as legacy's "SDMS Result Validation - PLM": the PLM
             Results grid instead of the SDMS tabs (owner screenshot 2026-09-30). */
          plmBatchOid={plmBatchOids.has(pkgTarget.id) ? pkgTarget.id : null}
          onOpenPlm={() => openPlmFor(pkgTarget)}
          allBatches={(rows as AnalyticalBatchRow[]).map(b => ({
            id: b.id, batchId: b.batchId, test: b.test, method: b.method,
            matrix: b.matrix, templateId: b.templateID, templateName: b.templateName,
            jobIds: b.jobIds, statusText: b.statusText,
          }))}
          onClose={closePackage}
          onRefresh={load}
          onValidated={(whoCode, whenIso) => {
            // Optimistic update on the list row.  Detail overlay STAYS
            // open — the analyst reads the ALPACA-parity green "Samples
            // validated/approved successfully." banner + the in-place Status
            // flip in the detail, then closes on their own.  Approval keeps the
            // row (batch shown as SDMS Approved with its verify stamp).
            if (isApproval) applyApprove([pkgTarget.id], whoCode, whenIso);
            else            applyValidate([pkgTarget.id], whoCode, whenIso);
          }}
        />
      )}
      {/* QC Batches list Data Package — the SAME package review view, opened as a modal
          POPUP (windowed, OG TargetWindow.NewModalWindow). Kept separate from the SDMS
          Validation/Approval overlay above; validating from here just reloads the list. */}
      {qcPkgTarget && (
        <SdmsPackageDetail
          windowed
          plmBatchOid={plmBatchOids.has(qcPkgTarget.id) ? qcPkgTarget.id : null}
          onOpenPlm={() => openPlmFor(qcPkgTarget)}
          packageStatus={qcPkgStatus}
          packageCreatedBy={qcPkgCreated.by} packageCreatedDate={qcPkgCreated.date}  /* OG header (persisted) */
          batch={qcPkgTarget as unknown as SdmsPackageRow}
          approval={false}
          canWrite={perm.write}
          canSeeForm={canSee}
          allBatches={(rows as AnalyticalBatchRow[]).map(b => ({
            id: b.id, batchId: b.batchId, test: b.test, method: b.method,
            matrix: b.matrix, templateId: b.templateID, templateName: b.templateName,
            jobIds: b.jobIds, statusText: b.statusText,
          }))}
          /* Reload the list on close too — a Submit inside the popup advanced the batch
             (Pending Submission → Pending SDMS Validation), so the QC batch row's status
             must refresh when the popup closes. (user UI feedback 2026-09-16) */
          onClose={() => { setQcPkgTarget(null); void load(); }}
          onRefresh={() => { void load(); }}
          onValidated={() => { void load(); }}
        />
      )}
    </div>
  );
}

/** Toolbar row on the grey page (BO UI Standard v2) — the shared <AppToolbar>.
 *  The form-specific Search By dropdown (Result Tracking + legacy Result
 *  Approval) and the Date Range picker ride in the toolbar's left slot; Refresh
 *  + Export to are the shared toolbar buttons.
 *
 *  SDMS Result Validation / Approval extend the left slot with a Preview button
 *  (XAF parity, DataReviewController); Rollback + Load live in the consolidated
 *  title row and Validate/Approve lives in <SdmsPackageDetail>. (Merged 2026-07-07.) */
function AnalysisToolbar({
  kind, loading,
  dateRange, setDateRange, searchBy, setSearchBy,
  onRetrieve, exportData,
  sdmsCanPreview, onSdmsPreview,
  onQcNew, onQcEdit, qcEditable, onQcDelete,
  onQcRollback, qcRollbackable, onQcSdms, onQcPlm, qcHasSelection,
  onQueueNew, queueHasSelection,
  onPrepBatchEdit, onPrepBatchDelete, prepBatchHasSelection, prepBatchDeletable,
}: {
  kind: AnalysisKind; loading: boolean;
  dateRange: DateRangeKey; setDateRange: (k: DateRangeKey) => void;
  searchBy: 'QCBatchID' | 'JOBID'; setSearchBy: (s: 'QCBatchID' | 'JOBID') => void;
  onRetrieve: () => void; exportData: ExportData;
  sdmsCanPreview?: boolean;
  onSdmsPreview?:  () => void;
  // (Merged 2026-07-07) QC Batches list-view actions.
  onQcNew?: () => void;
  onQcEdit?: () => void; qcEditable?: boolean;
  onQcDelete?: () => void;
  onQcRollback?: () => void; qcRollbackable?: boolean;
  onQcSdms?: () => void; onQcPlm?: () => void; qcHasSelection?: boolean;
  // (Merged 2026-07-07) Analysis Queue → Create QC Batch.
  onQueueNew?: () => void; queueHasSelection?: boolean;
  // Sample Prep Batches list — Edit (open) / Delete (blocked once ResultCompleted).
  onPrepBatchEdit?: () => void; onPrepBatchDelete?: () => void;
  prepBatchHasSelection?: boolean; prepBatchDeletable?: boolean;
}) {
  const isSdms  = kind === 'result-validation' || kind === 'sdms-approval';  // (Merged 2026-07-07)
  const isQc    = kind === 'qc-batches';  // (Merged 2026-07-07)
  const isPrepBatches = kind === 'prep-batches';  // Sample Prep Batches list (Edit/Delete; New is queue-driven)
  const isQueue = kind === 'analysis-queue' || kind === 'prep-queue';  // prep-queue also opens a detail (Prep Batch)
  // Result Validation / Approval use the PLAIN (white-bg, orange-text) toolbar
  // dropdowns — matching Custom Reporting (user request 2026-07-09); other Analysis
  // forms keep the solid-orange .sa-date-sel.
  const isManualVal = kind === 'result-validation-manual' || kind === 'result-approval';
  const dateSelCls = `sa-date-sel${isManualVal ? ' sa-date-sel-plain' : ''}`;

  // Phase 2.3 — these list-view actions are CUSTOM left-slot buttons (not
  // AppToolbar onNew/onEdit/onDelete props), so they need explicit shortcuts
  // (CLAUDE.md standing rule 14, §18.6, ADR-0013).  Each mirrors its button's
  // exact handler + disabled gate; grouped per screen.  Placed before this
  // component's single return (rules of hooks).
  const tbGroup = isPrepBatches ? 'Sample Prep Batches'
    : kind === 'prep-queue' ? 'Sample Prep Queue'
    : isQc ? 'QC Batches'
    : kind === 'analysis-queue' ? 'Analysis Queue'
    : isSdms ? 'SDMS' : 'Analysis';
  const tbKeys: HotkeyDef[] = [];
  if (isQueue && onQueueNew) tbKeys.push({ combo: 'alt+n', label: 'New', group: tbGroup, order: 1, run: () => onQueueNew(), disabled: !queueHasSelection });
  if (isSdms && onSdmsPreview) tbKeys.push({ combo: 'alt+p', label: 'Preview', group: tbGroup, order: 1, run: () => onSdmsPreview(), disabled: !sdmsCanPreview });
  if (isQc) {
    if (onQcNew) tbKeys.push({ combo: 'alt+n', label: 'New', group: tbGroup, order: 1, run: () => onQcNew() });
    if (onQcEdit) tbKeys.push({ combo: 'alt+e', label: 'Edit', group: tbGroup, order: 2, run: () => onQcEdit(), disabled: !qcEditable });
    if (onQcDelete) tbKeys.push({ combo: 'alt+d', label: 'Delete', group: tbGroup, order: 3, run: () => onQcDelete(), disabled: !qcEditable });
    if (onQcRollback) tbKeys.push({ combo: 'alt+b', label: 'Rollback', group: tbGroup, order: 4, run: () => onQcRollback(), disabled: !qcRollbackable });
  }
  if (isPrepBatches) {
    if (onPrepBatchEdit) tbKeys.push({ combo: 'alt+e', label: 'Edit', group: tbGroup, order: 1, run: () => onPrepBatchEdit(), disabled: !prepBatchHasSelection });
    if (onPrepBatchDelete) tbKeys.push({ combo: 'alt+d', label: 'Delete', group: tbGroup, order: 2, run: () => onPrepBatchDelete(), disabled: !prepBatchDeletable });
  }
  useHotkeys(tbKeys);

  return (
    <>
      {/* The page heading is the shared <FormTitle> in the page header above
          (ONE heading per page — §18.6); this bar carries only the actions. */}
      {/* Shared toolbar — canonical order: [left] · Refresh · Export to.
          These read grids have no New/Edit/Delete; the Search By dropdown (when
          shown) and the Date Range picker ride in the left slot. */}
      <AppToolbar
        left={
          <>
            {/* Result Validation (manual) — the "Load" action moved to the TOP-RIGHT
                of the page header (Custom Reporting parity, user request 2026-07-09),
                so it's no longer in this toolbar. */}
            {/* Date Range/Filter is the FIRST toolbar action (user request
                2026-07-09; §18.6 form-specific buttons sit left of the rest).  On
                SDMS it reads "Date Filter" and precedes Preview; on QC Batches
                "Date Range" precedes New.  Shared <DateRangeSelect> (same
                component Custom Reporting uses) — a native <select> can't
                restyle its highlighted option (browser-blue); this renders the
                app's own orange-highlight popup instead (user report
                2026-07-17: SDMS Date Filter showed blue where Custom Reporting
                showed orange). */}
            {supportsDate(kind) && (
              <DateRangeSelect variant="dropdown" label={`Date ${isSdms ? 'Filter' : 'Range'}`}
                ranges={TRACKING_RANGES}
                value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            )}
            {/* Result Tracking's primary action is the "Load" button in the TOP-RIGHT
                page header (Custom Reporting / Result Validation parity, §18.6) — NOT a
                toolbar button. See the dp-title-actions block above. */}
            {/* SDMS workflow action — Preview (select exactly 1 batch).  Rollback +
                Load sit in the consolidated title row; Validate/Approve lives in
                <SdmsPackageDetail>. (Merged 2026-07-07.) */}
            {isSdms && (
              <button className="ca-btn" onClick={onSdmsPreview} disabled={!sdmsCanPreview}
                title="Preview SDMS result image (select exactly 1 batch)">
                <i className="ti ti-eye" style={{ color: 'var(--ai-blue)' }} /> Preview
              </button>
            )}
            {/* (Merged 2026-07-07) Analysis Queue → Create QC Batch. Tick one
                test-method row, then New opens the QC Batch form in create mode
                for it (Job ID → Load → Sort → OK). Completes the create flow that
                the QC Batches list "New" button routes here for. */}
            {isQueue && onQueueNew && (
              <button className="ca-btn" onClick={onQueueNew} disabled={!queueHasSelection}
                title={queueHasSelection
                  ? `Create a ${kind === 'prep-queue' ? 'prep' : 'QC'} batch for the selected test method`
                  : 'Select one test-method row first'}>
                <span className="tb-i tb-i-green">＋</span> New
              </button>
            )}
            {/* (Merged 2026-07-07) QC Batches list-view actions in the EXACT legacy
                order: New · Edit · Delete · Rollback · SDMS (before the shared
                Refresh / Export).  New → Analysis Queue; Edit/Delete gate to status
                In(0,1); Rollback to Not In(0,1); SDMS is deferred (info toast).
                Data Package is NOT here — it's the per-row grid hyperlink. */}
            {isQc && (
              <>
                {onQcNew && <button className="ca-btn" onClick={onQcNew} title={`New (${formatCombo('alt+n')})`}>
                  <span className="tb-i tb-i-green">＋</span> New
                </button>}
                {onQcEdit && <button className="ca-btn" onClick={onQcEdit} disabled={!qcEditable} title={`Edit (${formatCombo('alt+e')})`}>
                  <span className="tb-i tb-i-blue">✎</span> Edit
                </button>}
                {onQcDelete && <button className="ca-btn" onClick={onQcDelete} disabled={!qcEditable} title={`Delete (${formatCombo('alt+d')})`}>
                  <span className="tb-i tb-i-red">🗑</span> Delete
                </button>}
                {onQcRollback && <button className="ca-btn" onClick={onQcRollback} disabled={!qcRollbackable}
                  title={(qcRollbackable ? 'Rollback the selected batch' : 'Available once the batch is past Pending Submission') + ` (${formatCombo('alt+b')})`}>
                  <i className="ti ti-arrow-back-up" /> Rollback
                </button>}
                <button className="ca-btn" onClick={onQcSdms} disabled={!qcHasSelection}
                  title="Open the SDMS sheet for the selected batch">
                  <i className="ti ti-table" /> SDMS
                </button>
                {/* PLM sits beside SDMS exactly as in legacy, whose QC Batches
                    list defines OpenPLMbatch right next to OpenSDMSQcbatch
                    (QCBatchController.Designer.cs :342, Caption "PLM").  Both
                    buttons stay enabled for any selection and validate on click,
                    which is also what legacy does — each handler checks
                    Test.IsPLMTest and refuses with a message. */}
                <button className="ca-btn" onClick={onQcPlm} disabled={!qcHasSelection}
                  title="Open PLM result entry for the selected batch">
                  <i className="ti ti-microscope" /> PLM
                </button>
              </>
            )}
            {/* Sample Prep Batches list-view actions — Edit · Delete (OG
                SamplePrepBatch_ListView).  New is a QUEUE action (Sample Prep
                Queue → New), so it is intentionally NOT here; Delete is blocked
                once the batch is ResultCompleted (statusCode 3). */}
            {isPrepBatches && (
              <>
                {onPrepBatchEdit && <button className="ca-btn" onClick={onPrepBatchEdit} disabled={!prepBatchHasSelection} title={`Edit (${formatCombo('alt+e')})`}>
                  <span className="tb-i tb-i-blue">✎</span> Edit
                </button>}
                {onPrepBatchDelete && <button className="ca-btn" onClick={onPrepBatchDelete} disabled={!prepBatchDeletable}
                  title={(prepBatchHasSelection
                    ? (prepBatchDeletable ? 'Delete the selected prep batch' : 'A prep batch with completed results cannot be deleted')
                    : 'Select one prep batch first') + ` (${formatCombo('alt+d')})`}>
                  <span className="tb-i tb-i-red">🗑</span> Delete
                </button>}
              </>
            )}
            {/* Search By dropdown (QCBatchID | JOBID) — Result Tracking, legacy
                job-level Result Approval, AND the manual Result Validation queue
                (ALPACA DataReviewQueryPanel's Search By + Retrieve).  The value
                drives the server SP's @SearchBy/@Search filter. */}
            {(kind === 'result-tracking' || kind === 'result-approval'
              || kind === 'result-validation-manual') && (
              <div className="sa-date-wrap" style={{ marginRight: 4 }}>
                <i className="ti ti-filter sa-date-prefix" />
                <span className="sa-date-label">Search By</span>
                <select className={dateSelCls} value={searchBy}
                  onChange={e => setSearchBy(e.target.value as 'QCBatchID' | 'JOBID')}>
                  <option value="QCBatchID">QCBatchID</option>
                  <option value="JOBID">JOBID</option>
                </select>
              </div>
            )}
          </>
        }
        onRefresh={onRetrieve}
        refreshing={loading}
        exportData={exportData}
      />
    </>
  );
}

// ── drill-down detail panel ────────────────────────────────────────────────
interface PrepDetailRow {
  sampleParameterId: string;
  sampleID:          string;
  jobId:             string | null;
  sampleNo:          number;
  collectDate:       string | null;
  dateReceived:      string | null;
  sampleLocation:    string | null;
  test:              string | null;
  method:            string | null;
  matrix:            string | null;
  parameter:         string | null;
  client:            string | null;
  projectId:         string | null;
  subOut:            boolean | null;
  // select-samples-then-load-into-QC-Batch — jobOid is the Job GUID
  // (Samplecheckin.Oid) qcBatchApi.load's jobOids expects; sampleLogInOid
  // (SampleLogIn.Oid) narrows Load to the picked samples. Populated only by
  // the analysis-queue detail endpoint; null on the prep-queue one.
  jobOid?:           string | null;
  sampleLogInOid?:   string | null;
}

/** Prep Queue "View details" popup — centered modal, SAME design as the Analysis
 *  Queue SampleDetailsModal (orange header + shared .list-search + sample grid).
 *  Read-only view of the pending-prep SampleParameter rows under one TestMethod
 *  (backed by prep-queue/{id}/samples). Close-only; it does not seed a batch. */
function PrepQueueDetailPanel({
  row, onClose, onLoadSelected,
}: { row: AnalysisQueueRow; onClose: () => void; onLoadSelected: (rows: PrepDetailRow[]) => void }) {
  const [detail, setDetail] = useState<PrepDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sel, setSel]         = useState<Set<string>>(new Set());
  const notify = useNotify();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Only this row's component (the queue is one row per test + component, 2026-09-30).
        const res = await api.get(`/api/v1/analysis/prep-queue/${row.testMethodId}/samples`, {
          params: { byComponent: true, ...(row.componentId ? { componentId: row.componentId } : {}) } });
        if (!cancelled) setDetail((res.data as { data: PrepDetailRow[] }).data ?? []);
      } catch (e) {
        // Surface the real failure via the shared notification service instead of
        // silently showing an empty grid.  An empty popup previously masked a
        // server error (a stale SP whose 14-column result no longer matched the
        // 16-arg Dapper record, so materialisation threw a 500) as a misleading
        // "No matches". (fix 2026-09-09)
        if (!cancelled) {
          setDetail([]);
          notify.error(qcApiErr(e, 'Failed to load samples for this test.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.testMethodId, row.componentId, notify]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const samples = onePerSample(detail);
    if (!term) return samples;
    return samples.filter(r =>
      `${r.jobId ?? ''} ${r.sampleID} ${r.client ?? ''}`.toLowerCase().includes(term));
  }, [detail, search]);

  const toggle = (id: string) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every(r => sel.has(sampleKey(r)));
  const toggleAll = () => setSel(prev => {
    if (allChecked) { const n = new Set(prev); filtered.forEach(r => n.delete(sampleKey(r))); return n; }
    const n = new Set(prev); filtered.forEach(r => n.add(sampleKey(r))); return n;
  });
  // OK → hand the ticked samples up so the Prep Batch form opens seeded with them.
  const handleOk = () => onLoadSelected(detail.filter(r => sel.has(sampleKey(r))));

  // Hide / sort / filter / resize (user 2026-09-29, shared useMiniGrid); the popup widens to fit.
  const grid = useMiniGrid<PrepDetailRow>(QUEUE_SAMPLE_COLS, filtered, {
    rowKey: sampleKey, loading, empty: 'No matches',
    lead: { width: 30,
      head: <input type="checkbox" className="chk" checked={allChecked} onChange={toggleAll} />,
      cell: r => <input type="checkbox" className="chk" checked={sel.has(sampleKey(r))} onChange={() => toggle(sampleKey(r))} /> },
    rowProps: r => ({ className: sel.has(sampleKey(r)) ? 'sel' : undefined }),
  });
  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width: popupWidthFor(900, grid.width) }} onClick={e => e.stopPropagation()}>
        <div className="mh">🧪 {row.test ?? 'Prep Queue'} — Samples <span className="x" onClick={onClose}>×</span></div>
        <div className="pb">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <button className="btn" onClick={() => exportCsv(filtered.map(({ parameter: _p, sampleParameterId: _id, ...r }) => r) as unknown as Record<string, unknown>[], 'prep-queue-samples.csv')}>
              ⭳ Export to
            </button>
            <div className="list-search">
              <i className="ti ti-search" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="gridbox" style={{ borderRadius: 6 }}>
            {grid.table}
          </div>
          <div className="foot">
            <span>Page 1 of 1 ({grid.viewRows.length} items){sel.size > 0 ? ` · Selected (${sel.size})` : ''}</span>
            <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
            <div className="psize">Page size: <select><option>100</option></select></div>
          </div>
        </div>
        <div className="mf">
          <button className="btn primary" disabled={sel.size === 0} onClick={handleOk}>OK</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Samples popups of the Prep Queue / Analysis Queue (same columns). No Parameter
// column (user 2026-09-30), so the popups list ONE row per sample.
const sampleKey = (r: PrepDetailRow) => r.sampleLogInOid ?? `${r.jobId ?? ''}|${r.sampleID}`;
const onePerSample = (rows: PrepDetailRow[]) => {
  const seen = new Set<string>();
  return rows.filter(r => { const k = sampleKey(r); if (seen.has(k)) return false; seen.add(k); return true; });
};
const QUEUE_SAMPLE_COLS: MiniCol<PrepDetailRow>[] = [
  { key: 'jobId',       label: 'JobID',     width: 110, text: r => r.jobId ?? '—' },
  { key: 'sampleID',    label: 'SampleID',  width: 140, text: r => r.sampleID ?? '' },
  { key: 'client',      label: 'Client',    width: 200, text: r => r.client ?? '—' },
  { key: 'collectDate', label: 'Collected', width: 130, text: r => fmtDate(r.collectDate), sortValue: r => r.collectDate },
];


/** Analysis Queue's "Sample Details" popup — centered modal (QC-Batch-Prototype
 *  `#sdScrim`/`.pickmodal`), backed by its OWN endpoint (analysis-queue/{id}/
 *  samples → AI_Analysis_AnalysisQueueDetail_SP), NOT prep-queue's, since the two
 *  queues' sample-readiness criteria differ. Checkboxes are functional (legacy OG
 *  parity — QCBatchController.cs Dc_Accepting ~line 2788): OK with a selection
 *  seeds the QC Batch screen with exactly those samples (jobOids pre-filled) via
 *  onLoadSelected; OK with nothing selected, or Cancel, just closes. */
function SampleDetailsModal({
  row, onClose, onLoadSelected,
}: { row: AnalysisQueueRow; onClose: () => void; onLoadSelected: (rows: PrepDetailRow[]) => void }) {
  const [detail, setDetail]   = useState<PrepDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [sel, setSel]         = useState<Set<string>>(new Set());
  const notify = useNotify();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/v1/analysis/analysis-queue/${row.testMethodId}/samples`);
        if (!cancelled) setDetail((res.data as { data: PrepDetailRow[] }).data ?? []);
      } catch (e) {
        // Surface the real failure rather than a misleading empty grid — same
        // guard as PrepQueueDetailPanel (fix 2026-09-09).
        if (!cancelled) {
          setDetail([]);
          notify.error(qcApiErr(e, 'Failed to load samples for this test.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.testMethodId, notify]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const samples = onePerSample(detail);
    if (!term) return samples;
    return samples.filter(r =>
      `${r.jobId ?? ''} ${r.sampleID} ${r.client ?? ''}`.toLowerCase().includes(term));
  }, [detail, search]);

  const toggle = (id: string) => setSel(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allChecked = filtered.length > 0 && filtered.every(r => sel.has(sampleKey(r)));
  const toggleAll = () => setSel(prev => {
    if (allChecked) { const next = new Set(prev); filtered.forEach(r => next.delete(sampleKey(r))); return next; }
    const next = new Set(prev);
    filtered.forEach(r => next.add(sampleKey(r)));
    return next;
  });
  const handleOk = () => {
    const selectedRows = detail.filter(r => sel.has(sampleKey(r)));
    if (selectedRows.length > 0) onLoadSelected(selectedRows);
    onClose();
  };

  // Hide / sort / filter / resize (user 2026-09-29, shared useMiniGrid); the popup widens to fit.
  const grid = useMiniGrid<PrepDetailRow>(QUEUE_SAMPLE_COLS, filtered, {
    rowKey: sampleKey, loading, empty: 'No matches',
    lead: { width: 30,
      head: <input type="checkbox" className="chk" checked={allChecked} onChange={toggleAll} />,
      cell: r => <input type="checkbox" className="chk" checked={sel.has(sampleKey(r))} onChange={() => toggle(sampleKey(r))} /> },
    rowProps: r => ({ className: sel.has(sampleKey(r)) ? 'sel' : undefined }),
  });
  return (
    <div className="qcb-scrim" onClick={onClose}>
      <div className="pickmodal" style={{ width: popupWidthFor(900, grid.width) }} onClick={e => e.stopPropagation()}>
        <div className="mh">🔬 {row.test ?? 'Analysis Queue'} — Samples <span className="x" onClick={onClose}>×</span></div>
        <div className="pb">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
            <button className="btn" onClick={() => exportCsv(filtered.map(({ parameter: _p, sampleParameterId: _id, ...r }) => r) as unknown as Record<string, unknown>[], 'sample-details.csv')}>
              ⭳ Export to
            </button>
            <div className="list-search">
              <i className="ti ti-search" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div className="gridbox" style={{ borderRadius: 6 }}>
            {grid.table}
          </div>
          <div className="foot">
            <span>Page 1 of 1 ({grid.viewRows.length} items){sel.size > 0 ? ` · Selected (${sel.size})` : ''}</span>
            <div className="pages"><span className="pg">‹</span><b>1</b><span className="pg">›</span></div>
            <div className="psize">Page size: <select><option>100</option></select></div>
          </div>
        </div>
        <div className="mf">
          <button className="btn primary" onClick={handleOk}>OK</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
